// This component is being migrated from a class component to a function component. This process entails
// extracting logic from the class component into custom hooks and new function components and then lifting
// them up to the parent function component.

// <reference path="type-declarations.d.ts" />
import {getTestProps} from '@benchling/js-test-utils';
import {range} from '@benchling/js-utils';
import classNames from 'classnames';
import produce from 'immer';
import $ from 'jquery';
import React, {CSSProperties, useCallback, useEffect, useMemo, useRef} from 'react';
import ReactDOM from 'react-dom';
import isEqual from 'react-fast-compare';
import {TableVirtuoso, TableVirtuosoHandle, VirtuosoMockContext} from 'react-virtuoso';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {ClickableContextMenuOption, view as viewContextMenu} from 'components/ContextMenu';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import getConfig from 'getConfig';
import checkSoftInvariant from 'lib/checkSoftInvariant';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import DocumentEditorContext from 'notebook/documents/DocumentEditorContext';
import {
  InventoryStructuredTableSchemaType,
  InventoryTableMetadata,
} from 'notebook/integrations/samples/InventoryTableConstants';
import {createCollapseId} from 'notebook/mediocre/base-plugin/createCollapseId';
import {parseTextWithLocale} from 'notebook/mediocre/clipboard/parseTextWithLocale';
import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
import {TableWYCIWYPPart} from 'notebook/mediocre/clipboard/WyciwypConstants';
// @ts-expect-error
import formatters from 'notebook/mediocre/formatted-text/formatters';
import FormattersConstants from 'notebook/mediocre/formatted-text/FormattersConstants';
import {getSearchResultPropsForIndex} from 'notebook/mediocre/search/SearchResultProps';
import * as MultiSelectListUtil from 'notebook/mediocre/selection/block/MultiSelectListUtil';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {INDENT_SIZE} from 'notebook/notes/lists/ListConstants';
import {resolveSelectorsFromPastedHTMLTables} from 'notebook/plain-tables/selectors/SelectorConflictResolver';
import SetSelectorModalClass from 'notebook/plain-tables/selectors/SetSelectorModal';
import getColumnConstraintWithRowData from 'notebook/structured-tables/columns/getColumnConstraint';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import TableSingleInputGroup from 'notebook/structured-tables/single-inputs/TableSingleInputGroup';
import {getStopDragPropagationProps} from 'notebook/structured-tables/single-inputs/useSingleInputDragHandlers';
import {
  ALL_PARAMETER_STRUCTURED_TABLE_KINDS,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import {
  getMaxRowsConfig,
  getTableLengthErrorMessage,
} from 'notebook/structured-tables/util/checkTableCapacity';
import {getLastSyncedUpstreamValueWithColumnConstraint} from 'notebook/structured-tables/util/getLastSyncedUpstreamValue';
import isMergedCell from 'notebook/structured-tables/util/isMergedCell';
import {CollapsedTableReduxActions} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {RowCountText} from 'notebook/tables/collapse/RowCountText';
import CellResizer from 'notebook/tables/core-ui/CellResizer';
import DraggableDimensionMover from 'notebook/tables/core-ui/DraggableDimensionMover';
import {
  computeFrozenCountAfterReorder,
  hasMergeCrossingColumn,
  selectionStraddlesFreezeBoundary,
  supportsUserFreezeColumns,
  wouldFreezeExceedViewport,
} from 'notebook/tables/core-ui/FreezeColumnUtils';
import {TableCellEditor, TableCellEditorProps} from 'notebook/tables/core-ui/TableCellEditor';
import {TableEditableProps, TableEditableClassProps} from 'notebook/tables/core-ui/TableEditableProps';
import {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import TableHeaderRow, {TableHeaderRowProps} from 'notebook/tables/core-ui/TableHeaderRow';
import TableRow, {TableRowProps} from 'notebook/tables/core-ui/TableRow';
import TableRowTagMemoized from 'notebook/tables/core-ui/TableRowTagMemoized';
import TableTitleBar from 'notebook/tables/core-ui/TableTitleBar';
import useMultiWindowEditing from 'notebook/tables/core-ui/useMultiWindowEditing';
import excess from 'notebook/tables/excess';
import {EvaluatedCell, EvaluatedCellError, Selector} from 'notebook/tables/excess/Evaluator';
import type {TryInsertOptions} from 'notebook/tables/excess/FormulaUtil';
import type {ReorderManualOptions} from 'notebook/tables/excess/ops/TableOps';
import {colNumberToLetters, rowIndexToNumber} from 'notebook/tables/excess/Util';
import makeTableHTML from 'notebook/tables/export/makeTableHTML';
import {TableReadonlyViewCollapseBanner} from 'notebook/tables/export/TableReadonlyViewCollapseBanner';
import {setCellEditorPosition} from 'notebook/tables/spreadsheets/SpreadsheetActions';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {RowModelProvider} from 'notebook/tables/store/contexts/RowModelContext';
import {TableModelProvider} from 'notebook/tables/store/contexts/TableModelContext';
import {useTableModel} from 'notebook/tables/store/hooks/useTableModel';
import {useTableMutations} from 'notebook/tables/store/hooks/useTableMutations';
import {
  computeAnyCellsLockedOrArchived,
  computeLockedWarningMessage,
} from 'notebook/tables/store/logic/TableLockingLogic';
import {
  computeAreMergedBoxesOverlapping,
  computeContainsMergedRegion,
  computeHasTextInMergedRegion,
} from 'notebook/tables/store/logic/TableMergedCellLogic';
import {computePasteRejectionReason} from 'notebook/tables/store/logic/TablePasteLogic';
import {
  computeExpandedSelectionBox,
  computeIsRealIndex,
  computeSelectionRange,
} from 'notebook/tables/store/logic/TableSelectionLogic';
import * as TableConstants from 'notebook/tables/TableConstants';
import {
  Cell,
  TableColumnType,
  TableIndicatorStatus,
  TableOpDiffMap,
  TABLE_SEARCH_PATHS,
} from 'notebook/tables/TableConstants';
import {
  SHIFT_ENTER_INSERT_ROW,
  PAGE_UP,
  PAGE_DOWN,
  HOME,
  END,
  SHIFT_SPACE,
  CTRL_SPACE,
  DELETE,
  TAB,
  SHIFT_TAB,
  ENTER,
  F2,
  ARROW_KEYS,
  ARROW_KEYS_EXTEND,
  ARROW_KEYS_JUMP,
  ARROW_KEYS_EXTEND_JUMP,
} from 'notebook/tables/TableShortcuts';
import TableUtil from 'notebook/tables/TableUtil';
import FormulaBox, {FormatPropertyChange} from 'notebook/tables/toolbar/FormulaBox';
import {AlignmentState} from 'notebook/tables/toolbar/FormulaBox';
import RenameTableModal from 'notebook/tables/toolbar/RenameTableModal';
import {formatTypedValue, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {extractSampleFromStageRunSample} from 'notebook/tables/typed-values/StageRunSampleUtil';
import {
  getLockedCellWarningMessage,
  getLockedTableWarningMessage,
  LockedCellWarningType,
  LockedTableWarningType,
} from 'notebook/tables/util/LockedTableWarningMessages';
import {trackKeyboardShortcut} from 'notebook/util/analytics/KeyboardShortcutAnalytics';
import {trackDragToReorderRowsOrColumns} from 'notebook/util/analytics/TableAnalyticsUtils';
import notifications from 'notifications';
import * as Perf from 'performance/Perf';
import {trackContainerValidityForTableEvents} from 'samples/containers/ValidContentsUtils';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import {isSchemaWithDescription} from 'schemas/SchemaConstants';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import legacyRunModal from 'ui-library/legacyRunModal';
import {TableCollapserContext} from 'ui-library/tables/TableCollapserContext';
import APIUtil from 'util/APIUtil';
import {ClipboardItemType} from 'util/clipboard/ClipboardUtil';
import findScrollContainer from 'util/findScrollContainer';
import {getIndexesfromIndexTreeRoot, getSubtreeAtIndex, isIndexTreeEmpty} from 'util/IndexTree';
import * as keyboard from 'util/Keyboard';
import {notifyTextHasBeenLocalized} from 'util/LocaleAwareParseText';
import memoizeLastCall from 'util/memoizeLastCall';
import * as modals from 'util/Modals';
import {normalizeLineEndings} from 'util/normalizeLineEndings';
import {
  assertNever,
  BasicEvent,
  ElementClipboardEvent,
  ElementKeyboardEvent,
  ElementMouseEvent,
  Optional,
} from 'util/Types';

import {
  getIsOverInlineRowLimit,
  getCollapseId,
  useTableCollapse,
  getIsOverInlineCellLimit,
} from '../collapse/useTableCollapse';
import transpose from '../util/transpose';
import AddRowsModal from './modals/AddRowsModal';
import PersistentHorizontalScrollbar from './PersistentHorizontalScrollbar';
import StickyHeaderHandler, {StickyHeaderPositioning} from './StickyHeaderHandler';
import TableFooter from './TableFooter';

const {
  TABLE_MAX_COLUMNS,
  TABLE_DEFAULT_COLUMN_WIDTH,
  TABLE_SELECTION_ROW,
  TABLE_SELECTION_COLUMN,
  TABLE_SELECTION_CELL,
} = TableConstants;

const DRAG_SCROLL_DURATION = 200;
const DRAG_SCROLL_PADDING = 30;
const AUTOSCROLL_DISTANCE = 20;
const AUTOSCROLL_TRIGGER_DISTANCE = 50;
export const AUTOSCROLL_TRIGGER_RATE = 32;
const TABLE_OVERSCROLL_CLEARANCE = 200;
const PADDING_RIGHT_SIZE = 35;

// 1 minute
const UPDATE_TABLE_CELL_PERF_INTERVAL = 1000 * 60;

export interface LockedCellCheckArgs {
  forUndoRedo?: boolean;
  /** Indicates that actions only affect the anchor cell, not the full selection. */
  onlyAnchor?: boolean;
}

/**
 * This wrapper serves as the provider for the redux store.
 * Once Redux is enabled, the props should no longer be passed down to TableEditableInner and instead should
 * go through redux.
 *
 * This must be a separate component outside of TableEditableComponent because the redux store has to be
 * populated by TableStateWrapper before useTable can be called.
 */
function TableEditable(props: TableEditableProps): JSX.Element {
  return (
    <TableModelProvider tableProps={props}>
      <TableEditableComponent />
    </TableModelProvider>
  );
}

/**
 * This component serves as the functional component for TableEditable.  Logic from TableEditableClass should
 * eventually be migrated here.
 */
function TableEditableComponent(): JSX.Element {
  const {tableModel, setState} = useTableModel();
  const {props, state, selection, context, dimensions, singleInputs, locking, views, fillDown} = tableModel;
  // DocumentName will only be populated when inside of a literal Document, but it's only
  // needed for Cross-Document references, so that's okay.
  const {documentName} = DocumentEditorContext.useContext();

  useEffect(() => {
    MultiSelectListUtil.multiSelectableWillMount(
      props.metadataForRestore,
      selection.multiSelectKey,
      setState
    );
  }, [props.metadataForRestore, selection.multiSelectKey, setState]);

  const tableRef = useRef<HTMLDivElement>(null);

  // Consume the EditorPropertiesContext
  const editorProperties = EditorPropertiesContext.useContext();
  const {isExportView, isPrintPreviewView, isConfig: fileIsConfig} = editorProperties;

  const {multiCrossEditing, showCellEditor} = useStoreSelector(
    SpreadsheetStore,
    (store) => {
      const storeState = store.getState(props.tableNamespace);
      return {
        multiCrossEditing: storeState.editingTable != null,
        showCellEditor: storeState.cellEditorPosition != null,
      };
    },
    isEqual
  );

  const multiFinalizeEditHandler = useCallback((): void => {
    if (!(props.multiEditing && state.multiSelIndex != null)) {
      return;
    }
    assert(selection.hasRealSelection, 'Expected to have a real selection');
    const index = state.multiSelIndex;
    const {formula, typedValue} = SpreadsheetStore.getState(
      props.tableNamespace,
      props.table.name,
      props.fileId
    );
    const [i, j] = Array.from(index);

    // Skip updating the table if the cell hasn't changed
    const oldCell = props.table.table[i][j];
    const oldTypedValue = oldCell.typedValue || null;
    assert(typedValue !== undefined, 'typedValue can be null but not undefined');
    if (formula === oldCell.text && isEqual(typedValue, oldTypedValue)) {
      return;
    }

    MultiSelectListUtil.saveMultiSelect(state, props.storeState, selection.multiSelectKey);
    const newCell = TableUtil.makeCell(formula);

    if (typedValue && !isTypedValueEmpty(typedValue)) {
      newCell.typedValue = typedValue;
      newCell.text = '';
    } else {
      newCell.typedValue = null;
    }
    setCellEditorPosition(null);

    const newTable = produce(props.table, (draftTable) => {
      draftTable.table[i][j] = {...draftTable.table[i][j], ...newCell};
    });
    props.updateHandler(props.index, {table: newTable}, undefined, {
      [props.table.name]: {type: 'update-cells', cells: [{row: i, column: j}]},
    });
    trackContainerValidityForTableEvents(props.fileId, props.table.apiId!, [
      {event: ELN_ANALYTICS_ACTIONS.REMOVED_CONTAINERS_FROM_STRUCTURED_TABLE, cells: [oldCell]},
    ]);
  }, [selection.hasRealSelection, selection.multiSelectKey, props, state]);

  useMultiWindowEditing({
    tableNamespace: props.tableNamespace,
    fileId: props.fileId,
    tableName: props.table.name,
    documentName: props.documentName ?? null,
    domNode: tableRef.current!,
    multiStartIndex: state.multiStartIndex,
    multiEndIndex: state.multiEndIndex,
    hasFormulasDisabled: props.disableFormulas ?? false,
    multiActive: state.multiActive ?? false,
    multiEditing: props.multiEditing ?? false,
    multiCrossEditing,
    canInsert: props.canInsert,
    setMultiEditingForTable: props.setMultiEditingForTable,
    onEditFinalize: multiFinalizeEditHandler,
    setState,
  });

  // TODO(BNCH-113857): Remove this once redux is enabled
  const propsWithDefaults = useMemo(
    () => ({
      canAttachFileToCell: props.canAttachFileToCell ?? (() => false),
      canAddRemoveColumns: props.canAddRemoveColumns ?? true,
      canAddColumnsFreeform: props.canAddColumnsFreeform ?? true,
      canRename: props.canRename ?? true,
      getEditWhileCellLockedWarning:
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        props.getEditWhileCellLockedWarning === null
          ? null
          : (props.getEditWhileCellLockedWarning ??
            (() => getLockedCellWarningMessage(LockedCellWarningType.GENERIC))),
      editWhileTableLockedWarning:
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        props.editWhileTableLockedWarning === null
          ? null
          : (props.editWhileTableLockedWarning ??
            getLockedTableWarningMessage(LockedTableWarningType.GENERIC)),
      indentation: props.indentation ?? 0,
      disableFormulas: props.disableFormulas ?? false,
      showTableRowExcessLabels: props.showTableRowExcessLabels ?? true,
      selectorsEnabled: props.selectorsEnabled ?? true,
      isMaximized: props.isMaximized ?? false,
      isSplitScreen: props.isSplitScreen ?? false,
      isFocused: props.isFocused ?? false,
      disableMaximizedTables: props.disableMaximizedTables ?? false,
      dirty: props.dirty ?? false,
      viewer: props.viewer ?? false,
      isCollapsible: props.isCollapsible ?? false,
      isCompactBottomSpacing: props.isCompactBottomSpacing ?? false,
      isLeftAlignColumnHeader: props.isLeftAlignColumnHeader ?? true,
      isLeftAlignRowHeader: props.isLeftAlignRowHeader ?? false,
      hasPlaceholderRows: props.hasPlaceholderRows ?? false,
      disableLegacyHeader: props.disableLegacyHeader ?? false,
      forceCollapsed: props.forceCollapsed ?? false,
      isNestedInParentComponent: props.isNestedInParentComponent ?? false,
      isTableHeaderFaded: props.isTableHeaderFaded ?? false,
      isExportView: isExportView ?? false,
      isPrintPreviewView: isPrintPreviewView ?? false,
    }),
    [props, isExportView, isPrintPreviewView]
  );

  const {isCollapsed, isCollapsedInitially, isPermaCollapsed} = useTableCollapse(props);

  const tableMutations = useTableMutations();

  return (
    <TableEditableClass
      {...props}
      {...propsWithDefaults}
      documentName={documentName}
      fileIsConfig={fileIsConfig}
      tableRef={tableRef}
      isCollapsed={isCollapsed}
      isCollapsedInitially={isCollapsedInitially}
      isPermaCollapsed={isPermaCollapsed}
      setState={setState}
      state={state}
      multiCrossEditing={multiCrossEditing}
      showCellEditor={showCellEditor}
      selection={selection}
      context={context}
      dimensions={dimensions}
      singleInputs={singleInputs}
      locking={locking}
      views={views}
      fillDown={fillDown}
      multiFinalizeEditHandler={multiFinalizeEditHandler}
      tableMutations={tableMutations}
    />
  );
}

export const hiddenInputCid = (cid: string): string => `${cid}-table`;
interface TableCopyOptions {
  showFormulas: boolean;
}

export interface TablePasteOptions {
  transpose?: boolean;
  type: ClipboardItemType;
  selectionIndex?: TableConstants.TableIndex;
}

type RenderFocusBearerProps = MultiSelectListUtil.RenderFocusBearerArgs<
  TableConstants.TableIndex,
  TableCopyOptions
>;

const TableFocusBearer = React.forwardRef(
  (props: Omit<RenderFocusBearerProps, 'multiCopyOptions'>, ref: React.RefObject<HTMLDivElement>) => (
    <EditorPropertiesContext.Consumer>
      {({showFormulas}: {showFormulas: boolean}) =>
        MultiSelectListUtil.renderFocusBearer({
          ...props,
          ref,
          multiGetHTMLForCopy: props.multiGetHTMLForCopy,
          multiGetTextForCopy: props.multiGetTextForCopy,
          multiCopyOptions: {showFormulas},
        })
      }
    </EditorPropertiesContext.Consumer>
  )
);

const FREEZE_MERGE_MESSAGES: Record<'merge' | 'freeze' | 'unfreeze', string> = {
  merge: 'Unmerge cells that span the freeze boundary first.',
  freeze: 'Cannot freeze through a merged cell. Unmerge first.',
  unfreeze: 'Cannot unfreeze through a merged cell. Unmerge first.',
};

function showFreezeMergeConflictToast(action: 'merge' | 'freeze' | 'unfreeze'): void {
  ToastActions.pushToast({message: FREEZE_MERGE_MESSAGES[action], type: ToastType.WARNING});
}

/**
 * @deprecated Avoid extending or depending on this class if possible. We are in the process of migrating this
 * class to a function component.
 */
export class TableEditableClass extends React.Component<TableEditableClassProps, TableEditableState> {
  static selectionAtStart(cid: string): {
    multiSelectKey: string;
    data: {
      multiActive: null;
      multiStartIndex: TableConstants.TableIndex;
      multiEndIndex: TableConstants.TableIndex;
      multiSelIndex: TableConstants.TableIndex;
    };
  } {
    return {
      multiSelectKey: TableUtil.getTableMultiSelectKey(cid),
      data: {
        multiActive: null,
        multiStartIndex: [0, 0],
        multiEndIndex: [0, 0],
        multiSelIndex: [0, 0],
      },
    };
  }

  tableNameRef?: Element | null;
  verticalScrollContainer?: Element | null;
  autoscrollIntervalId?: number;
  _measureId?: NodeJS.Timeout;
  clientX?: number;
  clientY?: number;
  _isMounted: boolean = false;
  virtualizationHandleRef: React.RefObject<TableVirtuosoHandle> = React.createRef<TableVirtuosoHandle>();
  updateCellPerfId: Perf.PerfId = Perf.PERF_ID_DISABLED;
  visibleRowIndicesRef: React.MutableRefObject<Array<number> | null> = React.createRef();

  focusBearerRef = React.createRef<HTMLDivElement>();
  hiddenInputRef = React.createRef<HTMLDivElement>();
  horizontalScrollContainerRef: React.MutableRefObject<HTMLDivElement | null> =
    React.createRef<HTMLDivElement>();
  tableRef = React.createRef<HTMLTableElement>();
  tableTitleBarRef = React.createRef<HTMLDivElement>();
  stickyHeaderRef = React.createRef<HTMLDivElement>();
  formulaBoxRef = React.createRef<HTMLDivElement>();

  getHorizontalScrollWrapperRect(): DOMRect | null {
    return this._getTableHorizontalScrollContainer()?.getBoundingClientRect() ?? null;
  }

  getTableVerticalScrollWrapperRect(): DOMRect | null {
    return this._getTableVerticalScrollContainer()?.getBoundingClientRect() ?? null;
  }

  getPageVerticalScrollWrapperRect(): DOMRect | null {
    return this._getPageVerticalScrollContainer()?.getBoundingClientRect() ?? null;
  }

  getStickyHeaderRect(): DOMRect | null {
    return this.stickyHeaderRef.current?.getBoundingClientRect() ?? null;
  }

  // TODO: When refactoring the virtualized table to functional components, replace this with a useMemo hook.
  getVirtualizedTableContext = memoizeLastCall(
    (
      state: TableEditableState,
      widths: TableConstants.TableData['widths'],
      computedTable: TableEditableProps['computedTable']
    ): TableEditableState & {
      widths: TableConstants.TableData['widths'];
      computedTable: TableEditableProps['computedTable'];
    } => ({
      ...state,
      widths,
      computedTable,
    })
  );

  updateHandler(
    index: number,
    {table}: {table: TableConstants.Table},
    selectors?: Optional<Array<Selector>>,
    tableOp?: TableOpDiffMap
  ): void {
    this.props.updateHandler(index, {table}, selectors, tableOp);
  }

  getCustomRowLabel(j: number): JSX.Element | string | null {
    return (this.props.table.rowLabels && this.props.table.rowLabels[j]) || null;
  }

  saveMultiSelect = (
    state?:
      | Partial<MultiSelectListUtil.MultiSelectState<TableConstants.TableIndex>>
      | {selection: SelectionRestoreData}
  ): void => {
    if (state == null) {
      state = this.props.state;
    }
    MultiSelectListUtil.saveMultiSelect(state, this.props.storeState, this.props.selection.multiSelectKey);
  };

  clearMultiSelect = (): void => {
    MultiSelectListUtil.clearMultiSelect(this.props.setState.bind(this));
  };

  setMultiExtra(multiExtraUpdate: {
    fillDownActive?: boolean;
    fillDownInvalid?: boolean;
    fillDownBox?: TableConstants.MultiSelectionBox | null;
  }): void {
    MultiSelectListUtil.setMultiExtra(
      multiExtraUpdate,
      this.props.setState.bind(this),
      this.props.state.multiExtra
    );
  }

  multiSelectSingle(
    index: TableConstants.TableIndex,
    param: MultiSelectListUtil.MultiSelectSingleParam<TableConstants.TableIndex> | undefined
  ): void {
    if (!this._isMounted) {
      return;
    }
    const options = {
      setState: this.props.setState.bind(this),
      multiStartIndex: this.props.state.multiStartIndex,
      multiEndIndex: this.props.state.multiEndIndex,
      setMultiEditing: this.setMultiEditing,
      getMultiEditingText: this.getMultiEditingText,
      // When editing, remove any typed value from the spreadsheet store state to prevent it from overwriting an incoming edit on finalize.
      // This handler handles the case where a cell was selected and a single character was typed, triggering cell edit mode.
      getMultiEditingTypedValue:
        param?.editing && (param?.text ?? '') !== '' ? () => null : this.getMultiEditingTypedValue,
      focusBearerNode: this.focusBearerRef.current!,
    };
    MultiSelectListUtil.multiSelectSingle(index, param, options);
    if (param?.editing) {
      this.setTableCellEditorPosition(index);
    }
  }

  /**
   * If possible, this will set the table cell editor position to the cell at the given index.
   * If the cell is not currently visible, then attempt once to scroll the table and cell into view.
   */
  setTableCellEditorPosition(index: TableConstants.TableIndex): void {
    const [rowIndex, columnIndex] = index;
    const cellQuery = `tr td[data-cid='${this.cellCid(rowIndex, columnIndex)}']`;
    const singleInputBoundaryIndex = this.props.singleInputs.boundaryIndex;
    const activeCell = (ReactDOM.findDOMNode(this) as Element).querySelector(
      singleInputBoundaryIndex != null && columnIndex < singleInputBoundaryIndex
        ? `.mediocre-tableEditable-singleInputGroup-container ${cellQuery}`
        : `.mediocre-tableEditable-tableWrapper ${cellQuery}`
    );
    if (!activeCell) {
      this._scrollTableIntoView();
      setTimeout(() => {
        this._scrollCellIntoView(index);
      }, DRAG_SCROLL_DURATION);
      setCellEditorPosition(null);
      this.props.setState({
        hasScrolledSinceEdit: false,
        tableCellEditorIsLoading: true,
      });
      setTimeout(() => {
        if (this._hasActiveCell() && this.props.state.multiSelIndex && !this.props.showCellEditor) {
          this.setTableCellEditorPosition(this.props.state.multiSelIndex);
        }
      }, DRAG_SCROLL_DURATION * 2); // Table scroll + cell scroll
      return;
    }
    const tableScrollContainerRect = this.getTableVerticalScrollWrapperRect();
    const activeCellRect = activeCell.getBoundingClientRect();
    const pageScrollContainerRect = this.getPageVerticalScrollWrapperRect();
    const stickyHeaderRect = this.getStickyHeaderRect();
    const headerRowHeight = this.props.state.rowHeights[0] ?? 0;
    // This may be a ghost cell, so we're not guaranteed to have a computed value yet
    const activeCellIsMerged = this.props.computedTable[rowIndex][columnIndex]?.mergedBox !== undefined;
    assert(
      tableScrollContainerRect != null && pageScrollContainerRect != null,
      'Expected to find the horizontal and vertical scroll containers when starting an edit'
    );
    const topIsOutOfView = pageScrollContainerRect.top > activeCellRect.top;
    const bottomIsOutOfView = pageScrollContainerRect.bottom < activeCellRect.bottom;
    const leftIsOutOfView = tableScrollContainerRect.left > activeCellRect.left;
    const rightIsOutOfView = tableScrollContainerRect.right < activeCellRect.right;
    const activeCellIsTallerThanView = activeCellRect.height > pageScrollContainerRect.height;
    if (
      !this.props.state.tableCellEditorIsLoading &&
      !activeCellIsMerged &&
      // If the cell is taller than the view, we really only care about the top of the cell being in view
      // so don't scroll if the top is already visible
      !(activeCellIsTallerThanView && !topIsOutOfView) &&
      (topIsOutOfView || bottomIsOutOfView || leftIsOutOfView || rightIsOutOfView)
    ) {
      this._scrollCellIntoView(index);
      setCellEditorPosition(null);
      this.props.setState({
        hasScrolledSinceEdit: false,
        tableCellEditorIsLoading: true,
      });
      setTimeout(() => {
        // If the table cell editor should be active but it has not been positioned yet,
        // then attempt to position it.
        if (this._hasActiveCell() && this.props.state.multiSelIndex && !this.props.showCellEditor) {
          this.setTableCellEditorPosition(this.props.state.multiSelIndex);
        }
      }, DRAG_SCROLL_DURATION);
      return;
    }
    const cellEditorPosition = TableUtil.getTableCellEditorPosition(
      tableScrollContainerRect,
      activeCellRect,
      headerRowHeight,
      columnIndex < (this.props.singleInputs.boundaryIndex ?? 0),
      this._isStickyHeaderVisible(),
      stickyHeaderRect
    );

    setCellEditorPosition(cellEditorPosition);

    this.props.setState({
      hasScrolledSinceEdit: false,
      tableCellEditorIsLoading: false,
    });
  }

  multiCancelEdit(index: TableConstants.TableIndex): void {
    if (!this._isMounted) {
      return;
    }
    const options = {
      setMultiEditing: this.setMultiEditing,
      getMultiEditingText: this.getMultiEditingText,
      getMultiEditingTypedValue: this.getMultiEditingTypedValue,
      focusBearerNode: this.focusBearerRef.current!,
    };
    MultiSelectListUtil.multiCancelEdit(index, options);
    setCellEditorPosition(null);
  }

  setMultiIndices(
    {
      start,
      end,
      text,
    }: {
      start: TableConstants.TableIndex;
      end: TableConstants.TableIndex;
      text?: string;
    },
    options: {
      multiSelIndex?: TableConstants.TableIndex | null;
    } = {}
  ): void {
    if (!this._isMounted) {
      return;
    }
    MultiSelectListUtil.setMultiIndices(
      {start, end, text},
      {
        setState: this.props.setState.bind(this),
        multiSelIndex: options.multiSelIndex,
        setMultiEditing: this.setMultiEditing,
        getMultiEditingText: this.getMultiEditingText,
        getMultiEditingTypedValue: this.getMultiEditingTypedValue,
        focusBearerNode: this.focusBearerRef.current!,
      }
    );
  }

  multiEditableMouseDownHandler = (
    args: {
      start: TableConstants.TableIndex;
      end?: TableConstants.TableIndex;
      active?: boolean | string;
      allowSingleOverride?: boolean;
    },
    e: ElementMouseEvent
  ): void => {
    if (!this._isMounted) {
      return;
    }
    const options = {
      multiStartIndex: this.props.state.multiStartIndex,
      multiEndIndex: this.props.state.multiEndIndex,
      multiSelIndex: this.props.state.multiSelIndex,
      setState: this.props.setState.bind(this),
      allowSingleMultiSelect: this.allowSingleMultiSelect(),
      multiTryInsert: this.multiTryInsert,
      multiCrossEditing: this.props.multiCrossEditing,
      multiFinalizeEditHandler: this.props.multiFinalizeEditHandler.bind(this),
      isIndexBetween: TableUtil.isIndexBetween,
      compareIndex: this.compareIndex,
      setMultiEditing: this.setMultiEditing,
      getMultiEditingText: this.getMultiEditingText,
      getMultiEditingTypedValue: this.getMultiEditingTypedValue,
      focusBearerNode: this.focusBearerRef.current!,
      disallowShiftExpand: !this.props.selection.hasRealSelection,
    };
    MultiSelectListUtil.multiEditableMouseDownHandler(args, e, options);
  };

  multiEditableMouseOverHandler(index: TableConstants.TableIndex): void {
    if (!this._isMounted) {
      return;
    }
    const options = {
      setState: this.props.setState.bind(this),
      multiActive: this.props.state.multiActive,
      multiStartIndex: this.props.state.multiStartIndex,
      multiEndIndex: this.props.state.multiEndIndex,
      multiExtra: this.props.state.multiExtra,
      disallowMultiExpand: false,
      multiCrossEditing: this.props.multiCrossEditing,
      allowSingleMultiSelect: this.allowSingleMultiSelect(),
      multiTryInsert: this.multiTryInsert,
      compareIndex: this.compareIndex,
      focusBearerNode: this.focusBearerRef.current!,
    };
    MultiSelectListUtil.multiEditableMouseOverHandler(index, options);
  }

  _getCopySelectionHTML = (): string => this.multiGetHTMLForCopy();
  _getCopySelection = (): string => this.multiGetTextForCopy();

  renderFocusBearer(): JSX.Element {
    return (
      <TableFocusBearer
        ref={this.focusBearerRef}
        state={this.props.state}
        isLockedWithEditWarn={this.isLockedWithEditWarn.bind(this)}
        restoreState={this.props.restoreState}
        setState={this.props.setState.bind(this)}
        setMultiEditing={this.setMultiEditing}
        getMultiEditingText={this.getMultiEditingText}
        getMultiEditingTypedValue={this.getMultiEditingTypedValue}
        firstIndex={this.props.dimensions.firstIndex}
        lastIndex={this.props.dimensions.lastIndex}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        multiSelectable={this as any}
        multiSelectKey={this.props.selection.multiSelectKey}
        multiSelIndex={this.props.state.multiSelIndex}
        multiKeyDownHandler={this.multiKeyDownHandler}
        multiInputHandler={this.multiInputHandler}
        multiPasteHandler={this.multiPasteHandler}
        multiGetHTMLForCopy={this.multiGetHTMLForCopy}
        multiGetTextForCopy={this.multiGetTextForCopy}
        dataTestAttrs={{component: 'TableEditable', element: 'focusBearer'}}
      />
    );
  }

  setTableNameRef = (element: Element | null): void => {
    this.tableNameRef = element;
  };

  /**
   * Returns numeric maximum height for the table, but this does NOT take into account whether the table is
   * maximized or intended to stretch to full height.
   * Use getCurrentMaxTableHeight() if this number is going to be used for DOM.
   */
  getMaxTableHeight(): number {
    return typeof this.props.maximumHeight === 'number'
      ? this.props.maximumHeight
      : TableConstants.DEFAULT_MAXIMUM_FIXED_TABLE_HEIGHT;
  }

  /**
   * Returns the minimum height that <table> tag must be.  It may be taller than this, but it should never
   * be smaller than this height.
   */
  getMinTableHeight(columnHeaderHeight: number = TableConstants.TABLE_AXIS_CELL_HEIGHT): number {
    const minFullHeight =
      this.props.table.table.length * TableConstants.TABLE_ROW_HEIGHT + columnHeaderHeight;
    const maxTableHeight = !this.props.views.isFullHeight ? this.getMaxTableHeight() : undefined;
    if (maxTableHeight) {
      return Math.min(minFullHeight, maxTableHeight);
    }
    return minFullHeight;
  }

  /**
   * @returns the maximum height of the table if it is fixed height, or undefined if it is not.
   * Unless a table is maximized/split screen, or it has explicity been set to full height by the props,
   * this will return the default maximum height of the given table.
   */
  getCurrentMaxTableHeight(): number | undefined {
    if (
      this.props.maximumHeight === TableConstants.MaximumTableHeight.FULL ||
      this.props.views.isMaximizedOrSplitScreen ||
      this.props.views.isFullHeight ||
      this.props.viewer // viewer mode always shows full height
    ) {
      return;
    }
    return this.getMaxTableHeight();
  }

  /**
   * Returns the height of the table if it is fixed height, or null if it is not.
   * The height is calculated based on the number of rows visible in the table.
   * This will either return null (no height computed), FULL to expand the to show all rows,
   * or a number representing the maximum height of the table.
   *
   * This method is called when initializing the props object, so this.props.state cannot be used inside of it.
   * Instead, values from state should be passed in as optional arguments.
   */
  getCurrentTableHeight(
    rowHeights?: Array<number>,
    horizontalScrollbarVisible?: boolean,
    isFullHeight: boolean = false
  ): TableConstants.MaximumTableHeightType | null {
    if (this.props.views.isMaximizedOrSplitScreen) {
      return null;
    }
    if (
      this.props.viewer ||
      this.props.maximumHeight === TableConstants.MaximumTableHeight.FULL ||
      isFullHeight
    ) {
      return TableConstants.MaximumTableHeight.FULL;
    }
    const maxHeight = this.getMaxTableHeight();
    const tableColumnHeadersHeight =
      rowHeights && rowHeights.length > 0 && rowHeights[0] > 0
        ? Math.max(TableConstants.TABLE_AXIS_CELL_HEIGHT, rowHeights[0])
        : TableConstants.TABLE_AXIS_CELL_HEIGHT;
    const minimumRowHeight = TableConstants.TABLE_ROW_HEIGHT;
    const tableAreaTopAndBottomBorder = 2;
    const horizontalScrollbarHeight = horizontalScrollbarVisible ? TableConstants.SCROLLBAR_WIDTH : 0;

    if (this.props.isCollapsed) {
      return 0;
    }

    let tableHeight: number = tableAreaTopAndBottomBorder + horizontalScrollbarHeight;
    let heightOfAllRows: number = tableColumnHeadersHeight;
    // If the real row heights haven't been measured, then take a best guess using the default row height to
    // prevent the table from jittering while it first renders.
    if (rowHeights && rowHeights.length > 1 && rowHeights[0] > 0) {
      for (let i = 1; i < rowHeights.length; i++) {
        const height = rowHeights[i] ?? minimumRowHeight;
        heightOfAllRows += height;
      }
    }
    // The height of the table should be at least as tall as the number of rows times the minimum row height
    tableHeight += Math.max(heightOfAllRows, this.getMinTableHeight(tableColumnHeadersHeight));

    if (tableHeight >= maxHeight) {
      return maxHeight;
    }
    return TableConstants.MaximumTableHeight.FULL;
  }

  setCanShowStickyHeader = (canShowStickyHeader: boolean): void => {
    this.props.setState({canShowStickyHeader});
  };

  setStickyHeaderPositioning = (stickyHeaderPositioning: StickyHeaderPositioning): void => {
    this.props.setState({stickyHeaderPositioning});
  };

  _measureTableSize(): void {
    if (!this._isMounted || this.props.isCollapsed) {
      return;
    }
    const trs = Array.from(
      (ReactDOM.findDOMNode(this) as Element).querySelectorAll(
        '.mediocre-tableEditable-table--main tr td:first-child'
      )
    );
    const rowHeights = trs.map((tr) => tr.getBoundingClientRect().height);
    // The first row contains an extra top border which affects the height computation of the first row
    // However, this border is also applied to the first filler row, so we need to remove it here.
    if (rowHeights.length > 0) {
      rowHeights[0] -= 1;
    }

    // The header can be suppressed, hence the ?. below
    const headerWidth = this.tableNameRef?.getBoundingClientRect().width;

    const tableWrapper = (ReactDOM.findDOMNode(this) as Element).querySelector(
      '.mediocre-tableEditable-tableWrapper'
    );
    const tableWrapperWidth = tableWrapper?.getBoundingClientRect()?.width;

    // Only show the horizontal scrollbar if the table is wider than the header
    // If the header is not rendered yet, assume the table does not scroll horizontally
    const headerColumnWidth =
      (ReactDOM.findDOMNode(this) as Element)
        .querySelector('.mediocre-tableEditable-axisCell--header')
        ?.getBoundingClientRect()?.width ?? 0;
    const sumOfColumnWidths =
      this.props.table.widths.reduce((acc, width) => acc + width, 0) + (headerColumnWidth ?? 0);
    const hasHorizontalScroll =
      this.props.dimensions.shouldShowGhostCells || (headerWidth ? sumOfColumnWidths > headerWidth : false);

    const tableHeight = this.getCurrentTableHeight(
      rowHeights,
      hasHorizontalScroll,
      this.props.views.isFullHeight
    );

    const pagePaddingTop = this.verticalScrollContainer
      ? parseFloat(window.getComputedStyle(this.verticalScrollContainer).paddingTop) || 0
      : 0;
    const pagePaddingBottom = this.verticalScrollContainer
      ? parseFloat(window.getComputedStyle(this.verticalScrollContainer).paddingBottom) || 0
      : 0;
    if (
      pagePaddingTop !== this.props.state.pagePaddingTop ||
      pagePaddingBottom !== this.props.state.pagePaddingBottom
    ) {
      this.props.setState({
        pagePaddingTop,
        pagePaddingBottom,
      });
    }

    if (!isEqual(this.props.state.rowHeights, rowHeights)) {
      this.props.setState({rowHeights});
    }
    if (headerWidth != null && !isEqual(this.props.state.headerWidth, headerWidth)) {
      this.props.setState({headerWidth});
    }
    if (tableWrapperWidth && !isEqual(this.props.state.tableWrapperWidth, tableWrapperWidth)) {
      this.props.setState({tableWrapperWidth});
    }
    if (this.props.state.currentFixedTableHeight !== tableHeight) {
      this.props.setState({currentFixedTableHeight: tableHeight});
    }
  }

  shouldRenderFooter(): boolean {
    return !this.props.hideFooter && !this.props.viewer;
  }

  shouldShowPersistentHorizontalScrollbar(): boolean {
    return !this.props.viewer;
  }

  override shouldComponentUpdate(nextProps: TableEditableClassProps, nextState: TableEditableState): boolean {
    // Need to deep compare for @props.selectorsLink
    return !isEqual(this.props, nextProps) || !isEqual(this.props.state, nextState);
  }

  onVerticalScroll = (): void => {
    if (this.props.showCellEditor && !this.props.state.hasScrolledSinceEdit) {
      this.props.setState({hasScrolledSinceEdit: true});
    }
  };

  findVerticalScrollContainer = (): void => {
    this.verticalScrollContainer?.removeEventListener('scroll', this.onVerticalScroll);
    this.verticalScrollContainer = findScrollContainer(ReactDOM.findDOMNode(this) as HTMLDivElement);
    this.verticalScrollContainer?.addEventListener('scroll', this.onVerticalScroll);
  };

  override componentDidMount(): void {
    this._isMounted = true;
    this.props.setState({currentFixedTableHeight: this.getCurrentTableHeight()});

    // Measure the height of the main table rows so that we can simply guarantee that the affixed rows are
    // rendered at the correct height.
    this._measureId = setInterval(() => {
      this._measureTableSize();

      // If the vertical scroll container is the body, then we most likely missed finding the real scroll
      // container.  Try again after the page has loaded.
      // There are some instances where the scroll css properties for the page are added after this component
      // has already mounted, so we need to check again.
      // The body should never be the scroll container of a table in Benchling
      if (this.verticalScrollContainer === document.body) {
        this.findVerticalScrollContainer();
      }
    }, 500);
    const collapseId = getCollapseId(this.props);
    if (collapseId) {
      CollapsedTableReduxActions.init(collapseId, this.props.isCollapsedInitially);
    }

    const selectionState = SpreadsheetStore.getState(
      this.props.tableNamespace,
      this.props.table.name,
      this.props.fileId
    );
    if (selectionState.editingTable === this.props.table.name) {
      this.props.setState({
        multiEditing: true,
        multiSelIndex: selectionState.index,
        multiStartIndex: selectionState.index,
        multiEndIndex: selectionState.index,
      });
    }

    this.findVerticalScrollContainer();

    this.updateCellPerfId = Perf.start(
      'update_table_cell',
      {enabled: false},
      {
        interval: UPDATE_TABLE_CELL_PERF_INTERVAL,
        aggregations: [Perf.Aggregation.MEDIAN, Perf.Aggregation.MAX, Perf.Aggregation.COUNT],
      }
    );
    this.props.setState({lockedRowIndices: TableUtil.lockedRowIndices(this.props.table) ?? []});
  }

  override componentDidUpdate(prevProps: TableEditableClassProps, prevState: TableEditableState): void {
    if (!this._isMounted) {
      return;
    }
    if (this.props.isCollapsed !== prevProps.isCollapsed) {
      this._measureTableSize();
    }
    const options = {
      domNode: ReactDOM.findDOMNode(this) as Element,
      multiEditing: this.props.multiEditing,
      improperFocusHook: this.improperFocusHook,
      multiStartIndex: this.props.state.multiStartIndex,
      multiEndIndex: this.props.state.multiEndIndex,
      focusBearerNode: this.focusBearerRef.current!,
    };
    MultiSelectListUtil.multiSelectableDidUpdate(options);

    // if locked row indices have changed, update the state value that we pass to TableRow
    const prevLockedRowIndices = new Set(this.props.state.lockedRowIndices);
    const newLockedRowIndices = new Set(TableUtil.lockedRowIndices(this.props.table));
    if (
      newLockedRowIndices.size !== prevLockedRowIndices.size ||
      ![...newLockedRowIndices].every((index) => prevLockedRowIndices.has(index))
    ) {
      this.props.setState({lockedRowIndices: Array.from(newLockedRowIndices)});
    }

    if (
      this.props.selectedSearchResultTree !== prevProps.selectedSearchResultTree &&
      !isIndexTreeEmpty(this.props.selectedSearchResultTree)
    ) {
      this.scrollToSearchResult();
    }

    // If the table cell editor should be active but it has not been positioned yet,
    // then attempt to position it.
    if (
      this._hasActiveCell() &&
      this.props.state.multiSelIndex &&
      !this.props.showCellEditor &&
      !this.props.state.tableCellEditorIsLoading &&
      this.props.state.multiActive
    ) {
      this.setTableCellEditorPosition(this.props.state.multiSelIndex);
    }
    // Otherwise, make sure the table cell editor's positioning has been reset
    // Only reset if we're actually done editing (not just changing editing state)
    // AND we're not in the middle of setting up a new cell editor for an active selection
    if (
      !this.props.multiEditing &&
      prevProps.multiEditing &&
      this.props.showCellEditor &&
      !this.props.state.multiSelIndex
    ) {
      setCellEditorPosition(null);
      this.props.setState({hasScrolledSinceEdit: false});
    }

    // Only scroll if the index is actually included in the table, there might be a delay between the scroll
    // request and the new table object actually making it back to this component from the note
    if (
      this.props.state.scrollToIndexAfterUpdate != null &&
      this.props.state.scrollToIndexAfterUpdate < this.props.table.table.length
    ) {
      const index = this.props.state.scrollToIndexAfterUpdate;
      this.props.setState({scrollToIndexAfterUpdate: null});
      // The new table object still needs to make it to TableVirtuoso before we try to scroll, so give it one
      // render cycle before attempting to scroll.
      setTimeout(() => {
        // This should hightlight the first non-single-input cell in the last row
        this._scrollCellIntoView([index, this.props.singleInputs.boundaryIndex ?? 0]);
      }, 1);
    }
  }

  override componentWillUnmount(): void {
    this._isMounted = false;

    clearInterval(this._measureId);
    this.stopDragAutoscroll();
    $(window).off('mouseup', this.fillDownMouseUpHandler);
    Perf.stop(this.updateCellPerfId);
  }

  override UNSAFE_componentWillReceiveProps(nextProps: TableEditableClassProps): void {
    MultiSelectListUtil.multiSelectableWillReceiveProps(
      this.props,
      nextProps,
      this.props.selection.multiSelectKey,
      this.props.setState.bind(this)
    );
  }

  isRealIndex(index: TableConstants.TableIndex | null): boolean {
    if (index == null) {
      return false;
    }
    return computeIsRealIndex(index, this.props.table);
  }

  isCellSelected(index: TableConstants.TableIndex): boolean {
    if (!this.props.selection.hasMultiSelection) {
      return false;
    }
    const {minI, maxI, minJ, maxJ} = this._multiSelectionBox();
    return TableUtil.isIndexBetween([minI, minJ], [maxI, maxJ], index);
  }

  scrollToSearchResult(): void {
    // Normally, these would automatically get scrolled to by DOM.ts.  But if the cell is virtualized out of
    // view, then we we need to manually scroll to it.
    const iIndexes = getIndexesfromIndexTreeRoot(this.props.selectedSearchResultTree);
    checkSoftInvariant(iIndexes.length === 1, {
      message: 'Expected only one search i index to be selected',
      expirationDate: '2026-02-01',
      errorInDevAndTest: true,
    });

    /*
      Search results for table names have the path [tableCid, 'name'], without a j index
      Search results for column headers have the path [tableCid, 'tableHeaderRow', columnIndex]
      In both cases, scroll the table into view, and then let the DOM.ts handle highlighting
    */
    if (iIndexes[0] === TABLE_SEARCH_PATHS.NAME || iIndexes[0] === TABLE_SEARCH_PATHS.TABLE_HEADER_ROW) {
      this._scrollTableHeaderIntoView();
      return;
    }

    const jIndexes = getIndexesfromIndexTreeRoot(
      getSubtreeAtIndex(this.props.selectedSearchResultTree, iIndexes[0])
    );
    checkSoftInvariant(jIndexes.length === 1, {
      message: 'Expected only one search j index to be selected',
      expirationDate: '2026-02-01',
      errorInDevAndTest: true,
    });

    // Search results for cell contents have the path [tableCid, rowIndex, columnIndex]
    const i = Number(iIndexes[0]);
    const j = Number(jIndexes[0]);

    // Scroll table header into view first, then scroll to cell after repaint
    this._scrollTableHeaderIntoView();
    requestAnimationFrame(() => {
      this._scrollCellIntoView([i, j]);
    });
  }

  getSelectionPrecision(): Optional<number> {
    if (!this.props.selection.hasRealSelection) {
      return null;
    }
    const {minI, maxI, minJ, maxJ} = this._multiSelectionBox();
    let precision: Optional<number> = null;
    for (let i = minI; i <= maxI; i++) {
      for (let j = minJ; j <= maxJ; j++) {
        if (TableUtil.isNotRendered(i, j, this.props.computedTable[i][j])) {
          continue;
        }
        if (precision != null && precision !== this.props.table.table[i][j].precision) {
          return null;
        }
        if (precision == null) {
          ({precision} = this.props.table.table[i][j]);
        }
      }
    }

    return precision;
  }

  getSelectionAlignment(): AlignmentState {
    return {
      active: this.getSelectionFormatting(FormattersConstants.ALIGN),
      default:
        this.getSelectionFormatting((cell, evaluatedCell) => TableUtil.alignCell(cell, evaluatedCell)) ??
        'left',
    };
  }

  getSelectionLink(): {url?: Optional<string>; text?: string} {
    if (
      !this.props.selection.hasRealSelection ||
      this.linkButtonDisabled() ||
      this.props.state.multiStartIndex == null
    ) {
      return {};
    }
    const [row, column] = this.props.state.multiStartIndex;
    return {
      url: this.props.table.table[row][column].link,
      text: this.props.table.table[row][column].text,
    };
  }

  _isSelectionOnlyDatetimeCells(): boolean {
    // At least one, and only real cells are selected
    if (!this.props.selection.hasRealSelection) {
      return false;
    }
    // If any column is not for a datetime, disallow inserting now.
    const {minJ, maxJ} = this._multiSelectionBox();
    for (let j = minJ; j <= maxJ; j++) {
      const constraint = this.props.table.columnConstraints[j];
      if (
        !(
          constraint?.columnDefinition?.type === TableColumnType.TAG_VALUE &&
          constraint?.columnDefinition?.schemaField.fieldType === FIELD_TYPES.DATETIME.type
        ) &&
        !(
          constraint?.columnDefinition?.type === TableColumnType.CUSTOM &&
          constraint?.columnDefinition?.customColumnConfig.dataType === CustomColumnDataType.DATETIME
        )
      ) {
        return false;
      }
    }
    return true;
  }

  /**
   * Return the common `format` value across all selected elements. If no
   * elements are selected or the format values aren't equal, return `otherwise`.
   */
  getSelectionFormatting<T extends string | boolean | number>(
    format: string | ((cell: Cell, evaluatedCell: EvaluatedCell) => T)
  ): T | undefined {
    const keyFn = _.iteratee(format);
    if (!this.props.selection.hasRealSelection) {
      return;
    }
    let value = null;
    const {minI, maxI, minJ, maxJ} = this._multiSelectionBox();
    for (let i = minI; i <= maxI; i++) {
      for (let j = minJ; j <= maxJ; j++) {
        if (TableUtil.isNotRendered(i, j, this.props.computedTable[i][j])) {
          continue;
        }
        const cell = this.props.table.table[i][j];
        const evaluatedCell = this.props.computedTable[i][j];
        // If we are a header and it was not already explicitly false, skip.
        if (
          format === 'bold' &&
          ((i === 0 && this.props.table.headerRow) || (j === 0 && this.props.table.headerCol))
        ) {
          value = true;
          // Else if this is the first loop, set the value to match against
        } else if (i === minI && j === minJ) {
          value = keyFn(cell, evaluatedCell);
          // Else if the current value doesn't match the previous, return null
        } else if (value !== keyFn(cell, evaluatedCell)) {
          return;
        }
      }
    }
    return value;
  }

  getEvaluatedFillDownDestCells(): Array<{
    cellId: TableConstants.CellIndex;
    evaluatedCell: EvaluatedCell;
  }> {
    if (!this.props.fillDown.box || !this.props.selection.hasMultiSelection) {
      return [];
    }
    const evaluatedCells = [];
    const {minI, maxI, minJ, maxJ} = this.props.fillDown.box;
    for (let i = minI; i <= maxI; i++) {
      for (let j = minJ; j <= maxJ; j++) {
        if (this.isRealIndex([i, j]) && !this.isCellSelected([i, j])) {
          evaluatedCells.push({
            evaluatedCell: this.props.computedTable[i][j],
            cellId: {row: i, column: j},
          });
        }
      }
    }
    return evaluatedCells;
  }

  _setSelectionProperty = (propertyUpdates: Array<FormatPropertyChange>): void => {
    this.saveMultiSelect();
    const tableOp: TableConstants.UpdateCellsTableOp = {type: 'update-cells', cells: []};
    const newTable = produce(this.props.table, (draftTable) => {
      draftTable.table.forEach((draftRow, i) => {
        draftRow.forEach((draftCell, j) => {
          if (!this.isCellSelected([i, j])) {
            return;
          }
          for (const {property, value} of propertyUpdates) {
            if (TableConstants.EVALUABLE_CELL_PROPERTIES.includes(property)) {
              tableOp.cells.push({row: i, column: j});
            }
            if (value != null) {
              // The `property` string is not typesafe
              // @ts-expect-error
              draftCell[property] = value;
            } else {
              // @ts-expect-error
              delete draftCell[property];
            }
          }
        });
      });
    });
    this.updateHandler(this.props.index, {table: newTable}, undefined, {
      [this.props.table.name]: tableOp.cells.length > 0 ? tableOp : {type: 'noop'},
    });
  };

  setSelectionSelector = (staticId: string | null): void => {
    this._setSelectionProperty([{property: 'selectorId', value: staticId}]);
  };

  _mergeButtonFormatting(): {active: boolean; disabled?: boolean} {
    if (
      !this.props.selection.hasRealSelection ||
      this.props.state.multiStartIndex == null ||
      this.props.state.multiEndIndex == null
    ) {
      return {active: false};
    }

    const {multiStartIndex, multiEndIndex} = this.props.state;
    const firstNonSingleInputIndex = this.props.singleInputs.boundaryIndex;
    if (firstNonSingleInputIndex && multiStartIndex && multiStartIndex[1] < firstNonSingleInputIndex) {
      return {active: false, disabled: true};
    }

    const {minI, minJ, maxJ} = TableUtil.indicesToCorners(multiStartIndex, multiEndIndex);
    if (selectionStraddlesFreezeBoundary(minJ, maxJ, this.getFreezeBoundary())) {
      return {active: false, disabled: true};
    }
    if (this.compareIndex(multiStartIndex, multiEndIndex) !== 0) {
      if (this.props.allowMergeSelection) {
        return {active: false, disabled: !this.props.allowMergeSelection(multiStartIndex, multiEndIndex)};
      }
      return {active: false};
    }

    const evaluatedCell = this.props.computedTable[minI][minJ];
    if (TableUtil.isRenderedMergedCell(minI, minJ, evaluatedCell)) {
      return {active: true};
    } else {
      return {active: false, disabled: true};
    }
  }

  // Merges the selected region into one cell. Warns the user if they have text in any cell other than the
  // top-leftmost cell.
  mergeSelection(): void {
    const selectedBox = this._multiSelectionBox();
    if (selectionStraddlesFreezeBoundary(selectedBox.minJ, selectedBox.maxJ, this.getFreezeBoundary())) {
      showFreezeMergeConflictToast('merge');
      return;
    }
    this.saveMultiSelect();
    if (computeHasTextInMergedRegion(this.props.table.table, selectedBox)) {
      const msg = 'Merging cells will only preserve the top-leftmost value. Merge anyway?';
      modals.confirm('Merge Selection', msg, 'danger').done(() => {
        this.props.tableMutations.mergeRegion!(this.props.table.name, selectedBox);
      });
    } else {
      this.props.tableMutations.mergeRegion!(this.props.table.name, selectedBox);
    }
  }

  unmergeSelection(): void {
    this.saveMultiSelect();
    this.props.tableMutations.unmergeRegion!(this.props.table.name, this._multiSelectionBox());
  }

  mergeHandler = (merge: boolean): void => {
    assert(this.supportsMerge(), 'Expected table to support merge');
    if (merge) {
      this.mergeSelection();
    } else {
      this.unmergeSelection();
    }
  };

  supportsMerge(): boolean {
    return this.props.tableMutations.mergeRegion != null && this.props.tableMutations.unmergeRegion != null;
  }

  renameTable = (tableName: string | null): void => {
    if (tableName != null) {
      this.saveMultiSelect({selection: {cid: this.getTableItemCid(), start: 0, end: 0}});

      CollapsedTableReduxActions.changeId(
        getCollapseId(this.props),
        createCollapseId(this.props.fileId, tableName)
      );

      this.props.tableMutations.renameTable(this.props.table.name, tableName);
    }

    this.props.setState({showRenameTableModal: false});
  };

  mouseDownHandler = (e: ElementMouseEvent): void => {
    if (this.props.mouseDownHandler) {
      this.props.mouseDownHandler(this.props.index, e);
    }
  };

  mouseOverHandler = (e: ElementMouseEvent): void => {
    if (this.props.mouseOverHandler) {
      this.props.mouseOverHandler(this.props.index, e);
    }
  };

  setMultiEditing = (e: {
    index: TableConstants.TableIndex | null;
    editing: boolean;
    text: string | null;
    typedValue?: Optional<TableConstants.TypedValue>;
    selection?: BenchlingSelection | null;
  }): void => {
    this.props.setMultiEditingForTable(
      {tableName: this.props.table.name, documentName: this.props.documentName ?? null},
      e
    );
  };

  multiTryInsert = (options: TryInsertOptions): boolean => {
    // If the selected index is a Single Input, lock the row reference with a $
    const [, columnIndex] = options.index;
    if (this.props.table.columnConstraints[columnIndex]?.structuredTableColumn?.isSingleInput) {
      options.cellLock = {row: true};
    }
    return this.props.tryInsert(
      {tableName: this.props.table.name, documentName: this.props.documentName ?? null},
      options
    );
  };

  multiCanInsert = (options: {start: number; end: number; cid: string; shift: boolean}): boolean =>
    this.props.canInsert(options);

  multiNamespace(): string {
    return this.props.tableNamespace;
  }

  getRequiredMultiSelIndex(): TableConstants.TableIndex {
    assert(this.props.state.multiSelIndex != null, 'Table interaction failed due to missing selection');
    return this.props.state.multiSelIndex;
  }

  scrollCellIntoViewAfterEdit(): void {
    if (
      this.props.state.hasScrolledSinceEdit &&
      this.props.showCellEditor &&
      this.props.state.multiSelIndex
    ) {
      this._scrollCellIntoView(this.props.state.multiSelIndex);
      this._scrollTableIntoView();
      setCellEditorPosition(null);
      this.props.setState({hasScrolledSinceEdit: false});
    } else if (this.props.showCellEditor) {
      // If they haven't scrolled, then just wipe the table cell editor position since we're done editing
      setCellEditorPosition(null);
    }
  }

  getMultiEditingCellValue(index: TableConstants.TableIndex): TableConstants.Cell | null {
    const [i, j] = Array.from(index);
    const tableRow = this.props.table.table[i];
    if (tableRow == null) {
      return null;
    }
    const tableCell = tableRow[j];
    if (tableCell == null) {
      return null;
    }
    return tableCell;
  }

  getMultiEditingText = (index: TableConstants.TableIndex): string => {
    const tableCell = this.getMultiEditingCellValue(index);
    return tableCell && tableCell.text != null ? tableCell.text : '';
  };

  getMultiEditingTypedValue = (index: TableConstants.TableIndex): TableConstants.TypedValue | null => {
    const tableCell = this.getMultiEditingCellValue(index);
    return tableCell && tableCell.typedValue != null ? tableCell.typedValue : null;
  };

  setTableWidths(widths: Array<number>): void {
    this.saveMultiSelect();
    this.updateHandler(
      this.props.index,
      {
        table: {...this.props.table, widths},
      },
      undefined,
      {[this.props.table.name]: {type: 'noop'}}
    );
    // If the cell editor is currently displayed, then update its positioning now that the widths have changed
    if (this._hasActiveCell() && this.props.showCellEditor && this.props.state.multiSelIndex) {
      this.setTableCellEditorPosition(this.props.state.multiSelIndex);
    }
  }

  setColumnWidth = (columnIndexString: string, width: number): void => {
    const resizedColumnIndex = parseInt(columnIndexString, 10);
    let columnRangeToResize: [number, number];

    // If the column is part of a multi-axis selection, resize all columns in the selection
    const columnSelection = this.props.selection.multiAxisDimSelection.column;
    if (
      columnSelection &&
      columnSelection[0] <= resizedColumnIndex &&
      resizedColumnIndex <= columnSelection[1]
    ) {
      columnRangeToResize = columnSelection;
    } else {
      columnRangeToResize = [resizedColumnIndex, resizedColumnIndex];
    }
    const newWidths = [...this.props.table.widths];
    for (let i = columnRangeToResize[0]; i <= columnRangeToResize[1]; i++) {
      newWidths[i] = width;
    }
    this.setTableWidths(newWidths);
  };

  setSingleInputColumnWidth = (columnIndex: number, width: number): void => {
    const newWidths = [...this.props.table.widths];
    newWidths[columnIndex] = width;
    this.setTableWidths(newWidths);
  };

  previousIndex(index: TableConstants.TableIndex): TableConstants.TableIndex {
    const [row, column] = index;
    return [row, Math.max(column - 1, 0)];
  }

  nextIndex(index: TableConstants.TableIndex): TableConstants.TableIndex {
    const [row, column] = index;
    return [row, Math.min(column + 1, this.props.table.table[0].length - 1)];
  }

  extendedLastIndex(): TableConstants.TableIndex {
    const totalColumnCount = TableUtil.getColumnCount(
      this.props.table,
      this.props.dimensions.shouldShowGhostCells
    );
    return [this.props.table.table.length - 1, totalColumnCount - 1];
  }

  compareIndex = (a: TableConstants.TableIndex, b: TableConstants.TableIndex): number =>
    a[0] - b[0] || a[1] - b[1];

  allowSingleMultiSelect = (): true => true;

  deleteRangeHelper = (): void => {
    if (!this.props.selection.hasRealSelection) {
      return;
    }
    this.saveMultiSelect();

    // Get the selection box (handles merged cells)
    const selectionBox = this._multiSelectionBox();

    // Delegate to the hook which handles mutation, persistence, and analytics
    this.props.tableMutations.clearCellsInRange(selectionBox);

    this.setMultiEditing({editing: false, index: null, text: ''});
  };

  expandToAndEditCell = ([i, j]: TableConstants.TableIndex, selectAll: boolean = false): void => {
    if (this.isLockedWithEditWarn()) {
      return;
    }

    let text;
    let typedValue;
    let end;
    if (!this.isRealIndex([i, j])) {
      ({text, typedValue} = this.expandToCell([i, j]));
      end = text.length;
    } else {
      text = null;
      typedValue = null;
      end = this.props.table.table[i][j].text.length;
    }

    this.multiSelectSingle([i, j], {
      text,
      typedValue: typedValue || null,
      editing: true,
      collapse: false,
      selection: {
        cid: this.cellCid(i, j),
        start: selectAll ? 0 : end,
        end,
      },
    });
  };

  _isFillDownBoxEditingLocked(): boolean {
    return (
      this.props.locking.isTableLocked ||
      computeAnyCellsLockedOrArchived(this.getEvaluatedFillDownDestCells(), this.props.table.rowData)
    );
  }

  getLockedWarningMessage(
    editingIsLocked?: boolean,
    selection?: TableConstants.MultiSelectionBox | null
  ): string | null {
    if (editingIsLocked == null) {
      return this.props.locking.lockedWarningMessage;
    }
    // Used for fill-down and other handlers with a dynamic selection box not on the model.
    return computeLockedWarningMessage({
      editingIsLocked,
      isTableLocked: this.props.locking.isTableLocked,
      editWhileTableLockedWarning: this.props.editWhileTableLockedWarning,
      getEditWhileCellLockedWarning: this.props.getEditWhileCellLockedWarning,
      selection: selection ?? null,
    });
  }

  _getLockedWarningMessageForCheck(args: LockedCellCheckArgs | undefined, locked: boolean): string | null {
    if (args == null) {
      return this.props.locking.lockedWarningMessage;
    }
    if (args.onlyAnchor) {
      return this.props.locking.lockedWarningMessageIfAnchorLocked;
    }
    if (args.forUndoRedo) {
      return this.props.locking.lockedWarningMessageForUndoRedo;
    }
    return this.getLockedWarningMessage(
      locked,
      this.props.selection.hasMultiSelection ? this.props.selection.expandedBox : null
    );
  }

  isFillDownBoxLockedWithEditWarn(): boolean {
    const locked = this._isFillDownBoxEditingLocked();
    const message = this.getLockedWarningMessage(locked, this.props.fillDown.box);
    if (message) {
      notifications.warn(message, {
        id: 'TableEditable.isLockedWithEditWarn',
      });
    }
    return locked;
  }

  isLockedWithEditWarn(args?: LockedCellCheckArgs): boolean {
    let locked: boolean;
    if (args?.onlyAnchor) {
      locked = this.props.locking.isAnchorCellSelectionLocked;
    } else if (args?.forUndoRedo) {
      locked = this.props.locking.isCellSelectionLockedForUndoRedo;
    } else {
      locked = this.props.locking.isCellSelectionLocked;
    }
    const message = this._getLockedWarningMessageForCheck(args, locked);
    if (message) {
      notifications.warn(message, {
        id: 'TableEditable.isLockedWithEditWarn',
      });
    }
    return locked;
  }

  isParentLockedWithEditWarn(args?: LockedCellCheckArgs): boolean {
    if (this.props.isParentLockedWithEditWarn) {
      return this.props.isParentLockedWithEditWarn();
    }
    return this.isLockedWithEditWarn(args);
  }

  multiKeyDownHandler = (e: ElementKeyboardEvent): void => {
    if (this.props.fillDown.isActive || this.props.state.draggingDimension) {
      e.preventDefault();
      return;
    }

    this.hideHoverCell();

    // Experimental hotkeys under config flag
    if (BenchlingConfig.ELN_ENABLE_TABLE_EXPERIMENTAL_HOTKEYS) {
      if (SHIFT_ENTER_INSERT_ROW.matches(e)) {
        if (!this.props.dimensions.canAddRows) {
          return;
        }
        e.preventDefault();
        trackKeyboardShortcut(SHIFT_ENTER_INSERT_ROW.description);
        this.handleAddBlankRows(1);
        return;
      } else if (PAGE_UP.matches(e)) {
        e.preventDefault();
        trackKeyboardShortcut(PAGE_UP.description);
        this.selectRowColumnEnd(TableConstants.TABLE_COLUMN, true);
        return;
      } else if (PAGE_DOWN.matches(e)) {
        e.preventDefault();
        trackKeyboardShortcut(PAGE_DOWN.description);
        this.selectRowColumnEnd(TableConstants.TABLE_COLUMN, false);
        return;
      } else if (HOME.matches(e)) {
        e.preventDefault();
        trackKeyboardShortcut(HOME.description);
        this.selectRowColumnEnd(TableConstants.TABLE_ROW, true);
        return;
      } else if (END.matches(e)) {
        e.preventDefault();
        trackKeyboardShortcut(END.description);
        this.selectRowColumnEnd(TableConstants.TABLE_ROW, false);
        return;
      } else if (SHIFT_SPACE.matches(e)) {
        e.preventDefault();
        trackKeyboardShortcut(SHIFT_SPACE.description);
        this.toggleCurrentRowColumn(TableConstants.TABLE_ROW);
        return;
      } else if (CTRL_SPACE.matches(e)) {
        e.preventDefault();
        trackKeyboardShortcut(CTRL_SPACE.description);
        this.toggleCurrentRowColumn(TableConstants.TABLE_COLUMN);
        return;
      }
    }

    // Early return if no cell is selected - prevents keyboard shortcuts from causing errors
    if (this.props.state.multiSelIndex == null) {
      return;
    }

    if (DELETE.matches(e)) {
      e.preventDefault();
      if (this.isLockedWithEditWarn()) {
        return;
      }
      trackKeyboardShortcut(DELETE.description);
      this.deleteRangeHelper();
    } else if (TAB.matches(e)) {
      e.preventDefault();
      trackKeyboardShortcut(TAB.description);
      const index = this.getRequiredMultiSelIndex();
      this._commitPendingEditIfNeeded();
      this.moveFromCell({index, wrap: true, dir: 'right'});
    } else if (SHIFT_TAB.matches(e)) {
      e.preventDefault();
      trackKeyboardShortcut(SHIFT_TAB.description);
      const index = this.getRequiredMultiSelIndex();
      this._commitPendingEditIfNeeded();
      this.moveFromCell({index, wrap: true, dir: 'left'});
    } else if (ENTER.matches(e)) {
      e.preventDefault();
      trackKeyboardShortcut(ENTER.description);
      const index = this.getRequiredMultiSelIndex();
      if (this._commitPendingEditIfNeeded()) {
        // Pending edit from rapid device input (e.g. balance/scale) — commit and navigate down.
        this.moveFromCell({index, dir: 'down'});
      } else {
        // No pending edit — open the cell editor (standard Enter behavior).
        this.expandToAndEditCell(index, true);
      }
    } else if (F2.matches(e)) {
      e.preventDefault();
      trackKeyboardShortcut(F2.description);
      this.expandToAndEditCell(this.getRequiredMultiSelIndex(), false);
    } else if (ARROW_KEYS_EXTEND_JUMP.matches(e)) {
      e.preventDefault();
      trackKeyboardShortcut(ARROW_KEYS_EXTEND_JUMP.description);
      this.processArrowKeyPress(keyboard.getArrowKeyDirection(e), true, true);
    } else if (ARROW_KEYS_EXTEND.matches(e)) {
      e.preventDefault();
      trackKeyboardShortcut(ARROW_KEYS_EXTEND.description);
      this.processArrowKeyPress(keyboard.getArrowKeyDirection(e), true, false);
    } else if (ARROW_KEYS_JUMP.matches(e)) {
      e.preventDefault();
      trackKeyboardShortcut(ARROW_KEYS_JUMP.description);
      this.processArrowKeyPress(keyboard.getArrowKeyDirection(e), false, true);
    } else if (ARROW_KEYS.matches(e)) {
      e.preventDefault();
      trackKeyboardShortcut(ARROW_KEYS.description);
      this.processArrowKeyPress(keyboard.getArrowKeyDirection(e), false, false);
    } else {
      const format = formatters.formatTypeFromKeydown(e, {allowLink: false});
      if (format === FormattersConstants.SYMBOL) {
        notifications.warn('Inserting symbols into tables is not currently supported.');
        return;
      }
      // Ensure formatting hotkeys are consumed so that they don't trigger cell editing
      if (format != null) {
        e.preventDefault();
      }
    }
  };

  // Helper method that determines where to move given that an arrow key was pressed.
  processArrowKeyPress(
    dir: 'up' | 'down' | 'left' | 'right',
    shiftKeyPressed: boolean,
    cmdKeyPressed: boolean
  ): void {
    let newActualIndex;
    let newIndex;
    if (
      this.props.state.multiStartIndex == null ||
      this.props.state.multiEndIndex == null ||
      this.props.state.multiActualIndex == null
    ) {
      return;
    }
    if (shiftKeyPressed) {
      if (!this.props.selection.hasRealSelection) {
        return;
      }
      if (cmdKeyPressed) {
        if (this.props.state.multiEndIndex) {
          ({newIndex, newActualIndex} = this.cmdMoveCell(this.props.state.multiEndIndex, dir));
        } else {
          ({newIndex, newActualIndex} = this.cmdMoveCell(this.props.state.multiStartIndex, dir));
        }
      } else if (
        this.compareIndex(this.getRequiredMultiSelIndex(), this.props.state.multiActualIndex) === 0
      ) {
        // Sort of a hack. If multiSelIndex == multiActualIndex that means that shift has
        // not been used for anything yet, and we need to use multiEndIndex to
        // determine where the selection should extend.
        ({newIndex, newActualIndex} = this.moveCell(
          this.props.state.multiEndIndex,
          this.props.dimensions.firstIndex,
          this.props.dimensions.lastIndex,
          dir,
          false
        ));
      } else {
        ({newIndex, newActualIndex} = this.moveCell(
          this.props.state.multiActualIndex,
          this.props.dimensions.firstIndex,
          this.props.dimensions.lastIndex,
          dir,
          false
        ));
      }
      const newEnd = newIndex;
      if (
        TableUtil.isIndexBetween(this.props.state.multiStartIndex, newEnd, this.getRequiredMultiSelIndex())
      ) {
        this.props.setState({multiEndIndex: newEnd, multiActualIndex: newActualIndex});
      } else {
        ({newIndex, newActualIndex} = this.moveCell(
          this.props.state.multiStartIndex,
          this.props.dimensions.firstIndex,
          this.props.dimensions.lastIndex,
          dir,
          false
        ));
        const newStart = newIndex;
        if (
          TableUtil.isIndexBetween(newStart, this.props.state.multiEndIndex, this.getRequiredMultiSelIndex())
        ) {
          this.props.setState({
            multiEndIndex: newStart,
            multiStartIndex: this.props.state.multiEndIndex,
            multiActualIndex: newActualIndex,
          });
        }
      }
    } else if (cmdKeyPressed) {
      ({newIndex, newActualIndex} = this.cmdMoveCell(this.props.state.multiStartIndex, dir));
      this.multiSelectSingle(newIndex, {
        editing: false,
        collapse: true,
        multiActualIndex: newActualIndex,
      });
    } else {
      // If only one cell is currently selected, moveCell should use the actualIndex
      // in order to track stateful position of the cell.
      if (this.compareIndex(this.props.state.multiStartIndex, this.props.state.multiEndIndex) === 0) {
        ({newIndex, newActualIndex} = this.moveCell(
          this.props.state.multiActualIndex,
          this.props.dimensions.firstIndex,
          this.extendedLastIndex(),
          dir,
          false
        ));
        // However, if there is already a selected region and an arrow key (wihtout shift!) is
        // pressed, it should behave as though the selected region is only the currently
        // selected cell and act accordingly. This is why we set the selectionBounds to
        // to be just the multiSelIndex.
      } else {
        const selectionBounds = {
          start: this.getRequiredMultiSelIndex(),
          end: this.getRequiredMultiSelIndex(),
        };
        ({newIndex, newActualIndex} = this.moveCell(
          this.getRequiredMultiSelIndex(),
          this.props.dimensions.firstIndex,
          this.extendedLastIndex(),
          dir,
          false,
          selectionBounds
        ));
      }
      this.multiSelectSingle(newIndex, {
        editing: false,
        collapse: true,
        multiActualIndex: newActualIndex,
      });
    }
    // Scroll table to follow user selection
    const scrollToIndex = this._findUnmergedCell(newActualIndex);
    this._scrollCellIntoView(scrollToIndex);
  }

  selectRowColumnEnd(dimension: TableConstants.TableDimension, selectStart: boolean): void {
    if (!this.props.state.multiSelIndex) {
      return;
    }

    const [curI, curJ] = this.props.state.multiSelIndex;
    const [maxI, maxJ] = this._tableSize();

    let newIndex: TableConstants.TableIndex;
    if (selectStart) {
      newIndex = dimension === TableConstants.TABLE_ROW ? [curI, 0] : [0, curJ];
    } else {
      newIndex = dimension === TableConstants.TABLE_ROW ? [curI, maxJ] : [maxI, curJ];
    }

    const actualIndex = this._findUnmergedCell(newIndex);
    this.multiSelectSingle(newIndex, {
      editing: false,
      collapse: true,
      multiActualIndex: actualIndex,
    });
    this._scrollCellIntoView(actualIndex);
  }

  toggleCurrentRowColumn(dimension: TableConstants.TableDimension): void {
    const {multiStartIndex, multiEndIndex, multiSelIndex} = this.props.state;
    if (!multiSelIndex || !multiStartIndex || !multiEndIndex) {
      return;
    }

    const [startRow, startCol] = multiStartIndex;
    const [endRow, endCol] = multiEndIndex;
    const [curRow, curCol] = multiSelIndex;
    const [maxRow, maxCol] = this._tableSize();

    let newStartIndex: TableConstants.TableIndex;
    let newEndIndex: TableConstants.TableIndex;

    if (dimension === TableConstants.TABLE_ROW) {
      const isRowSelected: boolean = startCol === 0 && endCol === maxCol;

      if (isRowSelected) {
        // Select the current span of rows, with the col from multiSelIndex
        newStartIndex = [startRow, curCol];
        newEndIndex = [endRow, curCol];
      } else {
        // For the current span of rows, select the entire row (all columns)
        newStartIndex = [startRow, 0];
        newEndIndex = [endRow, maxCol];
      }
    } else {
      const isColSelected: boolean = startRow === 0 && endRow === maxRow;

      if (isColSelected) {
        // Select the current span of columns, with the row from multiSelIndex
        newStartIndex = [curRow, startCol];
        newEndIndex = [curRow, endCol];
      } else {
        // For the current span of columns, select the entire column (all rows)
        newStartIndex = [0, startCol];
        newEndIndex = [maxRow, endCol];
      }
    }

    this.setMultiIndices({start: newStartIndex, end: newEndIndex}, {multiSelIndex});
  }

  multiInputHandler = ({index, text}: {index?: TableConstants.TableIndex; text: string}): void => {
    assert(!this.props.locked, 'TableEditable.multiInputHandler unexpectedly called while locked');
    if (index == null) {
      this.improperFocusHook();
      return;
    }

    const [i, j] = Array.from(index);
    if (!this.props.selection.hasRealSelection) {
      if (text === '') {
        return;
      }
      this.expandToCell([i, j]);
    } else if (text === '') {
      // this only happens on cut
      this.deleteRangeHelper();
      return;
    }

    // Update state
    this.multiSelectSingle([i, j], {
      text,
      editing: true,
      collapse: false,
      selection: {
        cid: this.cellCid(i, j),
        start: text.length,
        end: text.length,
      },
    });
  };

  multiGetHTMLForCopy = ({showFormulas = false}: {showFormulas?: boolean} = {}): string => {
    if (!this.props.selection.hasRealSelection) {
      return '';
    }
    const $table = makeTableHTML(this.props.table, this.props.computedTable, this._multiSelectionBox(), {
      disableFormulas: this.props.disableFormulas,
      showFormulas,
    });
    return $table[0].outerHTML;
  };

  multiGetTextForCopy = ({showFormulas = false}: {showFormulas?: boolean} = {}): string => {
    if (!this.props.selection.hasRealSelection) {
      return '';
    }

    const mergedCellsByIndex: {[key: string]: boolean} = {};
    const {minI, maxI, minJ, maxJ} = this._multiSelectionBox();
    const rows = [];
    for (let i = minI; i <= maxI; i++) {
      const row = [];
      for (let j = minJ; j <= maxJ; j++) {
        const cell = this.props.table.table[i][j];
        const isHiddenMergedCell = mergedCellsByIndex[`${i},${j}`];
        if (!isHiddenMergedCell && isMergedCell(cell)) {
          // Mark all the cells in the merged range as merged
          for (let mergeRowIndex = 0; mergeRowIndex < (cell.mergeRowSpan || 1); mergeRowIndex++) {
            for (let mergeColIndex = 0; mergeColIndex < (cell.mergeColSpan || 1); mergeColIndex++) {
              mergedCellsByIndex[`${i + mergeRowIndex},${j + mergeColIndex}`] = true;
            }
          }
        }
        // Don't include a value for hidden merged cells
        if (isHiddenMergedCell) {
          row.push('');
        } else {
          row.push(
            showFormulas
              ? this.props.table.table[i][j].text
              : formatTypedValue(this.props.computedTable[i][j].value, this.props.table.table[i][j])
          );
        }
      }
      rows.push(row.join('\t'));
    }
    return rows.join('\n');
  };

  _areTypedValuesAllowed(): boolean {
    return this.props.table.columnConstraints.some((columnConstraint: TableConstants.ColumnConstraint) =>
      Boolean(columnConstraint && columnConstraint.columnDefinition)
    );
  }

  _canAttachFileToSelectedCell(): boolean {
    const index = this.props.state.multiSelIndex;
    return Boolean(index && this.props.canAttachFileToCell(index));
  }

  async _attachFileToCell(file: File): Promise<void> {
    if (this._canAttachFileToSelectedCell()) {
      // Capture insertion index before awaiting, in case the selection changes during file upload.
      const index = this.getRequiredMultiSelIndex();
      const {text, cellData} = await this.props.convertFileToCellData!(file);
      this.cellEnterHandler({text, cellData, index});
    }
  }

  /**
   * Given raw TSV content, coerce it into a 2D array of cells.
   */
  _parseRawDataToCells = (data: string): TableConstants.Cells => {
    data = normalizeLineEndings(data);

    // Ignore trailing new lines.
    while (data.length > 0 && data[data.length - 1] === '\n') {
      data = data.slice(0, data.length - 1);
    }

    let didAnyCellsGetParsed = false;
    const result = data.split('\n').map((line) =>
      line.split('\t').map((text) => {
        if (BenchlingConfig.ELN_ENABLE_LOCALE_AWARE_PARSING_FOR_COPY_PASTE) {
          const localeParsedText = parseTextWithLocale(text, this.props.table._tableType);
          if (text !== localeParsedText) {
            didAnyCellsGetParsed = true;
            text = localeParsedText;
          }
        }
        return TableUtil.makeCell(text);
      })
    );
    if (BenchlingConfig.ELN_ENABLE_LOCALE_AWARE_PARSING_FOR_COPY_PASTE && didAnyCellsGetParsed) {
      notifyTextHasBeenLocalized();
    }

    // Rectangularify the table
    const colCount = _.max(result, (tableRow) => tableRow.length).length;
    const fillerFor = (count: number): Array<Cell> =>
      __range__(0, colCount - count, false).map((j) => TableUtil.makeEmptyCell());

    return result.map((row) => row.concat(fillerFor(row.length)));
  };

  multiPasteHandler = (e: ElementClipboardEvent, options?: TablePasteOptions): void => {
    let table: Array<Array<TableConstants.PastedCell>>;
    assert(!this.props.locked, 'TableEditable.multiPasteHandler unexpectedly called while locked');

    // Paste files into cell
    if (this._canAttachFileToSelectedCell()) {
      const filePromise = wyciwyp.extractImageFromPaste(e);
      if (filePromise) {
        filePromise.then((file: File | null) => {
          if (file) {
            this._attachFileToCell(file);
          }
        });
        return;
      }
    }

    e.preventDefault();

    // Parse HTML from clipboard, use if it finds a single table.
    // Otherwise, build table from plaintext with newline and tab delimiters.
    const parts = wyciwyp.processClipboard(e.clipboardData, {
      allowTables: true,
      ignoreTableMinWidth: true,
      ignoreTableMinHeight: true,
      tableType: this.props.table._tableType,
    });
    if (parts.length === 1 && parts[0].type === 'table') {
      ({table} = (parts[0] as TableWYCIWYPPart).text);
    } else {
      table = this._parseRawDataToCells(e.clipboardData.getData('text/plain'));
    }
    this.saveMultiSelect();
    this._pasteTable(table, options);
  };

  hasLockedCellInSelection = (): boolean => {
    if (!this.props.selection.hasMultiSelection) {
      return false;
    }
    const {minI, maxI, minJ, maxJ} = this._multiSelectionBox();
    return this.props.table.table
      .slice(minI, maxI + 1)
      .some((row) => row.slice(minJ, maxJ + 1).some((cell) => cell.locked));
  };

  _pasteTable(
    table: TableConstants.Cells,
    options: TablePasteOptions = {type: ClipboardItemType.HTML}
  ): void {
    // Async paste (e.g. context menu) can resolve after the user clicks elsewhere,
    // so the current multi-selection may no longer match the cell that initiated the
    // paste. options.selectionIndex captures that originating cell. If it still falls
    // inside the active multi-selection, keep the full range so tile-fill works;
    // otherwise paste into just the originating cell.
    let selectionStart: TableConstants.TableIndex;
    let selectionEnd: TableConstants.TableIndex;

    const {selectionIndex} = options;
    const {multiStartIndex, multiEndIndex} = this.props.state;
    if (
      selectionIndex != null &&
      multiStartIndex != null &&
      multiEndIndex != null &&
      TableUtil.isIndexBetween(multiStartIndex, multiEndIndex, selectionIndex)
    ) {
      selectionStart = multiStartIndex;
      selectionEnd = multiEndIndex;
    } else if (selectionIndex != null) {
      selectionStart = selectionIndex;
      selectionEnd = selectionIndex;
    } else if (multiStartIndex != null && multiEndIndex != null) {
      selectionStart = multiStartIndex;
      selectionEnd = multiEndIndex;
    } else {
      checkSoftInvariant(false, {
        message: 'Unable to paste: no selection or originating cell index available',
        expirationDate: '2027-04-01',
        errorInDevAndTest: true,
      });
      return;
    }
    if (options.transpose) {
      table = transpose(table);
    }
    // 1. Tile the table so that it at least fills the selection
    const {minI, maxI, minJ, maxJ} = this._multiSelectionBox(selectionStart, selectionEnd);
    const selH = maxI - minI + 1;
    const selW = maxJ - minJ + 1;

    const maxColumns = this.props.table.disallowCustomColumns
      ? this.props.table.table[0].length
      : TABLE_MAX_COLUMNS;

    // But first, truncate the data to paste if it results in a table wider than MAX_COLUMN
    const extraColumns = table[0].length + minJ - maxColumns;
    if (extraColumns > 0) {
      table = (() => {
        const result3 = [];
        for (const row of Array.from(table)) {
          result3.push(row.slice(0, -extraColumns));
        }
        return result3;
      })();
    }

    function fillToDimension<T>(dim: number, array: Array<T>): Array<T> {
      // Output should have array at least once, even if it is more than dim.
      const out = array.slice();
      // Fill it as long as it is less than the dimension
      while (out.length + array.length <= dim) {
        out.push(...Array.from(array || []));
      }
      return out;
    }

    function expandMergedCellWithHiddenCells(
      cell: TableConstants.Cell,
      mergeRowSpan?: number,
      mergeColSpan?: number
    ): TableConstants.Cells {
      const result = [];
      for (let i = 0; i < (mergeRowSpan || 1); i++) {
        const row = [];
        for (let j = 0; j < (mergeColSpan || 1); j++) {
          if (i === 0 && j === 0) {
            row.push(cell);
          } else {
            row.push({text: ''});
          }
        }
        result.push(row);
      }
      return result;
    }

    const isSingleOrMergedCell =
      (table.length === 1 && table[0].length === 1) ||
      ((table[0][0].mergeRowSpan ?? 1) === table.length &&
        (table[0][0].mergeColSpan ?? 1) === table[0].length);

    const destinationComputedCell = this.props.computedTable[minI][minJ];
    const destinationCell = this.props.table.table[minI][minJ];
    const isDestinationSingleOrMergedCell =
      (selH === 1 && selW === 1) ||
      (destinationComputedCell.mergedBox &&
        destinationComputedCell.mergedBox.minI === minI &&
        destinationComputedCell.mergedBox.maxI === maxI &&
        destinationComputedCell.mergedBox.minJ === minJ &&
        destinationComputedCell.mergedBox.maxJ === maxJ);

    if (isSingleOrMergedCell && isDestinationSingleOrMergedCell) {
      const sourceCell = table[0][0];
      // When pasting a merged cell into a single cell, just paste the value, not the merged cell
      if (this.props.preserveMergedCellsInPaste && isMergedCell(sourceCell)) {
        const {mergeColSpan, mergeRowSpan, ...pastedCell} = sourceCell;
        table = [[pastedCell]];
      }
      // When pasting a single cell into a merged cell, paste the value and keep the original merge.
      if (destinationCell && isMergedCell(destinationCell)) {
        const pastedCell = {...sourceCell};
        if (destinationCell.mergeColSpan) {
          pastedCell.mergeColSpan = destinationCell.mergeColSpan;
        }
        if (destinationCell.mergeRowSpan) {
          pastedCell.mergeRowSpan = destinationCell.mergeRowSpan;
        }
        table = expandMergedCellWithHiddenCells(pastedCell, pastedCell.mergeRowSpan, pastedCell.mergeColSpan);
      }
    }

    // When pasting a single cell into a single cell, just paste the value, not the merged cell
    table = fillToDimension(
      selH,
      (() => {
        const result4 = [];
        for (const row of Array.from(table)) {
          result4.push(fillToDimension(selW, row));
        }
        return result4;
      })()
    );

    // Set the selection according to the expanded paste table so that if it fails the user can see the issue
    this.setMultiIndices({
      start: [minI, minJ],
      end: [Math.max(maxI, minI + table.length - 1), Math.max(maxJ, minJ + table[0].length - 1)],
    });

    const pasteRejection = computePasteRejectionReason(
      this.props.table,
      this.props.computedTable,
      table,
      minI,
      minJ,
      {
        supportsMerge: this.supportsMerge(),
        preserveMergedCellsInPaste: Boolean(this.props.preserveMergedCellsInPaste),
        allowMergeSelection: this.props.allowMergeSelection ?? undefined,
        isAnyCellLockedOrArchived: (cells) =>
          computeAnyCellsLockedOrArchived(cells, this.props.table.rowData),
      }
    );
    if (pasteRejection != null) {
      if (pasteRejection.severity === 'warn') {
        notifications.warn(pasteRejection.reason);
      } else {
        notifications.error(pasteRejection.reason);
      }
      return;
    }

    if (this._areTypedValuesAllowed()) {
      // Empty out text if typedValue is specified.
      table = table.map((tableRow) =>
        tableRow.map((tableCell) => (tableCell.typedValue ? {...tableCell, text: ''} : tableCell))
      );
    } else {
      // eslint-disable-next-line no-restricted-syntax
      table = table.map((tableRow) => tableRow.map((tableCell) => _.omit(tableCell, 'typedValue')));
    }

    table = table.map((tableRow) =>
      tableRow.map((tableCell) =>
        tableCell.typedValue
          ? {...tableCell, typedValue: extractSampleFromStageRunSample(tableCell.typedValue)}
          : tableCell
      )
    );

    // 2. Before completing paste, process selectors and reconcile with current selectors.
    resolveSelectorsFromPastedHTMLTables({
      pastedTables: [table],
      existingSelectors: this.props.selectorsLink.value,
      selectorsEnabled: this.props.selectorsEnabled,
    }).then((selectors: Array<Selector> | null) => {
      // 3. Paste in the tiled table at top-left cell of the selection
      const pasteOpResult = excess.ops.paste(
        this.props.table,
        this.props.computedTable,
        table,
        minI,
        minJ,
        this.props.preserveMergedCellsInPaste
      );
      const {box, tableOp} = pasteOpResult;
      let {table: newTable} = pasteOpResult;

      const tableMetadata = this.props.table.metadata
        ? (this.props.table.metadata as TableConstants.StructuredTableToTableMetadata<unknown>)
        : undefined;

      // If tableMetadata is null, do not check for capacity here - it may be a plain table. Structured tables
      // will still catch the capacity issue before making changes, via a check in StructuredTablePlugin.setContent.
      const capacityErrorMsg =
        tableMetadata != null
          ? getTableLengthErrorMessage({
              currentTableLength: this.props.table.table.length,
              newTableLength: newTable.table.length,
              structuredTableKind: tableMetadata.kind,
              isTableEditable: true,
              allowAddRows: this.props.dimensions.canAddRows,
              allowRemoveRows: this.props.dimensions.canRemoveRows,
            })
          : null;
      if (capacityErrorMsg) {
        notifications.error(capacityErrorMsg);
        return;
      }

      // TODO(T7414): Refactor into `excess/ops/TableOps`
      if (TableUtil.isInvalidOpWithWarn(this.props.table, newTable)) {
        return;
      }

      // If any single inputs are present and the table is expanding, merge the single input columns accordingly.
      if (this.props.singleInputs.boundaryIndex && newTable.table.length > this.props.table.table.length) {
        // TODO(BNCH-136509): Possibly update tableOp to include single input merged cells?
        newTable = TableUtil.remergeSingleInputColumns(newTable);
      }

      // 4. Update state (table and selectors) / selection
      const replacedCells = this.props.table.table
        .slice(box.minI, box.maxI + 1)
        .flatMap((row) => row.slice(box.minJ, box.maxJ + 1));
      this.setMultiIndices({
        start: [box.minI, box.minJ],
        end: [box.maxI, box.maxJ],
        text: newTable.table[box.minI][box.minJ].text,
      });
      this.updateHandler(this.props.index, {table: newTable}, selectors, {[newTable.name]: tableOp});

      // 5. Track container validity analytics
      trackContainerValidityForTableEvents(this.props.fileId, this.props.table.apiId!, [
        {
          event: ELN_ANALYTICS_ACTIONS.ADDED_CONTAINERS_TO_STRUCTURED_TABLE,
          cells: table.flat(),
        },
        {event: ELN_ANALYTICS_ACTIONS.REMOVED_CONTAINERS_FROM_STRUCTURED_TABLE, cells: replacedCells},
      ]);
    });
  }

  performFillDown(): void {
    if (this.isFillDownBoxLockedWithEditWarn()) {
      return;
    }
    this.saveMultiSelect();
    const {table, box, tableOp} = excess.ops.fillDown(
      this.props.table,
      this.props.computedTable,
      this._multiSelectionBox(),
      this.props.fillDown.box,
      this.props.disableFormulas
    );
    this.updateHandler(this.props.index, {table}, this.props.selectorsLink.value, {
      [table.name]: tableOp,
    });
    this.setMultiIndices({
      start: [box.minI, box.minJ],
      end: [box.maxI, box.maxJ],
      text: table.table[box.minI][box.minJ].text,
    });
  }

  performDoubleClickFillDown(): void {
    // double click fill down does nothing if the selected cells contain any merged cells
    if (computeContainsMergedRegion(this.props.computedTable, this._multiSelectionBox())) {
      return;
    }
    const {table, box, tableOp} = excess.ops.fillDown(
      this.props.table,
      this.props.computedTable,
      this._multiSelectionBox(),
      this.getDoubleClickFillDownBox(),
      this.props.disableFormulas
    );

    this.props.updateHandler(this.props.index, {table}, this.props.selectorsLink.value, {
      [table.name]: tableOp,
    });
    this.setMultiIndices({
      start: [box.minI, box.minJ],
      end: [box.maxI, box.maxJ],
      text: table.table[box.minI][box.minJ].text,
    });
  }

  /** Get the current selection expanded down through any following blank cells. */
  getDoubleClickFillDownBox(): TableConstants.MultiSelectionBox {
    return {
      ...this._multiSelectionBox(),
      maxI: this._checkMaxRowOnFillDown(),
    };
  }

  /** Returns the index of the last consecutive blank row in the table following the selection. */
  _checkMaxRowOnFillDown(): number {
    const selectionBox = this._multiSelectionBox();
    const selectionStartRowIndex = selectionBox.maxI + 1;
    const selectionStartColIndex = selectionBox.minJ;
    const selectionEndColIndex = selectionBox.maxJ;
    const table = this.props.table.table;
    let fillDownEndRowIndex = table.length - 1;
    for (let rowIndex = selectionStartRowIndex; rowIndex < table.length; rowIndex++) {
      for (let colIndex = selectionStartColIndex; colIndex <= selectionEndColIndex; colIndex++) {
        const cell = table[rowIndex][colIndex];
        const computedCell = this.props.computedTable[rowIndex][colIndex];
        // do not fill down into any cells that are filled or part of a merged region
        if (cell.text || (cell.typedValue && !isTypedValueEmpty(cell.typedValue)) || computedCell.mergedBox) {
          fillDownEndRowIndex = Math.min(fillDownEndRowIndex, rowIndex - 1);
        }
      }
    }
    return fillDownEndRowIndex;
  }

  _indentationPx(): number {
    return this.props.views.isMaximizedOrSplitScreen ? 0 : this.props.indentation * INDENT_SIZE;
  }

  _paddingPx(): number {
    return this.props.views.isMaximizedOrSplitScreen || !this.props.documentName ? 0 : PADDING_RIGHT_SIZE;
  }

  /**
   * If the SpreadsheetStore has a pending edit for this table (e.g. from rapid input where the
   * cell editor hasn't mounted yet), commit it to the table and return true.
   * This handles the race condition where a device (e.g. a balance/barcode scanner) sends keystrokes
   * followed by Enter/Tab faster than the cell editor can mount and receive focus.
   */
  _commitPendingEditIfNeeded(): boolean {
    const storeState = SpreadsheetStore.getState(
      this.props.tableNamespace,
      this.props.table.name,
      this.props.fileId
    );
    if (storeState.editingTable !== this.props.table.name || storeState.index == null) {
      return false;
    }
    const [i, j] = storeState.index;
    const formula = storeState.formula;
    const typedValue = storeState.typedValue;

    this.saveMultiSelect();
    const cellData: Partial<TableConstants.Cell> = typedValue ? {typedValue} : {};
    this.updateCell([i, j], formula, cellData);
    return true;
  }

  // # Handlers for updating active cell (shared between TableCells and FormulaBox)

  cellEscapeHandler = (): void => {
    assert(this.props.selection.hasRealSelection, 'Expected to have a real selection');
    this.multiCancelEdit(this.getRequiredMultiSelIndex());
  };

  cellEnterHandler = (e: {
    text: string;
    cellData: Partial<TableConstants.Cell>;
    enterKey?: boolean;
    shiftKey?: boolean;
    index?: TableConstants.TableIndex;
  }): void => {
    const [i, j] = e.index ? e.index : this.getRequiredMultiSelIndex();

    // Save selection to history before making change.
    this.saveMultiSelect();

    // Determine new text and update table
    let expandTable = false;
    if (
      getConfig('ELN_ENABLE_TABLE_ADD_ROW_ON_ENTER') &&
      e.enterKey &&
      i === this.props.dimensions.lastIndex[0] &&
      this.props.dimensions.canModifyRowDim &&
      (this.props.table.maxRows === undefined || this.props.table.table.length < this.props.table.maxRows)
    ) {
      expandTable = true;
    }
    this.updateCell([i, j], e.text, e.cellData, expandTable);
    this.moveFromCell({
      index: [i, j],
      text: e.text,
      dir: !e.enterKey
        ? 'none' // if we did not use the enter key to finalize, remain in the same cell
        : e.shiftKey
          ? 'up' // `shift + enter` exits the cell and selects the cell above the current cell.
          : 'down',
      expanded: expandTable,
    });
    this.scrollCellIntoViewAfterEdit();
  };

  // # Table handlers for add/del row/col/table

  setIsInsertingRows = (e: BasicEvent): void => {
    // prevent multiEditableMouseDownHandler from doing note part selection
    e.preventDefault();
    this.props.setState({isInsertingRows: true});
  };

  handleAddBlankRows = (numRows: number): void => {
    const {table} = this.props;
    const maxAddableRows = this._getMaxAddableRows();
    if (numRows > maxAddableRows) {
      if (maxAddableRows === 0) {
        notifications.warn(`Cannot add any more rows to this table.`);
        // Cannot add any more rows.
        return;
      }
      // Can add more rows before reaching maxRows, but less than the given numRows.
      numRows = maxAddableRows;
      notifications.warn(`Cannot add more than ${numRows} rows to this table.`);
    }

    this.props.setState({isInsertingRows: false});
    // Save selection (last row) to history before making change.
    const {start, end} = this._rowRange(table.table.length - 1);
    this.saveMultiSelect({
      multiStartIndex: start,
      multiEndIndex: end,
      multiSelIndex: start,
    });

    const newTable = produce(table, (draftTable) => {
      draftTable.table.push(...TableUtil.makeBlankRows(draftTable, numRows));
      // If any single inputs are present and the table is expanding, merge the single input columns accordingly.
      if (this.props.singleInputs.boundaryIndex && numRows > 0) {
        draftTable.table = TableUtil.remergeSingleInputColumns(draftTable).table;
      }
    });
    const i = newTable.table.length - 1;

    const tableOp: TableOpDiffMap = {
      [newTable.name]: {
        type: 'insert-rows',
        rowIndexes: Array.from({length: numRows}, (_, i) => i + table.table.length),
      },
    };

    // We need to update propagate new rows to the editor root BEFORE setting state.
    // Otherwise, if we instead had set state first (with the new selection), when the new table
    // renders, the selection will be on rows that haven't been created yet and we'll error.
    this.updateHandler(this.props.index, {table: newTable}, undefined, tableOp);

    // Set new selection
    this.setMultiIndices({
      ...this._rowRange(i),
      text: newTable.table[i][0].text,
    });

    if (numRows > 0) {
      this.props.setState({scrollToIndexAfterUpdate: newTable.table.length - 1});
    }
  };

  expandToCell(index: TableConstants.TableIndex): TableConstants.Cell {
    assert(
      !TableUtil.hasLockedDims(this.props.table),
      `Prevented unexpected table expansion with locked dimensions`
    );

    this.saveMultiSelect({multiStartIndex: index, multiEndIndex: index, multiSelIndex: index});
    const [i, j] = Array.from(index);

    // Add new cols
    const newColCount = Math.max(0, j + 1 - this.props.table.widths.length);
    const newCols = Array(newColCount).fill(TableUtil.makeEmptyCell());
    let newTable = produce(this.props.table.table, (draftRows) => {
      for (const draftRow of draftRows) {
        draftRow.push(...newCols);
      }
    });
    // Add new rows
    const newRowsCount = Math.max(0, i + 1 - this.props.table.table.length);
    const lastRow: Array<TableConstants.Cell> = newTable[newTable.length - 1];
    const newRow: Array<TableConstants.Cell> = lastRow.map((cell: TableConstants.Cell) =>
      TableUtil.makeEmptyCell(cell.precision != null ? {precision: cell.precision} : {})
    );
    newTable = produce(newTable, (draftTable) => {
      for (let k = 0; k < newRowsCount; k++) {
        draftTable.push(newRow);
      }
    });
    const newFullTable = produce(this.props.table, (draftTable) => {
      draftTable.table = newTable;
      for (const [key, value] of Object.entries(TableConstants.COLUMN_FIELD_DEFAULTS)) {
        (draftTable[key as keyof TableConstants.TableData] as Array<unknown>).push(
          ...Array(newColCount).fill(value)
        );
      }
    });

    const tableOp: TableConstants.UpsertCellsTableOp = {type: 'upsert-cells', cells: []};
    if (newColCount > 0) {
      tableOp.newColIndexes = range(this.props.table.table[0].length, newFullTable.table[0].length - 1);
    }
    if (newRowsCount > 0) {
      tableOp.newRowIndexes = range(this.props.table.table.length, newFullTable.table.length - 1);
    }

    this.updateHandler(this.props.index, {table: newFullTable}, undefined, {
      [this.props.table.name]: tableOp,
    });
    return newTable[i][j];
  }

  /**
   * @returns [number of rows in table, number of columns in table]
   *
   * Note: This does not take into account the sliceStart and sliceEnd for columns!
   */
  _tableSize(): TableConstants.TableIndex {
    return [this.props.table.table.length - 1, this.props.table.table[0].length - 1];
  }

  _rowRange(i1: number, i2?: number): {start: TableConstants.TableIndex; end: TableConstants.TableIndex} {
    if (i2 == null) {
      i2 = i1;
    }
    const iMin = Math.min(i1, i2);
    const iMax = Math.max(i1, i2);
    const {sliceStart, sliceEnd} = this.getEffectiveTableSlices();
    return {
      start: [iMin, sliceStart],
      end: [iMax, sliceEnd],
    };
  }

  _colRange(j1: number, j2: number): {start: TableConstants.TableIndex; end: TableConstants.TableIndex} {
    if (j2 == null) {
      j2 = j1;
    }
    const jMin = Math.min(j1, j2);
    const jMax = Math.max(j1, j2);
    return {
      start: [0, jMin],
      end: [this._tableSize()[0], jMax],
    };
  }

  deleteRows = (start: number | null, end: number | null): void => {
    if (end == null) {
      end = start;
    }
    // `end == null` is included for typescript. At this point, end cannot be null if start != null
    if (start == null || end == null) {
      return;
    }
    // Save selection (rows to be deleted) to history before making change.
    const {start: startIndex, end: endIndex} = this._rowRange(start, end);
    this.saveMultiSelect({
      multiStartIndex: startIndex,
      multiEndIndex: endIndex,
      multiSelIndex: startIndex,
    });
    this.props.tableMutations.deleteRows(this.props.table.name, start, end);
  };

  deleteColumns = (start: number | null, end: number | null): void => {
    if (!this.props.canAddRemoveColumns) {
      return;
    }
    if (end == null) {
      end = start;
    }
    if (!(start != null && end != null)) {
      return;
    }
    // Save selection (columns to be deleted) to history before making change.
    const {start: startIndex, end: endIndex} = this._colRange(start, end);
    this.saveMultiSelect({
      multiStartIndex: startIndex,
      multiEndIndex: endIndex,
      multiSelIndex: startIndex,
    });
    this.props.tableMutations.deleteColumns(this.props.table.name, start, end);
  };

  insertRowsAbove = (start: number | null, end: number | null): void => {
    if (end == null) {
      end = start;
    }
    if (!(start != null && end != null)) {
      return;
    }
    // Save selection to history before making change.
    const {start: startIndex, end: endIndex} = this._rowRange(start, end);
    this.saveMultiSelect({
      multiStartIndex: startIndex,
      multiEndIndex: endIndex,
      multiSelIndex: startIndex,
    });
    this.props.tableMutations.insertRowsAbove(this.props.table.name, start, end);
  };

  insertRowsBelow = (start: number | null, end: number | null): void => {
    if (end == null) {
      end = start;
    }
    if (!(start != null && end != null)) {
      return;
    }
    // Save selection to history before making change.
    const {start: startIndex, end: endIndex} = this._rowRange(start, end);
    this.saveMultiSelect({
      multiStartIndex: startIndex,
      multiEndIndex: endIndex,
      multiSelIndex: startIndex,
    });
    this.props.tableMutations.insertRowsBelow(this.props.table.name, start, end);
  };

  insertColumnsLeft = (
    start: number | null,
    end: number | null,
    columnInfo: TableConstants.InsertableColumnInfo | {} = {}
  ): void => {
    if (!this.props.canAddRemoveColumns) {
      return;
    }
    if (end == null) {
      end = start;
    }
    if (!(start != null && end != null)) {
      return;
    }
    // Save selection to history before making change.
    const {start: startIndex, end: endIndex} = this._colRange(start, end);
    this.saveMultiSelect({
      multiStartIndex: startIndex,
      multiEndIndex: endIndex,
      multiSelIndex: startIndex,
    });
    this.props.tableMutations.insertColumnsLeft(
      this.props.table.name,
      start,
      end,
      columnInfo,
      this.props.getInitialCellValues?.(columnInfo)
    );
  };

  insertColumnsRight = (
    start: number | null,
    end: number | null,
    columnInfo: TableConstants.InsertableColumnInfo | {} = {}
  ): void => {
    if (!this.props.canAddRemoveColumns) {
      return;
    }
    if (end == null) {
      end = start;
    }
    if (!(start != null && end != null)) {
      return;
    }
    // Save selection to history before making change.
    const {start: startIndex, end: endIndex} = this._colRange(start, end);
    this.saveMultiSelect({
      multiStartIndex: startIndex,
      multiEndIndex: endIndex,
      multiSelIndex: startIndex,
    });
    this.props.tableMutations.insertColumnsRight(
      this.props.table.name,
      start,
      end,
      columnInfo,
      this.props.getInitialCellValues?.(columnInfo)
    );
  };

  renameColumn = (i: number, name: string): void => {
    this.saveMultiSelect();
    this.updateHandler(
      this.props.index,
      {
        table:
          // withColumnNameSet is passed in as a prop in StructuredTableView if the table is a structured table.
          // For structured table columns, we update the columnConstraint instead of the column label. This is because
          // when converting plain tables to structured tables, the structured table adapter uses the columnConstraint
          // instead of the columnLabel.
          this.props.withColumnNameSet?.(this.props.table, i, name) ??
          TableUtil.withColumnNameSet(this.props.table, i, name),
      },
      undefined,
      {[this.props.table.name]: {type: 'noop'}}
    );
  };

  _canDeleteTable(): boolean {
    return this.props.deleteHandler != null;
  }

  deleteTable = (): void => {
    this.props.deleteHandler!();
  };

  toggleHeaderColHandler = (): void => {
    const newStepTable = produce(this.props.table, (draftTable) => {
      draftTable.headerCol = !draftTable.headerCol;
      // Reset bold of first column when toggling header column
      draftTable.table.forEach((draftRow) => {
        draftRow.forEach((draftCell, j) => {
          if (j === 0) {
            delete draftCell.bold;
          }
        });
      });
    });
    this.updateHandler(this.props.index, {table: newStepTable}, undefined, {
      [this.props.table.name]: {type: 'noop'},
    });
  };

  toggleHeaderRow = (): void => {
    const newStepTable = produce(this.props.table, (draftTable) => {
      draftTable.headerRow = !draftTable.headerRow;
      // Reset bold of first row when toggling header row
      draftTable.table[0].forEach((cell) => {
        delete cell.bold;
      });
    });
    this.updateHandler(this.props.index, {table: newStepTable}, undefined, {
      [this.props.table.name]: {type: 'noop'},
    });
  };

  _setFormula(formula: string, cursorPosition: number): void {
    this.setMultiEditing({
      editing: true,
      index: this.getRequiredMultiSelIndex(),
      text: formula,
      selection: {
        cid: `${this.props.cid}-formula-box`,
        start: cursorPosition,
        end: cursorPosition,
      },
    });
  }

  handleFunctionClicked = async ({name, asyncOnClick}: TableConstants.TableFunction): Promise<void> => {
    if (asyncOnClick == null) {
      // Set the position such that the cursor is between the brackets
      const pos = 1 + name.length + 1;
      this._setFormula(`=${name}()`, pos);
    } else {
      const {minI, maxI, minJ, maxJ} = this._multiSelectionBox();
      const rowData =
        this.props.table.rowData != null && minI === maxI && minJ === maxJ
          ? this.props.table.rowData[minI]
          : null;
      const evaluatedRow = minI === maxI && minJ === maxJ ? this.props.computedTable[minI] : null;
      const result = await asyncOnClick({
        rowData,
        evaluatedRow,
        onUpdateSelectedCell: (cellData: Partial<TableConstants.Cell>) => {
          this.props.tableMutations.updateCell([minI, minJ], cellData);
        },
      });
      if (result) {
        this._setFormula(result.formula, result.cursorPosition);
      }
    }
  };

  mouseMoveHandler = (e: BasicEvent): void => {
    this._showHoverCell();
  };

  // # Internal helpers

  hideHoverCell = (): void => {
    if (!this.props.state.hideHoverCell) {
      this.props.setState({hideHoverCell: true});
    }
  };

  _showHoverCell(): void {
    if (this.props.state.hideHoverCell) {
      this.props.setState({hideHoverCell: false});
    }
  }

  _hasActiveCell(): boolean {
    return this.props.multiEditing;
  }

  cellCid = (i: number, j: number): string => `table-${this.props.cid}-${i}-${j}`;

  cellIndexFromCid(cid: string): TableConstants.TableIndex | null {
    const match = new RegExp(`table-${this.props.cid}-(\\d+)-(\\d+)`).exec(cid);
    if (match == null) {
      return null;
    } else {
      return [parseInt(match[1], 10), parseInt(match[2], 10)];
    }
  }

  // Returns the bounding box around the selection specified by [start, end]. The bounding box is such that
  // any merged regions are completely contained within the bounding box. The selection can also be outside
  // of the table (ghost cells)
  //
  // By default, the region is set as the current selection, but can also be specified explicitly.
  _multiSelectionBox(
    start?: TableConstants.TableIndex | null,
    end?: TableConstants.TableIndex | null
  ): TableConstants.MultiSelectionBox {
    if (start !== undefined && end !== undefined) {
      const expandedBox = computeExpandedSelectionBox(
        start ?? null,
        end ?? null,
        this.props.table,
        this.props.computedTable
      );
      assert(expandedBox != null, 'Expected to have a selection');
      return expandedBox;
    }

    assert(this.props.selection.expandedBox != null, 'Expected to have a selection');
    return this.props.selection.expandedBox;
  }

  // Helper method that calculates the closest index to the given index (`actualIndex`)
  // in the given direction taking into account merged regions.
  //
  // The general approach is as follows:
  //   1. Find all the merged boxes in the selected region.
  //   2. Helper method that finds the min row, max row, min column, and max column for merged regions
  //      that 'overlap' with the row and column specified by the given index (i, j).
  //      This will determine how far we need to move to 'jump over' all the merged cells
  //      that conflict with `actualIndex`.
  //   3. Move in the specified direction given 2. For example, if dir == 'right', we want
  //      to move one past the maximum column that conflicts with `actualIndex` to find the new index.
  _findClosestIndexInDirection(
    actualIndex: TableConstants.TableIndex,
    dir: 'up' | 'down' | 'left' | 'right',
    start: TableConstants.TableIndex,
    end: TableConstants.TableIndex,
    selectionBounds?: {start: TableConstants.TableIndex; end: TableConstants.TableIndex}
  ): TableConstants.TableIndex {
    let [i, j] = Array.from(actualIndex);
    const [i1, j1] = Array.from(start);
    const [i2, j2] = Array.from(end);

    const selectionBox =
      selectionBounds != null
        ? this._multiSelectionBox(selectionBounds.start, selectionBounds.end)
        : this._multiSelectionBox();
    const {overMinI, overMaxI, overMinJ, overMaxJ} = computeAreMergedBoxesOverlapping(
      this.props.computedTable,
      selectionBox,
      i,
      j
    );
    const [minI, maxI, minJ, maxJ] = Array.from([overMinI, overMaxI, overMinJ, overMaxJ]);

    // eslint-disable-next-line default-case
    switch (dir) {
      case 'up':
        [i, j] = Array.from([minI - 1, j]);
        if (i < i1) {
          i = minI;
        }
        break;
      case 'down':
        [i, j] = Array.from([maxI + 1, j]);
        if (i > i2) {
          i = maxI;
        }
        break;
      case 'left':
        [i, j] = Array.from([i, minJ - 1]);
        if (j < j1) {
          j = minJ;
        }
        break;
      case 'right':
        [i, j] = Array.from([i, maxJ + 1]);
        if (j > j2) {
          j = maxJ;
        }
        break;
    }
    return [i, j];
  }

  // Finds the closest index in the specified direction when tabbed (wrap == true)
  _findClosestIndexInDirectionTabbed(
    actualIndex: TableConstants.TableIndex,
    dir: 'up' | 'down' | 'left' | 'right',
    start: TableConstants.TableIndex,
    end: TableConstants.TableIndex,
    selectionBounds?: {start: TableConstants.TableIndex; end: TableConstants.TableIndex}
  ): TableConstants.TableIndex {
    let [i, j] = Array.from(actualIndex);
    let [i1, j1] = Array.from(start);
    let [i2, j2] = Array.from(end);
    const {minI, maxI, minJ, maxJ} = this._multiSelectionBox(start, end);
    [i1, j1] = Array.from([minI, minJ]);
    [i2, j2] = Array.from([maxI, maxJ]);

    // eslint-disable-next-line default-case
    switch (dir) {
      case 'left':
        [i, j] = Array.from([i, j - 1]);
        // keeps going until finds a rendered cell in this direction
        while (!TableUtil.isRenderedInRegion(i, j, this.props.computedTable[i][j], [i1, j1], [i2, j2])) {
          // if column is valid, keeps trying to go left
          if (j > j1) {
            [i, j] = Array.from([i, j - 1]);
          } else {
            // if row is valid, wrap upwards to the previous row
            // eslint-disable-next-line no-lonely-if
            if (i > i1) {
              [i, j] = Array.from([i - 1, j2]);
            } else {
              // if neither is valid, wrap all the way around to the bottom right corner
              [i, j] = Array.from([i2, j2]);
              break;
            }
          }
        }
        break;
      case 'right':
        [i, j] = Array.from([i, j + 1]);
        while (!TableUtil.isRenderedInRegion(i, j, this.props.computedTable[i][j], [i1, j1], [i2, j2])) {
          if (j < j2) {
            [i, j] = Array.from([i, j + 1]);
          } else if (i < i2) {
            [i, j] = Array.from([i + 1, j1]);
          } else {
            [i, j] = Array.from([i1, j1]);
            break;
          }
        }
        break;
    }
    return [i, j];
  }

  // Helper method for finding the rendered merged cell that `index` belongs to.
  // If `index` does not correspond to a merged cell, then just returns itself.
  _findUnmergedCell(index: TableConstants.TableIndex): TableConstants.TableIndex {
    if (!this.isRealIndex(index)) {
      // If the index is out of bounds, just return itself
      // This can occur when navgating into ghost cells
      return index;
    }
    const [row, column] = index;
    const evaluatedCell = this.props.computedTable[row][column];
    if (evaluatedCell != null && evaluatedCell.mergedBox != null) {
      return [evaluatedCell.mergedBox.minI, evaluatedCell.mergedBox.minJ];
    }
    return index;
  }

  // Moves a cell at `index` in the direction `dir` with wrap `wrap` taking
  // into account merged cells.
  // Note: this method returns an actual index, which does not necessarily
  // correspond to a valid (rendered) cell on the board.
  _moveCellActualIndex(
    index: TableConstants.TableIndex,
    start: TableConstants.TableIndex,
    end: TableConstants.TableIndex,
    dir: 'up' | 'down' | 'left' | 'right' | 'none',
    wrap: boolean | undefined,
    selectionBounds?: {start: TableConstants.TableIndex; end: TableConstants.TableIndex}
  ): TableConstants.TableIndex {
    if (dir === 'none') {
      return index;
    }
    let [rowIndex, columnIndex] = index;
    let [startRowIndex, startColumnIndex] = start;
    let [endRowIndex, endColumnIndex] = end;
    if (startRowIndex > endRowIndex) {
      [startRowIndex, endRowIndex] = [endRowIndex, startRowIndex];
    }
    if (startColumnIndex > endColumnIndex) {
      [startColumnIndex, endColumnIndex] = [endColumnIndex, startColumnIndex];
    }
    if (wrap) {
      [rowIndex, columnIndex] = this._findClosestIndexInDirectionTabbed(
        index,
        dir,
        [startRowIndex, startColumnIndex],
        [endRowIndex, endColumnIndex]
      );
    } else {
      [rowIndex, columnIndex] = this._findClosestIndexInDirection(
        index,
        dir,
        [startRowIndex, startColumnIndex],
        [endRowIndex, endColumnIndex],
        selectionBounds
      );
    }
    return [rowIndex, columnIndex];
  }

  // Moves a cell at `index` in the direction `dir` with wrap `wrap` taking
  // into account merged cells.
  // @param index              the index of cell that is being moved
  // @param start, end         the bounds of the box that we can move in
  // @param dir                the direction we're moving in
  // @param wrap               whether we want to wrap around when navigating
  //                           (used for tabbing)
  // @param selectionBoundes   the bounds we want to consider currently being
  //                           'selected'. This generally defaults to the multi
  // selection box, but occasionally we wish to use other bounds.
  // (See case where you press one of the arrow keys when there is currently
  // already a selected region.)
  moveCell(
    index: TableConstants.TableIndex,
    start: TableConstants.TableIndex,
    end: TableConstants.TableIndex,
    dir: 'up' | 'down' | 'left' | 'right' | 'none',
    wrap: boolean | undefined,
    selectionBounds?: {start: TableConstants.TableIndex; end: TableConstants.TableIndex}
  ): {newIndex: TableConstants.TableIndex; newActualIndex: TableConstants.TableIndex} {
    const newActualIndex = this._moveCellActualIndex(index, start, end, dir, wrap, selectionBounds);
    const newIndex = this._findUnmergedCell(newActualIndex);
    return {newIndex, newActualIndex};
  }

  /**
   * From the given cell, this will return an array of all the indexes of cells in the given direction.
   */
  getCellsInDirectionFromCell(
    index: TableConstants.TableIndex,
    dir: 'up' | 'down' | 'left' | 'right'
  ): Array<TableConstants.TableIndex> {
    if (dir === 'up') {
      return range(0, index[0] - 1)
        .reverse()
        .map((i: number) => [i, index[1]]);
    } else if (dir === 'down') {
      return range(index[0] + 1, this.props.computedTable.length - 1).map((i: number) => [i, index[1]]);
    } else if (dir === 'left') {
      const leftIndexExcludingSingleInputs = this.props.singleInputs.boundaryIndex ?? 0;
      return range(leftIndexExcludingSingleInputs, index[1] - 1)
        .reverse()
        .map((j: number) => [index[0], j]);
    } else if (dir === 'right') {
      return range(index[1] + 1, this.props.computedTable[index[0]].length - 1).map((j: number) => [
        index[0],
        j,
      ]);
    }
    return assertNever(dir);
  }

  isIndexEmpty(cellIndex: TableConstants.TableIndex): boolean {
    const cell = this.props.computedTable[cellIndex[0]][cellIndex[1]];
    return isTypedValueEmpty(cell.value);
  }

  /**
   * Given an array of cell indexes, this will return the first cell that is not empty.
   */
  getNextPopulatedCell(cellIndexes: Array<TableConstants.TableIndex>): TableConstants.TableIndex | undefined {
    return cellIndexes.find((cellIndex) => !this.isIndexEmpty(cellIndex));
  }

  /**
   * Given an array of cell indexes, this will return the populated cell right before the next empty cell.
   * If there is no empty cell, it will return undefined.
   */
  getLastPopulatedCellBeforeEmptyCell(
    cellIndexes: Array<TableConstants.TableIndex>
  ): TableConstants.TableIndex | undefined {
    const nextEmptyIndex = cellIndexes.findIndex((cellIndex) => this.isIndexEmpty(cellIndex));
    if (nextEmptyIndex !== -1) {
      return cellIndexes[nextEmptyIndex - 1];
    }
    return undefined;
  }

  cmdMoveCell(
    index: TableConstants.TableIndex,
    dir: 'up' | 'down' | 'left' | 'right'
  ): {newIndex: TableConstants.TableIndex; newActualIndex: TableConstants.TableIndex} {
    // If the index is out of bounds, just return itself
    // This can occur when navigating ghost cells
    if (!this.isRealIndex(index)) {
      return {newIndex: index, newActualIndex: this._findUnmergedCell(index)};
    }

    let cellsInDirection = this.getCellsInDirectionFromCell(index, dir);

    // Filter out any cells that are ghost merged cells
    cellsInDirection = cellsInDirection.filter((cellIndex, i) => {
      const cell = this.props.computedTable[cellIndex[0]]?.[cellIndex[1]];
      return (
        cell &&
        // Keep cells that aren't merged
        (!cell.mergedBox ||
          // Always leave the last cell, even if it's a ghost merged cell
          i === cellsInDirection.length - 1 ||
          // Keep the top left cell of any merge
          (cell.mergedBox.minI === cellIndex[0] && cell.mergedBox.minJ === cellIndex[1]))
      );
    });

    if (cellsInDirection.length === 0) {
      return {newIndex: index, newActualIndex: this._findUnmergedCell(index)};
    }
    const currentComputedCell = this.props.computedTable[index[0]][index[1]];
    const cellIsEmpty = isTypedValueEmpty(currentComputedCell.value);
    const nextCellIsEmpty = this.isIndexEmpty(cellsInDirection[0]);

    let newIndex: TableConstants.TableIndex | undefined;
    if (cellIsEmpty || nextCellIsEmpty) {
      //  If you start in an empty cell, move to the first non-empty cell
      //  o - > o
      //  _ _ _ X X X _ _ _
      //
      //  or
      //
      //  If you start in a non-empty cell, and the cell is immediately after you is empty,
      //  then move to the first non-empty cell
      //  o - > o
      //  X _ _ X X X _ _ _
      newIndex = this.getNextPopulatedCell(cellsInDirection);
    } else {
      //  If you start in a non-empty cell, and the cell is immediately after you is not empty,
      //  then move to the last non-empty cell before the first empty cell
      //  o - > o
      //  X X X X _ X _ X _
      newIndex = this.getLastPopulatedCellBeforeEmptyCell(cellsInDirection);
    }
    // If we didn't find a new index, then we want to move to the last cell in the direction
    if (!newIndex) {
      newIndex = cellsInDirection[cellsInDirection.length - 1];
    }

    return {newIndex, newActualIndex: this._findUnmergedCell(newIndex)};
  }

  updateCell = (
    index: TableConstants.TableIndex,
    text: string,
    cellData?: Partial<TableConstants.Cell>,
    expand?: boolean
  ): void => {
    Perf.markStart(this.updateCellPerfId);
    // eslint-disable-next-line no-restricted-syntax
    const newCellData = _.extend({text}, cellData ?? {});
    this.props.tableMutations.updateCell(index, newCellData, expand);
    Perf.markEnd(this.updateCellPerfId);
  };
  moveFromCell = ({
    index,
    dir,
    text,
    wrap,
    expanded,
  }: {
    index: TableConstants.TableIndex;
    dir: 'up' | 'down' | 'left' | 'right' | 'none';
    text?: string | null;
    wrap?: boolean;
    expanded?: boolean;
  }): void => {
    let newActualIndex;
    let newIndex;
    let collapse;
    if (text == null) {
      text = null;
    }
    let [start, end] = this.props.selection.hasRealSelection
      ? [
          this.props.state.multiStartIndex as TableConstants.TableIndex,
          this.props.state.multiEndIndex as TableConstants.TableIndex,
        ]
      : [index, index];

    if (this.compareIndex(start, end) === 0) {
      start = this.props.dimensions.firstIndex;
      end = this.props.dimensions.lastIndex;
      collapse = true;
    } else {
      collapse = false;
    }

    if (expanded) {
      newIndex = [index[0] + 1, index[1]] as TableConstants.TableIndex;
      newActualIndex = newIndex;
    } else {
      ({newIndex, newActualIndex} = this.moveCell(index, start, end, dir, wrap));
    }

    this.multiSelectSingle(newIndex, {
      editing: false,
      collapse,
      text: this.compareIndex(newIndex, index) === 0 ? text : null,
      multiActualIndex: newActualIndex,
    });
  };

  getFillDownRenderBox(): TableConstants.MultiSelectionBox | null {
    if (this.props.fillDown.box == null) {
      return null;
    }
    let {minI, maxI, minJ, maxJ} = this.props.fillDown.box;

    // If the fill down region is completely contained within the selection, don't render.
    const {minI: sbMinI, maxI: sbMaxI, minJ: sbMinJ, maxJ: sbMaxJ} = this._multiSelectionBox();
    if (
      sbMinI <= minI &&
      minI <= maxI &&
      maxI <= sbMaxI &&
      sbMinJ <= minJ &&
      minJ <= maxJ &&
      maxJ <= sbMaxJ
    ) {
      return null;
    }

    // Otherwise, subtract out the region that is in the selection.
    if (minI === sbMinI && maxI === sbMaxI) {
      // Horizontal fill
      if (maxJ > sbMaxJ) {
        // If it extends right,
        minJ = sbMaxJ + 1; //   start after the selection.
      } else if (minJ < sbMinJ) {
        // If it extends left,
        maxJ = sbMinJ - 1; //   end before the selection.
      }
    } else {
      // Vertical fill
      assert(minJ === sbMinJ && maxJ === sbMaxJ, 'Expected filldown to be either vertical or horizontal');
      if (maxI > sbMaxI) {
        // If it extends down,
        minI = sbMaxI + 1; //   start after the selection.
      } else if (minI < sbMinI) {
        // If it extends up,
        maxI = sbMinI - 1; //   end before the selection.
      }
    }
    return {minI, maxI, minJ, maxJ};
  }

  onMaximize = (): void => {
    if (this.props.setMaximizedTable) {
      this.props.setMaximizedTable(this.props.context.tableProvenanceUuid);
    }
  };

  onSplitScreen = (): void => {
    if (this.props.setSplitScreenTable) {
      this.props.setSplitScreenTable(this.props.context.tableProvenanceUuid);
    }
  };

  onClose = (): void => {
    if (this.props.setMaximizedTable) {
      this.props.setMaximizedTable(null);
    }
  };

  _renderStickyHeader(): JSX.Element | null {
    const {stickyHeaderPositioning} = this.props.state;
    if (!this._isStickyHeaderVisible() || !stickyHeaderPositioning) {
      return null;
    }
    const sliceStart = this.getTableSlices().sliceStart;
    return (
      <div
        ref={this.stickyHeaderRef}
        className="mediocre-tableEditable-stickyHeader"
        {...getTestProps({component: 'TableEditable', element: 'stickyHeader'})}
        style={{
          position: 'fixed',
          top: stickyHeaderPositioning.top,
          left: stickyHeaderPositioning.containerLeft ?? 0,
          width:
            (stickyHeaderPositioning.containerRight ?? 0) -
            (stickyHeaderPositioning.containerLeft ?? 0) -
            // If the table is at the maximum height, subtract the scrollbar width from the sticky header
            (this.props.state.currentFixedTableHeight !== TableConstants.MaximumTableHeight.FULL
              ? TableConstants.SCROLLBAR_WIDTH
              : 0),
          overflow: 'hidden',
          paddingRight: 0,
          paddingTop: undefined,
        }}
      >
        <div
          className="mediocre-tableEditable-tableWrapper"
          style={{
            position: 'relative',
            left:
              this.props.views.isFullHeight || this.props.views.isFixedHeight
                ? 0
                : (this.props.state.stickyHeaderPositioning?.left ?? 0) -
                  (this.props.state.stickyHeaderPositioning?.containerLeft ?? 0),
          }}
        >
          <div className="mediocre-tableEditable-flexBoxWrapper">
            <table
              className={classNames('mediocre-tableEditable-table', {
                'header-row': this.props.table.headerRow,
                'header-col': this.props.table.headerCol,
                'mediocre-tableEditable-table--is-dragging': Boolean(this.props.state.draggingDimension),
              })}
              style={{
                width: this.getSliceWidthPx(sliceStart, null),
              }}
              aria-hidden={true}
            >
              {this._renderColgroup(sliceStart, null)}
              <thead>{this._renderHeaderTR(sliceStart, null, false)}</thead>
            </table>
          </div>
        </div>
      </div>
    );
  }

  _getHeaderTRProps({
    sliceStart = null,
    sliceEnd = null,
    isAffixed,
    isSingleInput = false,
  }: {
    sliceStart?: number | null;
    sliceEnd?: number | null;
    isAffixed: boolean;
    isSingleInput?: boolean;
  }): TableHeaderRowProps {
    let minJ;
    let maxJ;
    if (this.props.selection.hasMultiSelection) {
      ({minJ, maxJ} = this._multiSelectionBox());
    }
    const {column: columns} = this.props.selection.multiAxisDimSelection;
    const tableMaxJ = this._tableSize()[1];

    return {
      fileId: this.props.fileId,
      isConfig: this.props.context.isConfigTable,
      // Table info
      table: this.props.table,
      canAddRemoveColumns: this.props.canAddRemoveColumns,
      ghostCellsCount: isSingleInput ? 0 : this.props.dimensions.ghostCellsCount,
      // Selection state
      isTableSelected: columns != null && columns[0] === 0 && columns[1] === tableMaxJ,
      maxJ: maxJ ?? null,
      minJ: minJ ?? null,
      selectedColumnRange: columns,
      // Rendering config
      disableColumnLocking: this.props.disableColumnLocking,
      disableLegacyHeader: this.props.disableLegacyHeader,
      isLeftAlignColumnHeader: this.props.isLeftAlignColumnHeader,
      isLeftAlignRowHeader: this.props.isLeftAlignRowHeader,
      locked: this.props.locked,
      showExcessLabels: this.props.showExcessLabels,
      viewer: this.props.viewer,
      isProtected: this.props.isProtected ?? false,
      hideColumnHeaderIcons: this.props.hideColumnHeaderIcons,
      showDragHandle: this._shouldShowDragHandles(TableConstants.TableDimension.COLUMN),
      // on the affixed table, make the header cells sticky to match the separate sticky header
      ...(isAffixed &&
        this._isStickyHeaderVisible() && {
          stickyHeaderCells: true,
          stickyTop: this.getStickyTopContentHeight() - this.props.state.pagePaddingTop,
        }),
      // Rendering slice
      sliceStart,
      sliceEnd,
      rowHeight: isAffixed ? this.props.state.rowHeights[0] : null,
      // Context menu helpers
      getDisallowedColumnNames: this.props.getDisallowedColumnNames,
      getExtraContextMenuOptions:
        // Partially apply this.getExtraContextMenuOptions with an `updateColumnConstraint` fn
        this.props.getExtraContextMenuOptions
          ? (selectionRange: TableConstants.SelectionRange | null) =>
              this.props.getExtraContextMenuOptions!(selectionRange, this.setColumnConstraint)
          : undefined,
      getSelectionRange: this.getSelectionRange,
      // Single Inputs use the Single-Input-compatible subset of insertable columns so that the
      // "Insert single input left/right" submenu only offers columns that can be Single Inputs.
      insertableColumnInfos: isSingleInput
        ? (this.props.singleInputInsertableColumnInfos ?? this.props.insertableColumnInfos)
        : this.props.insertableColumnInfos,
      // Table actions
      axisCellDragOverHandler: this.axisCellDragOverHandler,
      axisCellDragStartHandler: this.axisCellDragStartHandler,
      axisCellDragHandleMouseDown: this.axisCellDragHandleMouseDown,
      deleteColumns: this.deleteColumns,
      deleteTable: this._canDeleteTable() ? this.deleteTable : undefined,
      finalizeAndExitEditMode: this.finalizeAndExitEditMode,
      insertColumnsLeft: this.insertColumnsLeft,
      insertColumnsRight: this.insertColumnsRight,
      renameColumn: this.renameColumn,
      setColumnConstraint: this.setColumnConstraint,
      ...(supportsUserFreezeColumns(this.props.fileId) && {
        freezeThroughColumn: this.freezeThroughColumn,
        unfreezeFromColumn: this.unfreezeFromColumn,
      }),
      toggleHeaderColHandler: this.toggleHeaderColHandler,
      // Affixed Columns
      ...(!isSingleInput && {
        affixedColumnCount: TableUtil.getAffixedColumnCount(this.props.table),
        hasAnyFrozenColumns: (this.props.table.frozenColumnCount ?? 0) > 0,
        firstColumnWidth: TableUtil.firstColumnWidth(this.props.table),
        columnWidths: this.props.table.widths,
        showAffixedShadow: this.props.state.isAffixed,
      }),
      ...getSearchResultPropsForIndex(this.props, TABLE_SEARCH_PATHS.TABLE_HEADER_ROW),
    };
  }

  _renderHeaderTR(sliceStart: number | null, sliceEnd: number | null, isAffixed: boolean): JSX.Element {
    return <TableHeaderRow {...this._getHeaderTRProps({sliceStart, sliceEnd, isAffixed})} />;
  }

  getRowSelectionProps(
    rowIndex: number,
    selectionBox: TableConstants.MultiSelectionBox | null
  ): {
    selectedColIndexMin: number | null;
    selectedColIndexMax: number | null;
    selectedColIndexActive: number | null;
    fillDownSquareMouseDownHandler: ((e: ElementMouseEvent) => void) | null;
  } {
    if (selectionBox == null || this.props.state.multiSelIndex == null) {
      return {
        selectedColIndexMin: null,
        selectedColIndexMax: null,
        selectedColIndexActive: null,
        fillDownSquareMouseDownHandler: null,
      };
    }

    // Is the row within the selection?
    const isWithinSelection = rowIndex >= selectionBox.minI && rowIndex <= selectionBox.maxI;

    // Is the row selected?
    const [selectedRowIndexActive, selectedColIndexActive] = this.getRequiredMultiSelIndex();
    const isSelectedRow = selectedRowIndexActive === rowIndex;

    // Is the cell part of a merged cell whose row is selected?
    const mergedBox = this.props.computedTable[rowIndex]?.[selectedColIndexActive]?.mergedBox;
    const isMergedCellSelected =
      mergedBox != null &&
      mergedBox.minJ === selectedColIndexActive &&
      mergedBox.minI <= selectedRowIndexActive &&
      mergedBox.maxI >= selectedRowIndexActive;

    // Is the row the last row in the selection?
    const isLastRow = rowIndex === selectionBox.maxI;

    const canFillDown =
      isLastRow &&
      !this._hasActiveCell() &&
      !this.props.locking.isTableLocked &&
      !this.props.state.multiActive &&
      this.props.fillDown.isEnabled;

    return {
      selectedColIndexMin: isWithinSelection ? selectionBox.minJ : null,
      selectedColIndexMax: isWithinSelection ? selectionBox.maxJ : null,
      selectedColIndexActive: isSelectedRow || isMergedCellSelected ? selectedColIndexActive : null,
      fillDownSquareMouseDownHandler: canFillDown ? this.fillDownSquareMouseDownHandler : null,
    };
  }

  // Compute what edges should be rendered for rows/cells. Each cell has every border specified, so
  // if a dashed border is used for a cell, it may overlap with the adjacent cell's border, resulting in
  // no emphasis. For simplicity, we style both borders for every pair of adjacent cells.
  getRowFillDownProps(rowIndex: number): {
    fillColIndexMin: number | null;
    fillColIndexMax: number | null;
    isFillDownBoxTopEdge: boolean;
    isFillDownBoxBottomEdge: boolean;
    isFilledDown: boolean;
  } {
    const fillDownRenderBox = this.getFillDownRenderBox();
    if (fillDownRenderBox == null) {
      return {
        fillColIndexMin: null,
        fillColIndexMax: null,
        isFillDownBoxTopEdge: false,
        isFillDownBoxBottomEdge: false,
        isFilledDown: false,
      };
    }

    const isWithinFillDownRenderBox =
      fillDownRenderBox.minI - 1 <= rowIndex && rowIndex <= fillDownRenderBox.maxI + 1;

    return {
      fillColIndexMin: isWithinFillDownRenderBox ? fillDownRenderBox.minJ : null,
      fillColIndexMax: isWithinFillDownRenderBox ? fillDownRenderBox.maxJ : null,
      isFillDownBoxTopEdge: rowIndex === fillDownRenderBox.minI || rowIndex === fillDownRenderBox.maxI + 1,
      isFillDownBoxBottomEdge: rowIndex === fillDownRenderBox.maxI || rowIndex === fillDownRenderBox.minI - 1,
      isFilledDown: fillDownRenderBox.minI <= rowIndex && rowIndex <= fillDownRenderBox.maxI,
    };
  }

  _getTRProps({
    i,
    lockedRowIndices,
    sliceStart = null,
    sliceEnd = null,
    rowHeight = null,
    renderCellsOnly = false,
    isSingleInput = false,
    selectionBox = null,
  }: {
    i: number;
    lockedRowIndices: Array<number>;
    sliceStart?: number | null;
    sliceEnd?: number | null;
    rowHeight?: number | null;
    renderCellsOnly?: boolean | null;
    isSingleInput?: boolean | null;
    selectionBox?: TableConstants.MultiSelectionBox | null;
  }): TableRowProps {
    const tableHasActiveCell = this._hasActiveCell();
    const rowHasActiveCell = tableHasActiveCell && this.props.state.multiSelIndex?.[0] === i;

    const {selectedColIndexActive, selectedColIndexMin, selectedColIndexMax, fillDownSquareMouseDownHandler} =
      this.getRowSelectionProps(i, selectionBox);

    const {fillColIndexMin, fillColIndexMax, isFillDownBoxTopEdge, isFillDownBoxBottomEdge, isFilledDown} =
      this.getRowFillDownProps(i);

    const axisSelection = this.props.selection.multiAxisDimSelection;
    const {row: rows} = axisSelection;
    const tableMaxI = this._tableSize()[0];

    return {
      ...getSearchResultPropsForIndex(this.props, i),

      // Always show the rows if we're in viewer mode, otherwise use the default value
      calculateInView: this.props.viewer ? false : undefined,
      renderCellsOnly: renderCellsOnly || false,
      fileId: this.props.fileId,
      tableName: this.props.table.name,
      tableNamespace: this.props.tableNamespace,
      getLastIndex: this._getLastIndex,
      customLabel: this.getCustomRowLabel(i),
      locked: this.props.locking.isTableLocked,
      isProtected: this.props.isProtected ?? false,
      canModifyDims: this.props.dimensions.canModifyDims,
      canModifyRowDim: this.props.dimensions.canModifyRowDim,
      allowNoRows: TableUtil.allowNoRows(this.props.table),
      i,
      rowState: this.props.rowStates ? this.props.rowStates[i] : null,
      isHeaderRow: i === 0 && this.props.table.headerRow,
      headerRowHeight: this.props.state.rowHeights[0],
      hasHeaderCol: this.props.table.headerCol,
      cellColors: this.props.cellColors?.[i] ?? null,
      cellCid: this.cellCid,
      isLeftAlignRowHeader: this.props.isLeftAlignRowHeader,
      ghostCellsCount: isSingleInput ? 0 : this.props.dimensions.ghostCellsCount,
      // We only need to show excess labels on row header cells
      showExcessLabels: this.props.showTableRowExcessLabels && this.props.showExcessLabels,
      // Rendering slice
      sliceStart,
      sliceEnd,
      rowHeight,
      // Props for axis cell
      getCopySelection: this._getCopySelection,
      getCopySelectionHTML: this._getCopySelectionHTML,
      clearSelection: this.deleteRangeHelper,
      deleteRows: this.props.dimensions.canRemoveRows && !isSingleInput ? this.deleteRows : null,
      insertRowsAbove: this.insertRowsAbove,
      insertRowsBelow: this.insertRowsBelow,
      multiPasteHandler: this.multiPasteHandler,
      hasLockedCellInSelection: this.hasLockedCellInSelection,
      deleteTable: this._canDeleteTable() ? this.deleteTable : undefined,
      getExtraAxisCellContextMenuOptions: this.props.getExtraRowAxisContextMenuOptions,
      getSelectionRange: this.getSelectionRange,
      isTableSelected: rows != null && rows[0] === 0 && rows[1] === tableMaxI,
      toggleHeaderRow: !this.props.disableLegacyHeader ? this.toggleHeaderRow : undefined,
      numRows: this.props.table.table.length,
      lockedRowIndices: new Set(lockedRowIndices),
      showDragHandle: this._shouldShowDragHandles(TableConstants.TableDimension.ROW),
      // Props for fill down
      // No fill down operation is necessary for Single Inputs
      fillMaxJ: isSingleInput ? null : fillColIndexMax,
      fillMinJ: isSingleInput ? null : fillColIndexMin,
      isFillDownBoxBottomEdge: isSingleInput ? false : isFillDownBoxBottomEdge,
      isFillDownBoxTopEdge: isSingleInput ? false : isFillDownBoxTopEdge,
      isFilledDown: isSingleInput ? false : isFilledDown,
      // Props for selected cell
      hasActiveCell: rowHasActiveCell,
      axisSelection,
      selectedJ: selectedColIndexActive,
      selectedMinJ: selectedColIndexMin,
      selectedMaxJ: selectedColIndexMax,
      // Actions
      expandToAndEditCell: this.expandToAndEditCell,
      storeState: this.saveMultiSelect,
      commitCell: this.updateCell,
      moveFromCell: this.moveFromCell,
      selectCell: this.selectCell,
      // Event handlers
      axisCellDragOver: this.axisCellDragOverHandler,
      axisCellDragStart: this.axisCellDragStartHandler,
      axisCellDragHandleMouseDown: this.axisCellDragHandleMouseDown,
      cellEnterHandler: this.cellEnterHandler,
      cellEscapeHandler: this.cellEscapeHandler,
      cellDragOver: this.cellDragOverHandler,
      cellDragStart: this.multiEditableMouseDownHandler,
      cellMouseMoveHandler: this.fillDownMouseMoveHandler,
      fillDownSquareMouseDownHandler,
      fillDownSquareDoubleClickHandler: this.fillDownSquareDoubleClickHandler,
      keyDownHook: this.hideHoverCell,
      // Selectors
      selectorsEnabled: this.props.selectorsEnabled,
      openManageSelectorsModal: this.props.openManageSelectorsModal,
      // Affixed Columns
      ...(!isSingleInput && {
        affixedColumnCount: TableUtil.getAffixedColumnCount(this.props.table),
        hasAnyFrozenColumns: (this.props.table.frozenColumnCount ?? 0) > 0,
        firstColumnWidth: TableUtil.firstColumnWidth(this.props.table),
        columnWidths: this.props.table.widths,
        showAffixedShadow: this.props.state.isAffixed,
      }),
    };
  }

  _renderTR = ({
    i,
    lockedRowIndices,
    sliceStart = null,
    sliceEnd = null,
    rowHeight = null,
    renderCellsOnly = false,
    selectionBox = null,
  }: {
    i: number;
    lockedRowIndices: Array<number>;
    sliceStart?: number | null;
    sliceEnd?: number | null;
    rowHeight?: number | null;
    renderCellsOnly?: boolean | null;
    selectionBox?: TableConstants.MultiSelectionBox | null;
  }): JSX.Element => (
    <TableRow
      key={i}
      {...this._getTRProps({
        i,
        lockedRowIndices,
        sliceStart,
        sliceEnd,
        rowHeight,
        renderCellsOnly,
        selectionBox,
      })}
    />
  );

  getTableItemCid(): string {
    return this.props.tableItemCid != null ? this.props.tableItemCid : hiddenInputCid(this.props.cid);
  }

  selectTable(): void {
    if (this.props.selectTable) {
      this.props.selectTable();
    } else if (this.props.requestSelection) {
      this.props.requestSelection({selection: {cid: this.getTableItemCid(), start: 0, end: 0}});
    }
  }

  selectCell = (
    index: TableConstants.TableIndex,
    options: MultiSelectListUtil.MultiSelectSingleParam<TableConstants.TableIndex>
  ): void => {
    this.props.multiFinalizeEditHandler();
    this.multiSelectSingle(index, options);
  };

  improperFocusHook = (): void => {
    this.selectTable();
  };

  _handleTableSelected = (): void => {
    this.setMultiEditing({editing: false, index: null, text: null});
    this.clearMultiSelect();
    this.selectTable();
  };

  _tableContextMenuOptions(): Array<ClickableContextMenuOption> {
    return _.compact([
      !this.props.table._hideRename &&
        this.props.canRename && {
          name: 'Rename Table',
          click: this.openRenameTableModal,
        },
    ]);
  }

  openTableContextMenu = (e: ElementMouseEvent): void => {
    e.preventDefault();
    const message = this.props.locking.editWhileTableLockedWarningMessage;
    if (message) {
      notifications.warn(message);
      return;
    }
    viewContextMenu.open(e.nativeEvent, this._tableContextMenuOptions(), true);
  };

  hiddenCopyHandler = (e: ElementClipboardEvent): void => {
    e.preventDefault();
    this.props.setClipboard(this.props.index, e);
  };

  hiddenCutHandler = (e: ElementClipboardEvent): void => {
    if (!this.isParentLockedWithEditWarn() && !this.isLockedWithEditWarn() && this._canDeleteTable()) {
      e.preventDefault();
      this.props.setClipboard(this.props.index, e);
      if (this.props.onAtomicCut) {
        this.props.onAtomicCut();
      } else {
        this.deleteTable();
      }
    }
  };

  hiddenKeyDownHandler = (e: ElementKeyboardEvent): void => {
    if (!this._isMounted) {
      return;
    }
    if (keyboard.isCtrlC(e) || keyboard.isCtrlX(e)) {
      // Ensure input's selection is non-empty to ensure copy event in IE
      const hiddenInputNode = this.hiddenInputRef.current!;
      $(hiddenInputNode).html('&nbsp;');
      selutil.selectAll(hiddenInputNode);
      return;
    }

    // eslint-disable-next-line default-case
    switch (e.which) {
      case 38: // UP
        e.preventDefault();
        if (typeof this.props.moveHandler === 'function') {
          this.props.moveHandler(this.props.index, {up: true, beginning: true});
        }
        return;
      case 40: // DOWN
        e.preventDefault();
        if (typeof this.props.moveHandler === 'function') {
          this.props.moveHandler(this.props.index, {up: false, beginning: true});
        }
        return;
    }

    // The following actions should be only allowed if the protocol is not locked.
    if (keyboard.isUndoRedo(e)) {
      e.preventDefault();
      if (this.isParentLockedWithEditWarn({forUndoRedo: true})) {
        return;
      }
      if (typeof this.props.restoreState === 'function') {
        this.props.restoreState(e, {selection: selutil.getSelectionIndices()});
      }
      return;
    }

    if ([8, 46, 13, 9].includes(e.which) && this.isParentLockedWithEditWarn()) {
      e.preventDefault();
      return;
    }

    // eslint-disable-next-line default-case
    switch (e.which) {
      case 8: // BACKSPACE
        e.preventDefault();
        if (this._canDeleteTable()) {
          this.deleteTable();
        }
        break;
      case 46: // DELETE
        e.preventDefault();
        if (this._canDeleteTable()) {
          this.deleteTable();
        }
        break;
      case 13: {
        // ENTER
        e.preventDefault();
        const newIndex = e.shiftKey ? this.props.index - 1 : this.props.index;
        this.props.insertItemHandler(newIndex);
        break;
      }
      case 9: // TAB
        e.preventDefault();
        this.props.storeState({selection: selutil.getSelectionIndices()});
        if (typeof this.props.tabHandler === 'function') {
          this.props.tabHandler(this.props.index, {shiftKey: e.shiftKey, target: e.target});
        }
        break;
    }
  };

  isFormulaBoxVisible(): boolean {
    return (
      this.props.showFormulaBox &&
      !this.props.viewer &&
      !this.props.isCollapsed &&
      // While this "should" never happen, some result tables in the wild are broken, and have
      // no columns and no computed table. This guards against crashes when clicking on their samples
      this.props.computedTable[0].length > 0
    );
  }

  /**
   * The height of the sticky content is the title bar height + formula box height
   * This does not include the stickyHeader itself that renders the column headers
   */
  getStickyTopContentHeight(): number {
    return this.getTitleBarHeight() + this.getFormulaBoxHeight();
  }

  /**
   * Returns the height of the title bar, when visible.
   * For non-virtualized tables, this also includes the formula box because they are rendered
   * together in the title bar as a floating element.
   */
  getTitleBarHeight(): number {
    if (this.props.suppressTitleBar) {
      return 0;
    }
    return TableConstants.TABLE_HEADER_HEIGHT;
  }

  getFormulaBoxHeight(): number {
    if (!this.props.showFormulaBox) {
      return 0;
    }
    if (this.formulaBoxRef.current) {
      return this.formulaBoxRef.current.getBoundingClientRect().height;
    }
    // Fallback to the constant if ref is not available (during initial render)
    return TableConstants.FORMULA_BOX_HEIGHT_INLINE;
  }

  setColumnConstraint = (
    index: number,
    columnConstraint: TableConstants.ColumnConstraint,
    headerRowCell: Partial<TableConstants.Cell> = {}
  ): void => {
    this.saveMultiSelect();
    const newTable = produce(this.props.table, (draftTable) => {
      draftTable.columnConstraints[index] = columnConstraint;
      draftTable.table[0][index] = {
        ...draftTable.table[0][index],
        ...headerRowCell,
      };
    });

    // The safe thing is to assume that the change to the column constraint requires re-evaluation of the column's cells,
    // but a future optimization would be to not produce a table edit op for changes that don't alter evaluation.
    const tableOp: TableOpDiffMap = {
      [this.props.table.name]: {type: 'eval-columns', colIndexes: [index]},
    };

    this.updateHandler(this.props.index, {table: newTable}, undefined, tableOp);
  };

  private getSingleInputBoundary(): number {
    return this.props.singleInputs.boundaryIndex ?? 0;
  }

  private getFreezeBoundary(): number {
    return TableUtil.getFreezeBoundary(this.props.table);
  }

  freezeThroughColumn = (j: number): void => {
    const siBoundary = this.getSingleInputBoundary();
    const newCount = j - siBoundary + 1;
    assert(
      newCount > 0,
      `freezeThroughColumn: column ${j} is at or before singleInputBoundary ${siBoundary}`
    );
    if (newCount === TableUtil.getAffixedColumnCount(this.props.table)) {
      return;
    }
    if (hasMergeCrossingColumn(this.props.table.table, j)) {
      showFreezeMergeConflictToast('freeze');
      return;
    }
    const container = this._getTableHorizontalScrollContainer();
    const widths = this.props.table.widths;
    if (container && widths && widths.length > 0) {
      if (wouldFreezeExceedViewport(widths, siBoundary, j, container.clientWidth)) {
        ToastActions.pushToast({
          message: 'Cannot freeze: not enough room to display the next column.',
          type: ToastType.WARNING,
        });
        return;
      }
    }
    this.saveMultiSelect();
    this.finalizeAndExitEditMode();
    const newTable = produce(this.props.table, (draft) => {
      draft.frozenColumnCount = newCount;
    });
    this.updateHandler(this.props.index, {table: newTable});
  };

  unfreezeFromColumn = (j: number): void => {
    const siBoundary = this.getSingleInputBoundary();
    const newCount = j - siBoundary;
    const currentCount = TableUtil.getAffixedColumnCount(this.props.table);
    if (newCount >= currentCount) {
      return;
    }
    if (newCount > 0 && hasMergeCrossingColumn(this.props.table.table, j - 1)) {
      showFreezeMergeConflictToast('unfreeze');
      return;
    }
    this.saveMultiSelect();
    this.finalizeAndExitEditMode();
    const newTable = produce(this.props.table, (draft) => {
      draft.frozenColumnCount = newCount;
    });
    this.updateHandler(this.props.index, {table: newTable});
  };

  _handleHorizontalScroll = (): void => {
    if (!this._isMounted) {
      return;
    }
    if (this.props.isCollapsed) {
      // The browser may trigger the scroll event for non-user-initiated reasons even when the
      // the table is collapsed (BNCH-7640), so we simply no-op.
      return;
    }
    const table = (ReactDOM.findDOMNode(this) as Element).querySelector(
      '.mediocre-tableEditable-table--main'
    ) as Element;
    const tablePos = $(table).offset()!.left - parseInt($(table).css('left'), 10);
    const horizontalScrollArea = this._getTableHorizontalScrollContainer();
    const scrollPos = horizontalScrollArea ? $(horizontalScrollArea).offset()!.left : 0;
    const isAffixed = tablePos < scrollPos;
    if (this.props.showCellEditor && !this.props.state.hasScrolledSinceEdit) {
      this.props.setState({hasScrolledSinceEdit: true});
    }
    if (this.props.state.isAffixed !== isAffixed) {
      this.props.setState({isAffixed});
    }
  };

  _handleDoubleClickToAddRows = async (): Promise<void> => {
    const numRows = await legacyRunModal(AddRowsModal, {
      // plain tables do not have maxRows so we fall back to the editable table default config in that case
      max: this._getMaxAddableRows(),
    });
    if (numRows != null) {
      this.handleAddBlankRows(numRows);
    }
  };

  _getMaxAddableRows = (): number => {
    const isPlainTable = TableUtil.getTableKind(this.props.table) == null;
    const plainTableMax = BenchlingConfig.ELN_MAX_PLAIN_TABLE_ROWS;
    const maxRows = this.props.table.maxRows ?? (isPlainTable ? plainTableMax : getMaxRowsConfig(true));
    return Math.max(maxRows - this.props.table.table.length, 0);
  };

  isTitleBarSticky(): boolean {
    if (this.props.isCollapsed) {
      return false;
    }
    return this._isStickyHeaderVisible() || (this.props.views.isFullHeight && !this.props.viewer);
  }

  isBottomContainerSticky(): boolean {
    return (
      this.props.views.isFullHeight &&
      (this.shouldShowPersistentHorizontalScrollbar() || this.shouldRenderFooter())
    );
  }

  _renderFooter = (): JSX.Element | null => {
    if (!this.shouldRenderFooter()) {
      return null;
    }
    const canAddRows = this.props.dimensions.canAddRows || this.props.dirty;
    const rowCountLocked = !canAddRows && this.props.dimensions.hasLockedRowCount;
    const columnCount =
      TableUtil.getColumnCount(this.props.table, this.props.dimensions.shouldShowGhostCells) +
      (canAddRows || rowCountLocked ? 0 : 1);

    return (
      <div className="mediocre-tableEditable-footer-wrapper">
        <TableFooter
          canAddRows={canAddRows}
          rowCountLocked={rowCountLocked}
          columnCount={columnCount}
          footerControls={this.props.footerControls}
          footerInformation={this.props.footerInformation}
          showRoundedBottomCorners={!this.props.views.isMaximizedOrSplitScreen}
          maxAddableRows={this._getMaxAddableRows()}
          numberOfRows={this.props.table.table.length}
          onDraggerMouseDown={this.setIsInsertingRows}
          onDraggerDoubleClick={this._handleDoubleClickToAddRows}
          onAddRows={this.handleAddBlankRows}
        />
      </div>
    );
  };

  _renderColgroup(sliceStart: number | null, sliceEnd: number | null): JSX.Element {
    return (
      <colgroup>
        {TableUtil.mapColumns({
          ghostCellsCount: this.props.dimensions.ghostCellsCount,
          normalCellsCount: this.props.table.table[0].length,
          sliceStart,
          sliceEnd,
          // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
          mapLeadingCell: () => (
            <col key="colWidth-lead" style={{width: TableUtil.firstColumnWidth(this.props.table)}} />
          ),
          // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
          mapNormalCell: (j: number) => (
            <col
              data-id={j}
              key={`colWidth-${j}`}
              style={{width: this.props.table.widths[j]}}
              {...getTestProps({component: 'TableEditable', element: 'column', index: String(j)})}
            />
          ),
          // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
          mapGhostCell: (k: number) => (
            <col
              key={`colWidth-${this.props.table.widths.length + k}`}
              style={{width: TABLE_DEFAULT_COLUMN_WIDTH}}
            />
          ),
        })}
      </colgroup>
    );
  }

  getSliceWidthPx(sliceStart: number | null, sliceEnd: number | null): number {
    return TableUtil.tableWidth(this.props.table, this.props.dimensions.shouldShowGhostCells, {
      sliceStart,
      sliceEnd,
    });
  }

  _getLastIndex = (): TableConstants.TableIndex => this.props.dimensions.lastIndex;

  _renderFormulaBox(): JSX.Element | null {
    if (!this.isFormulaBoxVisible()) {
      return null;
    }
    const hasSelection = this.props.selection.hasRealSelection;
    const selectionBox = hasSelection ? this._multiSelectionBox() : null;

    const formulaBox = (
      <FormulaBox
        additionalButtons={
          this.props.renderAdditionalFormulaBoxButtons
            ? this.props.renderAdditionalFormulaBoxButtons(selectionBox)
            : undefined
        }
        additionalFunctions={this.props.additionalFunctions}
        additionalSettings={this.props.additionalSettings}
        banner={this.props.isMaximized ? this.props.banner : undefined} // Read-only banner should only show when table is fullscreen
        cellEnterHandler={this.cellEnterHandler}
        cellEscapeHandler={this.cellEscapeHandler}
        cid={this.props.cid}
        createSelectedCellsUpdate={this._createSelectedCellsUpdate}
        disableFormulas={this.props.disableFormulas}
        error={this._getError()}
        fileId={this.props.fileId}
        formatting={{
          bold: this.getSelectionFormatting(FormattersConstants.BOLD),
          italic: this.getSelectionFormatting(FormattersConstants.ITALIC),
          underline: this.getSelectionFormatting(FormattersConstants.UNDERLINE),
          strikethrough: this.getSelectionFormatting(FormattersConstants.STRIKETHROUGH),
          align: this.getSelectionAlignment(),
          link: this.getSelectionLink(),
          foregroundColor: this.getSelectionFormatting(FormattersConstants.FOREGROUND_COLOR),
          backgroundColor: this.getSelectionFormatting(FormattersConstants.BACKGROUND_COLOR),
          important: this.getSelectionFormatting(FormattersConstants.IMPORTANT),
          scientificNotation: this.getSelectionFormatting(FormattersConstants.SCIENTIFIC_NOTATION),
          merge: this._mergeButtonFormatting(),
          datetime: this._isSelectionOnlyDatetimeCells(),
          precision: this.getSelectionFormatting(FormattersConstants.PRECISION),
        }}
        getLastIndex={this._getLastIndex}
        disabled={!hasSelection || this.props.locked}
        disabledCellLinking={this.linkButtonDisabled()}
        disabledCellEditing={this.props.locking.isCellSelectionLocked}
        mergeHandler={this.supportsMerge() ? this.mergeHandler : undefined}
        mouseDownHandler={this._handleTableControlsMouseDown}
        multiSelIndex={this.props.state.multiSelIndex}
        numColumnsSupportingPrecision={
          hasSelection
            ? TableUtil.getNumberOfColumnsSupportingPrecision(
                this.props.table,
                selectionBox!.minJ,
                selectionBox!.maxJ
              )
            : 0
        }
        onFormulaFocus={this.formulaFocusHandler}
        onFunctionClick={this.handleFunctionClicked}
        onCellPropertyChange={this._setSelectionProperty}
        openSetSelectorModal={this.openSetSelectorModalAndFinalize}
        rowMetadata={
          this.props.state.multiSelIndex
            ? (this.props.table.rowData?.[this.props.state.multiSelIndex[0]]?.rowMetadata ?? null)
            : null
        }
        selectorsEnabled={this.props.selectorsEnabled}
        tableName={this.props.table.name}
        tableNamespace={this.props.tableNamespace}
      />
    );
    return (
      <div ref={this.formulaBoxRef} className="mediocre-tableEditable-formulaBox">
        {formulaBox}
      </div>
    );
  }

  _toggleCollapsed = (): void => {
    if (this.props.isCollapsible && !this.props.views.isMaximizedOrSplitScreen) {
      if (!this.props.isCollapsed) {
        this._snapPageScrollToTableTopIfPastHeader();
      }
      CollapsedTableReduxActions.toggle(getCollapseId(this.props));
    }
  };

  /**
   * Adjusts the entry's scroll position so the table sits at the top of the viewport when the
   * user has scrolled past the table's title bar. Called before collapsing.
   *
   * Without this, the scrollTop is preserved across the collapse, which causes the visible
   * content to jump downward by the height of the table body. For a long table, the user's
   * old scrollTop typically lands past the end of the now-shorter document and the browser
   * clamps it, so collapsing while scrolled into a long table looks like it flings the user
   * to the bottom of the entry. Snapping pre-collapse keeps the (now-collapsed) table where
   * the user was looking and shows the content immediately below it.
   *
   * No-op when the title bar is still in the viewport (so collapsing tables you're looking
   * at directly continues to behave as before) or when we can't resolve the title bar or
   * entry scroll container.
   */
  _snapPageScrollToTableTopIfPastHeader = (): void => {
    const scrollContainer = this.verticalScrollContainer;
    const titleBar = this.tableTitleBarRef.current;
    if (!scrollContainer || !titleBar) {
      return;
    }
    const tableNode = ReactDOM.findDOMNode(this);
    if (!(tableNode instanceof Element)) {
      return;
    }
    const tableRect = tableNode.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    // The title bar sits at the top of the table, so its natural bottom edge is at
    // `tableRect.top + titleBar.offsetHeight`. Only snap once that natural bottom is above
    // the container top — i.e. the title bar has fully scrolled out of view (whether it's
    // currently sticky-pinned or scrolled off entirely). `offsetHeight` is used instead of
    // the title bar's own bounding rect because the rect reflects the sticky-pinned position,
    // not the natural one, when the user is scrolled into a long table.
    if (tableRect.top + titleBar.offsetHeight >= containerRect.top) {
      return;
    }
    const delta = tableRect.top - containerRect.top;
    scrollContainer.scrollTop = Math.max(0, scrollContainer.scrollTop + delta);
  };

  _renderHeaderControls(headerControls?: TableConstants.TableHeaderControls): JSX.Element | null {
    if (headerControls == null) {
      return null;
    }
    if (typeof headerControls === 'function') {
      const args = this._getHeaderControlsArgs();
      return headerControls(args);
    }
    return headerControls;
  }

  _renderLeftHeaderControls(): JSX.Element | null {
    if (this.props.viewer) {
      return null;
    }
    return this._renderHeaderControls(this.props.leftHeaderControls);
  }

  _renderRightHeaderControls(): JSX.Element | null {
    if (this.props.viewer) {
      return null;
    }
    const headerControls = this._renderHeaderControls(this.props.headerControls);
    return (
      <>
        {this.props.isCollapsed && (
          <RowCountText
            hide={this.props.hideRowCount}
            numRows={this.props.forceRowCount ?? this.props.table.table.length}
          />
        )}
        {headerControls}
      </>
    );
  }

  _getHeaderControlsArgs(): TableConstants.TableHeaderControlsArgs {
    const multiSelectionBox = this.props.selection.hasMultiSelection ? this._multiSelectionBox() : null;
    return {
      multiSelectionBox,
      computedTable: this.props.computedTable,
      isCollapsed: this.props.isCollapsed,
    };
  }

  _renderFooterControls(): JSX.Element | null | undefined {
    if (this.props.viewer) {
      return null;
    }

    return this.props.footerControls;
  }

  _shouldRenderCollapserChevron(): boolean {
    if (this.props.forceCollapsed || this.props.views.isMaximizedOrSplitScreen) {
      return false;
    } else if (this.props.isCollapsed) {
      return true;
    } else {
      return this.props.isCollapsible;
    }
  }

  _renderToolTipText(): string {
    let tooltipText = '';

    // If the table is a config table, the tooltip should not contain any text.
    if (this.props.context.isConfigTable) {
      tooltipText = '';
    } else {
      // Else check if the table has a status and return the respective string value.
      switch (this.props.tableStatus) {
        case TableConstants.TableIndicatorStatus.ERROR: {
          tooltipText = 'Some values with error';
          break;
        }
        case TableConstants.TableIndicatorStatus.IMMUTABLE: {
          tooltipText =
            'This table is a snapshot of the data at the time of its creation, which has subsequently been archived or modified';
          break;
        }
        case TableConstants.TableIndicatorStatus.OUT_OF_DATE: {
          tooltipText = 'Some values out of date';
          break;
        }
        case TableConstants.TableIndicatorStatus.ROWS_ARCHIVED: {
          tooltipText = 'Some values archived';
          break;
        }
        case TableConstants.TableIndicatorStatus.TABLE_OR_SCHEMA_ARCHIVED: {
          tooltipText = 'Table or required schemas archived';
          break;
        }
        case TableConstants.TableIndicatorStatus.UNSUBMITTED_CHANGES: {
          tooltipText = 'Some values not submitted';
          break;
        }
        default: {
          tooltipText = '';
        }
      }
    }

    // Only legacy tables use the nameTooltip prop, but if a table status is also set, we should merge
    // the two tooltips together.
    if (this.props.table.nameTooltip) {
      if (tooltipText) {
        tooltipText = `${this.props.table.nameTooltip}\n${tooltipText}`;
      } else {
        tooltipText = this.props.table.nameTooltip;
      }
    }

    return tooltipText;
  }

  getTitleTooltip(): string | null {
    let titleTooltip: string | null = null;
    const tableKind = TableUtil.getTableKind(this.props.table);

    if (
      this.props.table.metadata &&
      // eslint-disable-next-line benchlint/no-in-operator
      'schemas' in this.props.table.metadata &&
      this.props.table.metadata.schemas.length > 0 &&
      // We don't want the schema tooltip for parameter confirmation tables, which usually include multiple parameter schemas.
      (tableKind == null || !ALL_PARAMETER_STRUCTURED_TABLE_KINDS.includes(tableKind))
    ) {
      if (this.props.table.metadata.schemas.length > 1) {
        titleTooltip = 'Multiple schemas';
      } else {
        const schemaName = this.props.table.metadata.schemas[0]?.name;
        const schemaDescription = isSchemaWithDescription(this.props.table.metadata.schemas[0])
          ? this.props.table.metadata.schemas[0].schemaDescription
          : undefined;
        if (schemaName) {
          titleTooltip = `Schema: ${schemaName}`;
          if (schemaDescription) {
            titleTooltip = `${titleTooltip}\n\nDescription: ${schemaDescription}`;
          }
        }
      }
    }

    // If an inventory table accepts any plate or any container schema, provides the table a default schema
    // name to make it viewable on the table title tooltip.
    if (!titleTooltip && tableKind === StructuredTableKind.INVENTORY) {
      const storableSchemaType = (
        this.props.table.metadata as TableConstants.StructuredTableToTableMetadata<InventoryTableMetadata>
      )?.metadata.storableSchemaType;

      if (storableSchemaType === InventoryStructuredTableSchemaType.PLATE) {
        titleTooltip = 'Any plate (no schema)';
      } else if (storableSchemaType === InventoryStructuredTableSchemaType.CONTAINER) {
        titleTooltip = 'Any container (no schema)';
      }
    }

    return titleTooltip;
  }

  _renderTitleBar(): JSX.Element | null {
    if (this.props.suppressTitleBar) {
      return null;
    }

    const isTitleBarSticky = this.isTitleBarSticky();

    const paddingAboveStickyHeader = -this.props.state.pagePaddingTop;

    return (
      // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
      <TableCollapserContext.Provider value={{isTableInitiallyCollapsed: this.props.isCollapsedInitially}}>
        <div
          className={classNames('mediocre-tableEditable-titleBarWrapper', {
            'mediocre-tableEditable-titleBarWrapper--sticky': isTitleBarSticky,
          })}
          style={
            isTitleBarSticky
              ? {
                  top: paddingAboveStickyHeader,
                }
              : {}
          }
          ref={this.tableTitleBarRef}
        >
          <TableTitleBar
            leftHeaderControls={this._renderLeftHeaderControls()}
            rightHeaderControls={this._renderRightHeaderControls()}
            onHeaderControlsMouseDown={this._handleTableControlsMouseDown}
            icon={this.props.icon}
            titleTooltip={this.getTitleTooltip()}
            isTableExpanded={!this.props.isCollapsed}
            isTableMaximized={this.props.isMaximized}
            isTableSplitScreen={this.props.isSplitScreen}
            isFocused={this.props.isFocused}
            viewer={this.props.viewer}
            disableMaximize={!this.props.views.canMaximize}
            disableSplitScreen={!this.props.views.canSplitScreen}
            useRoundedTopBorder={!this.isFormulaBoxVisible() && !this.props.views.isMaximizedOrSplitScreen}
            useRoundedBottomBorder={this.props.isCollapsed}
            tableIndentationPx={0}
            tableTypeLabel={this.props.tableLabel}
            tableName={this.props.table.renderedName || this.props.table.name}
            shouldRenderCollapserChevron={this._shouldRenderCollapserChevron()}
            usePointerCursor={this._shouldShowTitlePointer()}
            tooltipText={this._renderToolTipText()}
            onOpenContextMenu={this.openTableContextMenu}
            onTitleBarClick={this._handleTableSelected}
            onTableNameRefChanged={this.setTableNameRef}
            onToggleCollapsed={this._toggleCollapsed}
            onMaximize={this.onMaximize}
            onSplitScreen={this.onSplitScreen}
            onClose={this.onClose}
            status={
              this.props.context.isConfigTable || this.props.tableStatus == null
                ? TableIndicatorStatus.NO_STATUS
                : this.props.tableStatus
            }
            shouldIncreaseCollapserSpacing={this.props.isNestedInParentComponent}
            isTableHeaderFaded={this.props.isTableHeaderFaded && !this.props.isCollapsed}
            dataTestAttrs={{component: 'TableEditable', element: 'titleBar'}}
            {...getSearchResultPropsForIndex(this.props, TABLE_SEARCH_PATHS.NAME)}
          />
        </div>
      </TableCollapserContext.Provider>
    );
  }

  _shouldShowTitlePointer(): boolean {
    return Boolean(
      !this.props.forceCollapsed && (this.props.requestSelection != null || this.props.isCollapsible)
    );
  }

  _renderVirtualizedTableTag = ({
    children,
    context: _context,
    ...props
  }: React.ComponentProps<'table'> & {context?: unknown}): JSX.Element | null => {
    const {sliceStart} = this.getTableSlices();
    const tableWidth = this.getSliceWidthPx(sliceStart, null);
    return (
      <div style={{position: 'relative', width: '100%'}}>
        {this.props.views.isMaximizedOrSplitScreen &&
          this._isSingleInputVisible() &&
          this._renderSingleInputGroup(true)}
        <table
          ref={this.tableRef}
          {...props}
          className={classNames('mediocre-tableEditable-table', 'mediocre-tableEditable-table--main', {
            'header-row': this.props.table.headerRow,
            'header-col': this.props.table.headerCol,
            'mediocre-tableEditable-table--scrolledHorizontally': this.props.state.isAffixed,
            'mediocre-tableEditable-table--is-dragging': Boolean(this.props.state.draggingDimension),
          })}
          style={{width: tableWidth}}
          {...getTestProps({component: 'TableEditable', element: 'table'})}
        >
          {this._renderColgroup(sliceStart, null)}
          {children}
        </table>
        {!this.props.locked &&
          (this.props.enableColumnResizing ?? true) &&
          !this.props.fillDown.isActive &&
          !this.props.state.draggingDimension && (
            <CellResizer
              widths={this.props.table.widths}
              setColumnWidth={this.setColumnWidth}
              allowAutoResize={true}
              tableRef={this.tableRef}
              stickyHeaderRef={this.stickyHeaderRef}
              isStickyHeaderVisible={this._isStickyHeaderVisible()}
            />
          )}
        {this._renderDraggableDimensionMover()}
      </div>
    );
  };

  _getVirtualizedScrollContainerStyle(): CSSProperties {
    const maxTableHeight = this.getCurrentMaxTableHeight();
    if (this.props.views.isFixedHeight) {
      return {
        // 'height' gets overriden here
        // to avoid cropping of paginated tables in PDF exports.
        height:
          this.props.state.currentFixedTableHeight === TableConstants.MaximumTableHeight.FULL ||
          !maxTableHeight
            ? 'fit-content'
            : maxTableHeight,
        maxHeight: maxTableHeight,
        minHeight: this.getMinTableHeight(),
      };
    }
    if (this.props.views.isFullHeight) {
      return {
        height: 'fit-content',
        minHeight: this.getMinTableHeight(),
      };
    }
    return {};
  }

  /**
   * To optimize performance, we should not virtualize rows if the table is below the minimum.
   * The javascript overhead of virtualization is not worth it for small tables.
   */
  isVirtualizationEnabledForTable(): boolean {
    return (
      !this.props.viewer && this.props.table.table.length > TableConstants.MINIMUM_ROWS_FOR_VIRTUALIZATION
    );
  }

  /**
   * Renders a table without virtualization.  This is intended to be used for exports and print preview.
   * This should use the same rendering methods as _renderVirtualizedTable below without using Virtuoso.
   *
   * One specific difference is that the header TR (column headers) should be rendered inside the tbody
   * instead of a thead.  This is because the browser print code will repeat the thead on each page of the pdf
   * and this is not desired in a PDF export.
   */
  _renderUnvirtualizedTable(): JSX.Element | null {
    const {sliceStart} = this.getTableSlices();
    const context = this.getVirtualizedTableContext(
      this.props.state,
      this.props.table.widths,
      this.props.computedTable
    );
    const table = this.props.table.table;
    const selectionBox = this.props.selection.hasMultiSelection ? this._multiSelectionBox() : null;
    return this._renderVirtualizedTableTag({
      children: (
        <>
          <tbody>
            {this._renderHeaderTR(this.getTableSlices().sliceStart, null, false)}
            {table.map((_, i) => (
              <TableRowTagMemoized
                data-index={i}
                data-item-index={i}
                data-known-size={-1}
                item={table[i]}
                context={context}
                key={i}
              >
                {this._renderTR({
                  i,
                  lockedRowIndices: this.props.state.lockedRowIndices,
                  renderCellsOnly: true,
                  sliceStart,
                  selectionBox,
                })}
              </TableRowTagMemoized>
            ))}
          </tbody>
          {this._renderFooter()}
        </>
      ),
    });
  }

  _renderVirtualizedTable(): JSX.Element | null {
    // The virtualized table re-renders when the data or context changes. We want this to happen when the
    // table state changes or certain props change.
    const context = this.getVirtualizedTableContext(
      this.props.state,
      this.props.table.widths,
      this.props.computedTable
    );
    const {sliceStart} = this.getTableSlices();
    const parentScrollContainer: HTMLElement = this.props.views.isFullHeight
      ? (this._getPageVerticalScrollContainer() as HTMLElement)
      : (this.horizontalScrollContainerRef.current as HTMLElement);

    const table = this.props.table.table;
    const selectionBox = this.props.selection.hasMultiSelection ? this._multiSelectionBox() : null;
    return (
      <TableVirtuoso
        // We customize the item size calculation because the default implementation rounds the height to the
        // nearest pixel, which can lead to an incorrect total height calculation, particularly when the
        // browser is zoomed in.
        // Context: https://github.com/petyosi/react-virtuoso/issues/680
        itemSize={(i) => i.getBoundingClientRect().height}
        className={classNames('mediocre-tableEditable-table--virtualized', {
          'override-virtuoso': !this.isVirtualizationEnabledForTable(),
        })}
        {...getTestProps({
          component: 'TableEditable',
          element:
            this.props.state.currentFixedTableHeight === TableConstants.MaximumTableHeight.FULL
              ? 'fullHeightTable'
              : 'virtualizedTable',
        })}
        ref={this.virtualizationHandleRef}
        customScrollParent={this.isVirtualizationEnabledForTable() ? parentScrollContainer : undefined}
        totalCount={table.length}
        itemsRendered={(items) => {
          this.visibleRowIndicesRef.current = items.map((item) => item.index);
        }}
        context={context}
        computeItemKey={(index) => `${this.props.cid}-${index}`}
        increaseViewportBy={
          this.props.views.isFullHeight || this.props.views.isMaximizedOrSplitScreen
            ? {
                top: 1500,
                bottom: 1500,
              }
            : {
                top: 200,
                bottom: 200,
              }
        }
        fixedHeaderContent={() => this._renderHeaderTR(this.getTableSlices().sliceStart, null, false)}
        components={{
          Table: this._renderVirtualizedTableTag,
          TableRow: TableRowTagMemoized,
        }}
        data={table}
        itemContent={(index) => {
          if (index >= table.length) {
            // An extra render is possible here when the length of the table has changed but Virtuoso hasn't
            // updated its internal memoization of the data.  This is a workaround to skip a potential render
            // of a row that no longer exists.
            // This occurs because Virtuoso is expecting you to use the data passed from their internal
            // memoization, but we're using the data from the table prop since we can't rely on their
            // memoization.
            return undefined;
          }
          return this._renderTR({
            i: index,
            lockedRowIndices: this.props.state.lockedRowIndices,
            renderCellsOnly: true,
            sliceStart,
            selectionBox,
          });
        }}
      />
    );
  }

  _getSingleInputGroupHeight(): number {
    const singleInputGroupWrapper = (ReactDOM.findDOMNode(this) as HTMLElement).querySelector(
      '.mediocre-tableEditable-singleInputGroup'
    );
    return singleInputGroupWrapper?.getBoundingClientRect().height ?? 0;
  }

  _renderDraggableDimensionMover(): JSX.Element | null {
    const {sliceStart} = this.getTableSlices();
    const draggingRange = this.getDraggingRange();
    if (!draggingRange) {
      return null;
    }
    const realRowHeights = this.props.state.rowHeights.slice(1);
    assert(
      realRowHeights.length > 0,
      'Row heights should not be empty when rendering draggable dimension mover'
    );
    return (
      <DraggableDimensionMover
        tableRef={this.tableRef}
        dimension={draggingRange.dimension}
        startIndex={draggingRange.range.minIndex}
        endIndex={draggingRange.range.maxIndex}
        getFirstVisibleIndex={() =>
          draggingRange.dimension === TableConstants.TableDimension.COLUMN
            ? (sliceStart ?? 0)
            : // Once the table is missing rows due to virtualization, there will always be one addition row in
              // the rowHeights that represents all previously unrendered rows. So this needs to be accounted
              // for here.
              (this.visibleRowIndicesRef.current?.[0] || 1) - 1
        }
        onDragComplete={this.onDragComplete}
        firstColumnWidth={TableUtil.firstColumnWidth(this.props.table)}
        columnWidths={this.props.table.widths.slice(sliceStart ?? 0)}
        headerRowHeight={this.props.state.rowHeights[0]}
        rowHeights={realRowHeights}
        verticalOffset={this.props.views.isMaximizedOrSplitScreen ? this._getSingleInputGroupHeight() : 0}
      />
    );
  }

  _headerHeight(): number {
    return this.props.suppressTitleBar ? 0 : TableConstants.TABLE_HEADER_HEIGHT;
  }

  _getPrintableLayoutWidth(): number {
    // Indentation and padding are contained within the DOM node for .printable-container-scaleForPrint,
    // so we include those in the calculated width of this node to ensure proper scaling.
    const indentation = this._indentationPx();
    const tableWidth = TableUtil.tableWidth(
      this.props.table,
      this.props.dimensions.shouldShowGhostCells,
      this.getTableSlices()
    );
    const tablePadding = TableConstants.TABLE_PADDING * 2;
    return indentation + tableWidth + tablePadding;
  }

  /** Are we both tracking and actually displaying the sticky header (the table is scrolled past the header and focused) */
  _isStickyHeaderVisible(): boolean {
    return (
      this.props.state.canShowStickyHeader &&
      this.props.state.stickyHeaderPositioning !== undefined &&
      this.props.selection.hasMultiSelection
    );
  }

  _isSingleInputVisible(): boolean {
    return !this.props.isCollapsed && Boolean(this.props.singleInputs.boundaryIndex);
  }

  _renderSingleInputGroup(isSticky: boolean = false): JSX.Element | null {
    const firstNonSingleInputIndex = this.props.singleInputs.boundaryIndex;
    if (!firstNonSingleInputIndex) {
      return null;
    }
    const horizontalScrollWrapperWidth = this.getHorizontalScrollWrapperRect()?.width;
    const stickyStyles: CSSProperties = isSticky
      ? {
          left: 0,
          width: horizontalScrollWrapperWidth
            ? horizontalScrollWrapperWidth - TableConstants.SCROLLBAR_WIDTH
            : '100%',
          position: 'sticky',
        }
      : {};

    const selectionBox = this.props.selection.hasMultiSelection ? this._multiSelectionBox() : null;

    return (
      <div className="mediocre-tableEditable-singleInputGroup-container" {...getStopDragPropagationProps()}>
        <div style={stickyStyles}>
          <TableSingleInputGroup
            sliceStart={0}
            sliceEnd={firstNonSingleInputIndex}
            clearMultiSelect={this.clearMultiSelect}
            headerRowProps={this._getHeaderTRProps({
              isAffixed: false,
              isSingleInput: true,
            })}
            rowProps={this._getTRProps({
              i: 0 /* row index of single input */,
              lockedRowIndices: this.props.state.lockedRowIndices,
              renderCellsOnly: true,
              isSingleInput: true,
              selectionBox,
            })}
            columnWidths={this.props.table.widths}
            setColumnWidth={this.setSingleInputColumnWidth}
            locked={this.props.locked}
            tableName={this.props.table.name}
            storeState={this.saveMultiSelect}
            reorderSingleInputs={this.props.tableMutations.reorderRowsOrColumns}
          />
        </div>
      </div>
    );
  }

  _renderReadOnlyViewCollapseBanner(): JSX.Element {
    const {rowCount, cellCount} = TableUtil.getTableDimensionsFromTable(this.props.table);
    const isOverInlineRowLimit = getIsOverInlineRowLimit(rowCount, TableUtil.getTableKind(this.props.table));
    const isOverInlineCellLimit = getIsOverInlineCellLimit(
      cellCount,
      TableUtil.getTableKind(this.props.table)
    );
    return (
      <TableReadonlyViewCollapseBanner
        tableName={this.props.table.name}
        tableId={this.props.table.apiId ?? this.props.context.tableProvenanceUuid!}
        fileId={this.props.table.fileId ?? null}
        isOverInlineRowLimit={isOverInlineRowLimit}
        isOverInlineCellLimit={isOverInlineCellLimit}
      />
    );
  }

  /** Returns the pieces of the table that affect the slicing of the displayed table. This is important for
   * Single Inputs which offset the table indices.
   *
   * sliceStart and sliceEnd are INCLUSIVE when specified.
   */
  private getTableSlices(): {sliceStart: number | null; sliceEnd: number | null} {
    return {
      sliceStart: this.props.singleInputs.boundaryIndex || null,
      sliceEnd: null,
    };
  }

  /** Returns the sliceStart and sliceEnd of the table, defaulting them to 0 and the end of the table
   * respectively if the table is not actually sliced.
   */
  private getEffectiveTableSlices(): {sliceStart: number; sliceEnd: number} {
    const {sliceStart, sliceEnd} = this.getTableSlices();
    return {
      sliceStart: sliceStart ?? 0,
      sliceEnd: sliceEnd ?? this._tableSize()[1],
    };
  }

  /**
   * Creates a TableCellEditor for the cell being currently edited that is absolutely positioned and floating.
   * Initially, this should appear identical to the active cell, but once the table or page has been scrolled
   * then it should start displaying a label of the cell's coordinates.
   */
  renderTableCellEditor(): JSX.Element | null {
    let rowIndex: number;
    let baseColIndex: number;
    let selectionBox: TableConstants.MultiSelectionBox | null;
    let colIndex: number;

    if (this._hasActiveCell() && this.props.state.multiSelIndex) {
      // Normal case: we have an active cell
      rowIndex = this.props.state.multiSelIndex[0];
      baseColIndex = this.props.state.multiSelIndex[1];
      selectionBox = this._multiSelectionBox();
      colIndex = this.getRowSelectionProps(rowIndex, selectionBox).selectedColIndexActive ?? baseColIndex;
    } else if (this.props.showCellEditor) {
      // Cross-step editing case: get the index from the store and bootstrap selection
      const storeState = SpreadsheetStore.getState(
        this.props.tableNamespace,
        this.props.table.name,
        this.props.fileId
      );
      if (!storeState.index || !storeState.selection) {
        return null;
      }
      rowIndex = storeState.index[0];
      baseColIndex = storeState.index[1];
      selectionBox = null;
      // For cross-step editing, we don't have a selection, so we use the column index directly
      colIndex = baseColIndex;
      // Seed selection state from store for cross-step editing
      // Use queueMicrotask to defer state updates until after render completes,
      // avoiding "Cannot update a component while rendering a different component" warning
      queueMicrotask(() => {
        const currentStoreState = SpreadsheetStore.getState(
          this.props.tableNamespace,
          this.props.table.name,
          this.props.fileId
        );
        const hasExistingSelection =
          this.props.selection.hasRealSelection &&
          this.props.state.multiStartIndex != null &&
          this.props.state.multiEndIndex != null;
        this.props.setState({
          multiActive: false,
          multiSelIndex: currentStoreState.index,
          multiStartIndex: hasExistingSelection ? this.props.state.multiStartIndex : currentStoreState.index,
          multiEndIndex: hasExistingSelection ? this.props.state.multiEndIndex : currentStoreState.index,
          multiActualIndex: currentStoreState.index,
          // If returning to the original step, mark that we've scrolled since edit to keep label rendered
          ...(this.props.state.multiEditing && {hasScrolledSinceEdit: true}),
        });
        this.setMultiEditing({
          index: currentStoreState.index,
          editing: true,
          text: currentStoreState.formula ?? '',
          typedValue: currentStoreState.typedValue ?? null,
          selection: currentStoreState.selection,
        });
      });
    } else {
      return null;
    }
    assert(colIndex != null, 'Column index should not be null');

    const evaluatedRow = this.props.computedTable[rowIndex];
    // If the row hasn't been evaluated yet or is a ghost, then don't render it.
    // This will get called again after evaluation completes
    if (!evaluatedRow || !this.isRealIndex([rowIndex, baseColIndex])) {
      return null;
    }

    const rowData = this.props.table.rowData?.[rowIndex] ?? null;
    const evaluatedCell = evaluatedRow[colIndex];

    const columnConstraint = getColumnConstraintWithRowData(
      this.props.table.columnConstraints[colIndex],
      rowData
    );

    // If this is the first rendered row of a merge cell, then we must use the virtual row index of the first
    // row of the merge. The only merge cell we render must be the top left cell in the merge, or a simulated
    // version of it if the real top left cell is not rendered.
    const mergedBox = evaluatedCell?.mergedBox;
    const virtualRowIndex = mergedBox !== undefined ? mergedBox.minI : rowIndex;

    const lastSyncedUpstreamValue = getLastSyncedUpstreamValueWithColumnConstraint(
      rowData?.lastSyncedUpstreamValues,
      columnConstraint
    );

    const tableCellEditorProps: TableCellEditorProps = {
      i: virtualRowIndex,
      j: colIndex,
      fileId: this.props.fileId,
      tableName: this.props.table.name,
      tableNamespace: this.props.tableNamespace,
      getLastIndex: this._getLastIndex,
      cid: this.cellCid(virtualRowIndex, colIndex),
      lastSyncedUpstreamValue: lastSyncedUpstreamValue ?? undefined,
      highlightClass: this.props.cellColors?.[rowIndex]?.[colIndex],
      storeState: () => {
        this.saveMultiSelect();
      },
      commitCell: (
        index: TableConstants.TableIndex,
        text: string,
        cellData?: Partial<TableConstants.Cell>,
        expand?: boolean
      ) => {
        this.updateCell(index, text, cellData, expand);
      },
      moveFromCell: (args: {
        index: TableConstants.TableIndex;
        dir: 'up' | 'down' | 'left' | 'right' | 'none';
        text?: string | null;
        wrap?: boolean;
        expanded?: boolean;
      }) => {
        this.moveFromCell(args);
      },
      cellEnterHandler: (e: {
        text: string;
        cellData: Partial<TableConstants.Cell>;
        enterKey?: boolean;
        shiftKey?: boolean;
        index?: TableConstants.TableIndex;
      }) => {
        this.cellEnterHandler(e);
      },
      cellEscapeHandler: () => {
        this.cellEscapeHandler();
      },
      keyDownHook: () => {
        this.hideHoverCell();
      },
      disableFormulas: this.props.disableFormulas,
      ...(this.props.state.hasScrolledSinceEdit && {
        floating: {
          // If we can't scroll, don't allow navigation to the editing cell
          ...(this._getTableVerticalScrollContainer() && {
            navigateToEditingCell: () => {
              this._scrollTableIntoView();
              this._scrollCellIntoView([virtualRowIndex, colIndex]);
            },
          }),
        },
      }),
    };

    return (
      <RowModelProvider rowIndex={virtualRowIndex}>
        <TableCellEditor {...tableCellEditorProps} />
      </RowModelProvider>
    );
  }

  override render(): JSX.Element {
    return (
      // Use a DIV container to prevent modals's <noscript> from causing a large reflow
      <div
        role="region"
        aria-label={this.props.table.name}
        ref={this.props.tableRef}
        data-index={this.props.index}
        data-subindex="cell"
        data-formattable={true}
        data-printable-layout-width={this._getPrintableLayoutWidth()}
        {...getTestProps(
          this.props.dataTestAttrs ? this.props.dataTestAttrs : {component: 'TableEditable', element: 'root'}
        )}
        className={classNames('mediocre-tableEditable-container multi-editable', {
          'printable-container-scaleForPrint': !this.props.isPermaCollapsed,
        })}
        onMouseMove={this.scrollAreaMouseMoveHandler}
      >
        <div
          className={classNames('mediocre-tableEditable', {
            'hide-hover': this.props.state.hideHoverCell,
            'tableEditable-fullyExpanded': this.props.views.isFullHeight,
            'tableEditable-fixedHeight': this.props.views.isFixedHeight,
            'tableEditable-maximized': this.props.views.isMaximizedOrSplitScreen,
            'tableEditable-printView': this.props.viewer,
          })}
          style={{
            marginLeft: this._indentationPx(),
            width: `calc(100% - ${this._indentationPx()}px)`,
            paddingRight: this._paddingPx(),
          }}
        >
          <StickyHeaderHandler
            isEnabled={this.props.selection.hasMultiSelection}
            tableName={this.tableNameRef}
            verticalScrollContainer={this._getPageVerticalScrollContainer()}
            horizontalScrollContainer={this._getTableHorizontalScrollContainer()}
            stickyHeaderRef={this.stickyHeaderRef}
            headerRowHeight={this.props.state.rowHeights[0] ?? 0}
            titleBarHeight={this.getStickyTopContentHeight()}
            canShowStickyHeader={this.props.state.canShowStickyHeader}
            setCanShowStickyHeader={this.setCanShowStickyHeader}
            stickyHeaderPositioning={this.props.state.stickyHeaderPositioning}
            setStickyHeaderPositioning={this.setStickyHeaderPositioning}
          />
          {/* The focus bearer handles keyboard actions when (multiple) cells are selected. */}
          <div>
            {this.props.selectorsEnabled ? (
              <SetSelectorModalClass
                open={this.props.state.showSetSelectorModal}
                onRequestClose={this.closeSetSelectorModal}
                selectorsLink={this.props.selectorsLink}
                setSelector={this.setSelectionSelector}
              />
            ) : undefined}
            {this.props.state.showRenameTableModal ? (
              <RenameTableModal
                initialTableName={this.props.table.name}
                onTableRenamed={this.renameTable}
                isTableNameUsed={this.props.isTableNameUsed}
              />
            ) : null}
          </div>
          {this.renderFocusBearer()}
          {/*
          While NoteEditor wraps TableEditable in a caption (providing it with full-table selection),
          StepsEditor does not; it uses the following hiddenFocusEditable for handling full-table selection
          keyboard actions. */}
          <div
            ref={this.hiddenInputRef}
            className="hiddenFocusEditable editable hiddenFocusEditable--fullTable"
            data-cid={hiddenInputCid(this.props.cid)}
            data-index={this.props.index}
            data-subindex="table"
            data-formattable={true}
            {...getTestProps({component: 'HiddenTableFocusEditable'})}
            contentEditable={true}
            onKeyDown={this.hiddenKeyDownHandler}
            onCopy={this.hiddenCopyHandler}
            onCut={this.hiddenCutHandler}
          >
            {null}
          </div>
          {this._renderTitleBar()}
          {!this.props.isCollapsed && (
            <>
              {this.renderTableCellEditor()}
              <div
                className={classNames({
                  'mediocre-tableEditable-formulaBox-wrapper--sticky':
                    !this.props.views.isMaximizedOrSplitScreen &&
                    (this.props.views.isFullHeight || this._isStickyHeaderVisible()),
                })}
                style={{
                  top: this.getTitleBarHeight(),
                }}
              >
                {this._renderFormulaBox()}
              </div>
              {!this.props.views.isMaximizedOrSplitScreen &&
                this._isSingleInputVisible() &&
                this._renderSingleInputGroup()}
              {!this.props.views.isMaximizedOrSplitScreen && this.props.aboveTableSlot && (
                <div
                  className="mediocre-tableEditable-aboveTableSlotContainer"
                  {...getTestProps({component: 'TableEditable', element: 'aboveTableSlotContainer'})}
                >
                  {this.props.aboveTableSlot}
                </div>
              )}
              <div
                className={classNames('u-position-relative mediocre-tableEditable-scrollAreaWrapper', {
                  'mediocre-tableEditable-scrollAreaWrapper--topBorder': this.props.suppressTitleBar,
                })}
              >
                <div
                  ref={this.horizontalScrollContainerRef}
                  style={this._getVirtualizedScrollContainerStyle()}
                  {...getTestProps({component: 'TableEditable', element: 'horizontalScroll'})}
                  className={classNames('mediocre-tableEditable-scrollArea')}
                  onMouseDown={this.scrollAreaMouseDownHandler}
                  onScroll={this._handleHorizontalScroll}
                >
                  {/*
                The MultiEditable handles the mouse actions when the entire TableEditable is being selected
                in the context of it being a "line" in the larger document. It also serves as a container for
                the table, so that we can use the absolutely-positioned CellResizer. */}
                  <div
                    className={classNames('mediocre-tableEditable-tableWrapper', {
                      'mediocre-tableEditable-tableWrapper--activeCell': this._hasActiveCell(),
                      'mediocre-tableEditable-tableWrapper--compact': this.props.isCompactBottomSpacing,
                      'mediocre-tableEditable-tableWrapper--fillDown':
                        this.props.fillDown.isActive && !this.props.fillDown.isInvalid,
                      'mediocre-tableEditable-tableWrapper--fillDownInvalid': this.props.fillDown.isInvalid,
                    })}
                    onMouseMove={this.mouseMoveHandler}
                    onMouseOver={!this.props.locked ? this.mouseOverHandler : undefined}
                    onMouseDown={!this.props.locked ? this.mouseDownHandler : undefined}
                  >
                    {this.props.isPrintPreviewView || this.props.isExportView
                      ? this._renderUnvirtualizedTable()
                      : this._renderVirtualizedTable()}
                    {!this.props.views.isMaximizedOrSplitScreen && this._renderStickyHeader()}
                  </div>
                </div>
              </div>
              <div
                className={classNames('mediocre-tableEditable-stickyBottomContainer', {
                  'mediocre-tableEditable-stickyBottomContainer--sticky': this.isBottomContainerSticky(),
                })}
                style={{
                  bottom: this.isBottomContainerSticky() ? -this.props.state.pagePaddingBottom : undefined,
                }}
              >
                {this.shouldShowPersistentHorizontalScrollbar() && (
                  <PersistentHorizontalScrollbar
                    horizontalScrollContainer={this._getTableHorizontalScrollContainer()}
                    table={this.tableRef.current}
                    isCollapsed={Boolean(this.props.isCollapsed)}
                  />
                )}
                {this._renderFooter()}
              </div>
            </>
          )}
          {this.props.viewer && (this.props.unsaved || this.props.state.multiEditing) ? (
            <div>
              <span style={{fontWeight: 700}}>Note: </span>
              <span>Changes made to this table will not be saved. </span>
              {this.props.unsaved ? <a onClick={this.props.resetHandler}>Reset changes.</a> : undefined}
            </div>
          ) : undefined}
          {this.props.viewer && this.props.isPermaCollapsed && this._renderReadOnlyViewCollapseBanner()}
        </div>
      </div>
    );
  }

  /**
   * Finalize any uncommitted text if necessary to ensure that no cell is in edit mode.
   *
   * This method should be called before any attempt to modify the table contents if there is a possibility
   * the user is currently editing a cell. It can safely be called even if there is no active edit.
   */
  finalizeAndExitEditMode = (): void => {
    if (!this.props.state.multiSelIndex) {
      return;
    }
    this.selectCell(this.props.state.multiSelIndex, {
      collapse: false, // preserve selection
      editing: false, // but exit edit mode
    });
    setCellEditorPosition(null);
  };

  openSetSelectorModalAndFinalize = (): void => {
    this.finalizeAndExitEditMode();
    this.props.setState({showSetSelectorModal: true});
  };

  closeSetSelectorModal = (): void => {
    this.props.setState({showSetSelectorModal: false});
  };

  openRenameTableModal = (): void => {
    this.props.setState({showRenameTableModal: true});
  };

  _handleUpdateCells = (newCells: Array<Array<TableConstants.Cell>>): void => {
    const newTable = {...this.props.table, table: newCells};
    this.updateHandler(this.props.index, {table: newTable}, undefined, {
      [newTable.name]: {type: 'eval-table'},
    });
  };

  _createSelectedCellsUpdate = (
    applySettingToSelection: (
      args: TableConstants.MultiSelectionBox & {
        table: TableConstants.Table;
        onUpdateCells: (newCells: Array<Array<TableConstants.Cell>>) => void;
      }
    ) => void
  ): void => {
    this.finalizeAndExitEditMode();
    assert(this.props.selection.hasRealSelection, 'Expected to have a real selection');
    const {minI, maxI, minJ, maxJ} = this._multiSelectionBox();
    applySettingToSelection({
      minI,
      maxI,
      minJ,
      maxJ,
      table: this.props.table,
      onUpdateCells: this._handleUpdateCells,
    });
  };

  linkButtonDisabled(): boolean {
    // Allow setting a link if exactly one cell is selected
    const start = this.props.state.multiStartIndex;
    const end = this.props.state.multiEndIndex;
    return !(start != null && end != null && this.compareIndex(start, end) === 0);
  }

  _handleTableControlsMouseDown = (): void => {
    // If we are multi-editing a cell, finalize the value. This prevents weird things from happening,
    // e.g. if you click a format button.
    if (this.props.multiEditing) {
      this.finalizeAndExitEditMode();
    }
  };

  formulaFocusHandler = (text: string): void => {
    if (!this.props.selection.hasRealSelection) {
      this.expandToCell(this.getRequiredMultiSelIndex());
    }
    const index = this.getRequiredMultiSelIndex();
    this.setMultiEditing({
      editing: true,
      index,
      text,
      selection: {
        cid: `${this.props.cid}-formula-box`,
        start: text.length,
        end: text.length,
      },
    });
    this.setTableCellEditorPosition(index);
  };

  /**
   * Function to get the `computedTable`'s error for the current selection.
   */
  _getError(): Optional<EvaluatedCellError> {
    if (!this.props.selection.hasRealSelection) {
      return null;
    }
    const multiSelIndex = this.getRequiredMultiSelIndex();
    return this.props.computedTable[multiSelIndex[0]][multiSelIndex[1]]?.error;
  }

  // Helper methods/handlers for drag scrolling

  _getTableVerticalScrollContainer(): HTMLElement | null {
    if (this.props.views.isFixedHeight || this.props.views.isMaximizedOrSplitScreen) {
      return this.horizontalScrollContainerRef.current;
    }
    return (this.verticalScrollContainer as HTMLElement) ?? null;
  }

  _getTableHorizontalScrollContainer(): HTMLElement | null {
    return this.horizontalScrollContainerRef.current;
  }

  _getPageVerticalScrollContainer(): Element | null {
    return this.verticalScrollContainer ?? null;
  }

  /**
   * Helper method to scroll the table by a specified amount, triggering cell drag behavior
   * on the cell that ends up under the mouse.
   * @param  {Number} offsetLeft Amount to modify scrollLeft by
   * @param  {Number} offsetTop  Amount to modify scrollTop by
   */
  scrollBy(offsetLeft: number, offsetTop: number): void {
    if (!this._isMounted || (offsetLeft === 0 && offsetTop === 0)) {
      return;
    }
    const horizontalScrollContainer = this._getTableHorizontalScrollContainer();
    const verticalScrollContainer = this._getTableVerticalScrollContainer();

    // If they're the same container, then scroll together
    if (horizontalScrollContainer === verticalScrollContainer && horizontalScrollContainer) {
      $(horizontalScrollContainer)
        .stop()
        .animate(
          {
            scrollLeft: horizontalScrollContainer.scrollLeft + offsetLeft,
            scrollTop: horizontalScrollContainer.scrollTop + offsetTop,
          },
          {
            complete: this._scrollCompleteHandler,
            duration: DRAG_SCROLL_DURATION,
          }
        );
    } else {
      if (horizontalScrollContainer) {
        $(horizontalScrollContainer)
          .stop()
          .animate(
            {
              scrollLeft: horizontalScrollContainer.scrollLeft + offsetLeft,
            },
            {
              complete: this._scrollCompleteHandler,
              duration: DRAG_SCROLL_DURATION,
            }
          );
      }
      if (verticalScrollContainer) {
        $(verticalScrollContainer)
          .stop()
          .animate(
            {
              scrollTop: verticalScrollContainer.scrollTop + offsetTop,
            },
            {
              complete: this._scrollCompleteHandler,
              duration: DRAG_SCROLL_DURATION,
            }
          );
      }
    }
  }

  scrollAreaMouseDownHandler = (e: ElementMouseEvent): void => {
    // Keep track of clientX and clientY so that we can use them in scrollCompleteHandler when the drag
    // starts, since scroll events do not include mouse position information.
    this.clientX = e.clientX;
    this.clientY = e.clientY;

    // Don't steal focus from the focusBearer (to allow keyboard interaction with table)
    if (e.target === e.currentTarget) {
      e.preventDefault();
    }
  };

  /**
   * Keep track of clientX and clientY so that we can use them in scrollCompleteHandler, as the drag happens,
   * since scroll events do not include mouse position information.
   * Also begin autoscrolling.
   */
  scrollAreaMouseMoveHandler = (e: ElementMouseEvent): void => {
    if (this.props.state.multiActive || this.props.fillDown.isActive || this.props.state.draggingDimension) {
      this.clientX = e.clientX;
      this.clientY = e.clientY;

      this.startDragAutoscroll(e);
    }
  };

  /**
   * Start automatically vertically scrolling when the mouse reaches the bottom or top of a table. It will
   * stop scrolling when the table reaches one of its ends.
   * @param e ElementMouseEvent
   */
  startDragAutoscroll = (e: ElementMouseEvent): void => {
    // TODO(BNCH-107021): Also auto-scroll horizontally when dragging

    // if we're already scrolling, replace the existing interval
    if (this.autoscrollIntervalId) {
      this.stopDragAutoscroll();
    } else {
      // if we are just starting to scroll, add a window listener to stop the autoscroll when we stop dragging
      $(window).one('mouseup', this.stopDragAutoscroll);
    }
    this.autoscrollIntervalId = window.setInterval(() => {
      const verticalScrollContainer = this._getTableVerticalScrollContainer();
      if (e && verticalScrollContainer && this.tableRef.current) {
        const {pageY} = e;
        const verticalScrollRect = verticalScrollContainer.getBoundingClientRect();
        const tableRect = this.tableRef.current.getBoundingClientRect();
        // When in expanded inline mode, you want to trigger scrolling near the edge view, since the table
        // itself doesn't scroll. Which means your mouse cannot go past the edge of the screen, so it should
        // start scrolling before the edge.  For a fixed height table this is not necessary because you can
        // always move your mouse past the edge of the table to trigger scrolling
        // TODO(BNCH-115475): We should delete this once expanded inline is deprecated
        const autoscrollTriggerDistance = this.props.views.isFixedHeight ? 0 : AUTOSCROLL_TRIGGER_DISTANCE;
        // scroll down if the mouse is at the bottom of the scroll area, and the bottom of that table has
        // not been reached
        if (
          pageY > verticalScrollRect.bottom - autoscrollTriggerDistance &&
          verticalScrollRect.bottom < tableRect.bottom + TABLE_OVERSCROLL_CLEARANCE
        ) {
          verticalScrollContainer.scrollBy({top: AUTOSCROLL_DISTANCE, behavior: 'instant'});
        }
        // scroll up if the mouse is at the top of the scroll area (excluding the height of the sticky
        // header), and the top of that table has not been reached
        else if (
          pageY < verticalScrollRect.top + autoscrollTriggerDistance + this.props.state.rowHeights[0] &&
          verticalScrollRect.top > tableRect.top - TABLE_OVERSCROLL_CLEARANCE
        ) {
          verticalScrollContainer.scrollBy({top: -AUTOSCROLL_DISTANCE, behavior: 'instant'});
        }
        this._scrollCompleteHandler();
      }
    }, AUTOSCROLL_TRIGGER_RATE);
  };

  stopDragAutoscroll = (): void => {
    clearInterval(this.autoscrollIntervalId);
    this.autoscrollIntervalId = undefined;
  };

  /**
   * Callback for scrollBy-initiated scroll to trigger cell drag behavior on the cell that
   * ends up under the mouse.
   */
  _scrollCompleteHandler = (): void => {
    if (!(this.props.state.multiActive && this.clientX != null && this.clientY != null)) {
      return;
    }
    const el = document.elementFromPoint(this.clientX, this.clientY) as Element;
    const $cell = $(el).closest('.cell');
    if (!$cell.length) {
      return;
    }
    const index = this.cellIndexFromCid($cell.attr('data-cid'));
    if (
      index == null ||
      !TableUtil.isIndexBetween(this.props.dimensions.firstIndex, this.props.dimensions.lastIndex, index)
    ) {
      // Skip ghost cells
      return;
    }
    this.cellDragOverHandler(index);
    this.fillDownMouseMoveHandler(index);
  };

  fillDownMouseMoveHandler = (index: TableConstants.TableIndex): void => {
    let fillDownBox;
    if (!this.props.fillDown.isActive || !this._isMounted) {
      return;
    }

    // Compute the rectangle that extends from selected region to the current cell. Include the current cell
    // if the mouse is passed the halfway point of the cell away from the selected region.
    const [i, j] = Array.from(index);
    const {minI, maxI, minJ, maxJ} = this._multiSelectionBox();
    const cellBox = this._cellBox(i, j)!;
    const cellMidX = cellBox.left + cellBox.width / 2;
    const cellMidY = cellBox.top + cellBox.height / 2;
    // Assume we are including the cell, and then adjust depending on mouse position.
    let targetI = i;
    let targetJ = j;
    // Exclude:
    if (i < maxI && this.clientY! > cellMidY) {
      // row as top, if extending up and below cell midpoint
      targetI++;
    }
    if (i > maxI && this.clientY! < cellMidY) {
      // row as bottom, if extending down and above cell midpoint
      targetI--;
    }
    if (j < maxJ && this.clientX! > cellMidX) {
      // column as leftmost, if extending left and right of cell midpoint
      targetJ++;
    }
    if (j > maxJ && this.clientX! < cellMidX) {
      // column as rightmost, if extending right and left of cell midpoint
      targetJ--;
    }

    // Determine which direction we will expand in (the one that results in more affected cells),
    // and store the corresponding rectangle.
    const potlMinI = Math.min(targetI, minI);
    const potlMaxI = Math.max(targetI, maxI);
    const potlMinJ = Math.min(targetJ, minJ);
    const potlMaxJ = Math.max(targetJ, maxJ);
    if (Math.max(potlMaxI - maxI, minI - potlMinI) >= Math.max(potlMaxJ - maxJ, minJ - potlMinJ)) {
      fillDownBox = {minI: potlMinI, maxI: potlMaxI, minJ, maxJ};
    } else {
      fillDownBox = {minI, maxI, minJ: potlMinJ, maxJ: potlMaxJ};
    }

    if (computeContainsMergedRegion(this.props.computedTable, fillDownBox)) {
      this.setMultiExtra({fillDownInvalid: true});
      return;
      // Unsets fillDownInvalid because if we are not in a merged region,
      // then fillDownInvalid should be false.
    } else {
      this.setMultiExtra({fillDownInvalid: false});
    }

    if (!isEqual(this.props.fillDown.box, fillDownBox)) {
      this.setMultiExtra({fillDownBox});
    }
  };

  cellDragOverHandler = (index: TableConstants.TableIndex): void => {
    if (!this.props.fillDown.isActive && !this.props.state.draggingDimension) {
      this.multiEditableMouseOverHandler(this._expandSelectionIndex(index, this.props.state.multiActive));
    }
  };

  fillDownMouseUpHandler = (): void => {
    if (!this.props.fillDown.isActive || this.props.fillDown.isInvalid) {
      this.setMultiExtra({fillDownActive: false, fillDownBox: null, fillDownInvalid: false});
      return;
    }
    try {
      // If this for any reason errors, unset fillDownActive to allow normal selections.
      this.performFillDown();
    } finally {
      this.setMultiExtra({fillDownActive: false, fillDownBox: null, fillDownInvalid: false});
    }
  };

  /**
   * Return an index given the raw selection index and the type of selection
   * which is active.
   */
  _expandSelectionIndex(
    index: TableConstants.TableIndex,
    active: string | boolean | null
  ): TableConstants.TableIndex {
    const [i, j] = Array.from(index);
    const [maxI, maxJ] = Array.from(this._tableSize());
    return (() => {
      switch (active) {
        case TABLE_SELECTION_CELL:
          return index;
        case TABLE_SELECTION_ROW:
          return [i, maxJ] as TableConstants.TableIndex;
        case TABLE_SELECTION_COLUMN:
          return [maxI, j] as TableConstants.TableIndex;
        default:
          return index;
      }
    })();
  }

  /**
   * Return the current selection if the index is within the current selection, else returns `null`.
   *
   * This function is used by TableAxisCell to get the current selection.
   */
  getSelectionRange = (dim: 'row' | 'column', index: number): TableConstants.SelectionRange | null =>
    computeSelectionRange(this.props.selection.multiAxisDimSelection, dim, index);

  getDraggingRange = (): {
    dimension: TableConstants.TableDimension;
    range: TableConstants.SelectionRange;
  } | null => {
    if (
      this.props.locked ||
      !this.props.state.draggingDimension ||
      this.props.state.multiStartIndex == null ||
      this.props.state.multiEndIndex == null
    ) {
      return null;
    }
    const dimIdx = this.props.state.draggingDimension === TableConstants.TableDimension.ROW ? 0 : 1;
    const [startIndex, endIndex] = [
      this.props.state.multiStartIndex[dimIdx],
      this.props.state.multiEndIndex[dimIdx],
    ].sort((a, b) => a - b);

    return {
      dimension: this.props.state.draggingDimension,
      range: {minIndex: startIndex, maxIndex: endIndex},
    };
  };

  /**
   * Handler for TableAxisCell mouse down events. This provides row/column
   * select, and initiates row/column multi select.
   */
  axisCellDragStartHandler = (
    options: {start: TableConstants.TableIndex; active: string},
    e: ElementMouseEvent
  ): void => {
    // If the axis selection is in the current selection, don't create a
    // new multi select.
    let {start} = options;
    const {active} = options;
    const {sliceStart, sliceEnd} = this.getTableSlices();
    const isRowSelection = active === TABLE_SELECTION_ROW;
    // for row selection, if the table is sliced then start the row selection at the sliceStart
    if (isRowSelection && sliceStart) {
      start = [start[0], sliceStart ?? 0];
    }
    let end = this._expandSelectionIndex(start, active);
    // for row selection, if the table is sliced then end the row selection at the sliceEnd
    if (isRowSelection && sliceEnd) {
      end = [end[0], sliceEnd ?? end[1]];
    }

    // If we are extending the selection, take the existing selected index,
    // and project it to the start of the desired selection dimension.
    if (e.shiftKey && this.props.state.multiSelIndex != null) {
      start = isRowSelection
        ? [this.props.state.multiSelIndex[0], sliceStart ?? 0]
        : [0, this.props.state.multiSelIndex[1]];
    }

    this.multiEditableMouseDownHandler({start, end, active}, e);
  };

  /**
   * Should be called when the user releases the mouse button when dragging a selection of rows or columns.
   * Reorders the selected rows or columns to the specified index, unless they would cross a merged cell.
   *
   * @param insertIndex Index of rows or columns to move the selected range to
   */
  onDragComplete = (insertIndex: number): void => {
    const draggingDimension = this.props.state.draggingDimension!;
    assert(draggingDimension != null, 'Expected to have been dragging');

    assert(
      this.props.state.multiStartIndex != null && this.props.state.multiEndIndex != null,
      'Expected to have a selection'
    );

    this.props.setState({
      draggingDimension: null,
    });

    // Need to sort since start > end when you drag from the bottom to the top
    const dimIdx = draggingDimension === TableConstants.TableDimension.ROW ? 0 : 1;
    const [selectionStart, selectionEnd] = [
      this.props.state.multiStartIndex[dimIdx],
      this.props.state.multiEndIndex[dimIdx],
    ].sort((a, b) => a - b);
    const numMoved = selectionEnd - selectionStart + 1;

    // Error if the insert index contains any partial merged cells.
    const partialMergedCellInInsertRegion = TableUtil.hasMergedCellCrossingLine(
      this.props.computedTable,
      draggingDimension,
      insertIndex,
      {
        // if the table is sliced, ignore columns that aren't shown
        ignoredColumnIndices:
          draggingDimension === TableConstants.TableDimension.ROW
            ? range(0, this.getEffectiveTableSlices().sliceStart - 1)
            : undefined,
      }
    );
    if (partialMergedCellInInsertRegion) {
      const dimensionName = draggingDimension === TableConstants.TableDimension.ROW ? 'row' : 'column';
      const indexToReadableName =
        draggingDimension === TableConstants.TableDimension.ROW ? rowIndexToNumber : colNumberToLetters;
      const mergedCellPosition = partialMergedCellInInsertRegion
        .map((idx) => indexToReadableName(idx))
        .join(' to ');
      notifications.error(
        `Cannot move ${benchlingUtil.pluralize(numMoved, dimensionName)} to position ${indexToReadableName(insertIndex)} since it crosses a merged cell in ${dimensionName}s ${mergedCellPosition}. Please unmerge the cell and try again.`,
        {id: 'drag-to-reorder-partial-merged-cell'}
      );
      return;
    }

    // Save selection to history before making change.
    this.saveMultiSelect({
      multiStartIndex: this.props.state.multiStartIndex,
      multiEndIndex: this.props.state.multiEndIndex,
      multiSelIndex: this.props.state.multiSelIndex,
    });

    const reorderOptions: ReorderManualOptions = {
      startIndex: selectionStart,
      endIndex: selectionEnd,
      insertPosition: insertIndex,
      dim: draggingDimension,
    };

    // Update frozenColumnCount before the reorder mutation so the reorder sees the
    // correct frozen state. These are two dispatches, not a single atomic operation.
    if (draggingDimension === TableConstants.TableDimension.COLUMN) {
      const newFrozenCount = computeFrozenCountAfterReorder({
        selectionStart,
        selectionEnd,
        insertIndex,
        siBoundary: this.getSingleInputBoundary(),
        effectiveFrozen: TableUtil.getAffixedColumnCount(this.props.table),
        absoluteFreezeBoundary: this.getFreezeBoundary(),
      });
      if (newFrozenCount != null) {
        const newTable = produce(this.props.table, (draft) => {
          draft.frozenColumnCount = newFrozenCount;
        });
        this.updateHandler(this.props.index, {table: newTable});
      }
    }

    // If the user dragged but didn't reorder, we can skip calling the actual mutation code.
    if (insertIndex !== selectionStart) {
      this.props.tableMutations.reorderRowsOrColumns(this.props.table.name, reorderOptions);
    }

    const newFirstIndex = insertIndex > selectionStart ? insertIndex - numMoved : insertIndex;
    const newLastIndex = newFirstIndex + numMoved - 1;

    // Set selection to the reordered range
    const newSelection =
      draggingDimension === TableConstants.TableDimension.ROW
        ? this._rowRange(newFirstIndex, newLastIndex)
        : this._colRange(newFirstIndex, newLastIndex);
    this.setMultiIndices(newSelection);

    trackDragToReorderRowsOrColumns(
      this.props.table,
      reorderOptions,
      {
        fileId: this.props.fileId,
      },
      {
        isMaximized: this.props.isMaximized,
        isFixedHeight: true,
        isSplitScreen: this.props.isSplitScreen,
      }
    );
  };

  /**
   * Handler for TableAxisCell mouse drag events. This extends row/column
   * selections.
   */
  axisCellDragOverHandler = (index: TableConstants.TableIndex, e: ElementMouseEvent): void => {
    if (!this.props.state.draggingDimension) {
      this.multiEditableMouseOverHandler(this._expandSelectionIndex(index, this.props.state.multiActive));
    }
  };

  _shouldShowDragHandles(dimension: TableConstants.TableDimension): boolean {
    if (
      this.props.locked ||
      APIUtil.isStageEntry(this.props.fileId) ||
      APIUtil.isStageEntryTemplate(this.props.fileId) ||
      // Disable dragging columns for fill-in only entries, but allow dragging rows
      (this.props.isProtected && dimension === TableConstants.TableDimension.COLUMN)
    ) {
      return false;
    }

    let allowReorderDimension: boolean;
    if (this.props.allowReorderDimensions) {
      allowReorderDimension = this.props.allowReorderDimensions[dimension];
    } else {
      const isStructuredTable = TableUtil.isStructuredTable(this.props.table._tableType);
      if (isStructuredTable) {
        allowReorderDimension = false;
      } else if (dimension === TableConstants.TableDimension.ROW) {
        allowReorderDimension = this.props.dimensions.canModifyRowDim;
      } else {
        allowReorderDimension = this.props.dimensions.canModifyColumnDim;
      }
    }

    if (dimension === TableConstants.TableDimension.ROW) {
      return this.props.table.table.length > 1 && allowReorderDimension;
    } else {
      return this.props.table.table[0].length > 1 && allowReorderDimension;
    }
  }

  axisCellDragHandleMouseDown = (
    draggingDimension: TableConstants.TableDimension,
    index: number
  ): boolean => {
    if (this.props.state.draggingDimension || this.props.locked) {
      checkSoftInvariant(!this.props.state.draggingDimension, {
        message: 'Expected not to be dragging already',
        expirationDate: '2026-02-01',
        errorInDevAndTest: true,
      });
      return false;
    }

    let selectionStart = this.props.state.multiStartIndex;
    let selectionEnd = this.props.state.multiEndIndex;

    let isIndexSelected = false; // Whether any cells in the dragged row/column are selected
    if (this.props.selection.hasRealSelection) {
      const {minI, maxI, minJ, maxJ} = this._multiSelectionBox();
      // Extend selection to include whole row/column
      if (draggingDimension === TableConstants.TableDimension.ROW) {
        ({start: selectionStart, end: selectionEnd} = this._rowRange(minI, maxI));
        // Whether the row is in the selection AND the selection isn't every row
        isIndexSelected = index >= minI && index <= maxI && !(minI === 0 && maxI === this._tableSize()[0]);
      } else {
        // The minJ for column reordering should begin only with columns that are not Single Input.
        const firstNonSingleInputIndex = this.props.singleInputs.boundaryIndex;
        const newMinJ = Math.max(minJ, firstNonSingleInputIndex ?? minJ);
        ({start: selectionStart, end: selectionEnd} = this._colRange(newMinJ, maxJ));
        const {sliceStart, sliceEnd} = this.getEffectiveTableSlices();
        // Whether the column is in the selection AND the selection isn't every column
        isIndexSelected = index >= minJ && index <= maxJ && !(minJ <= sliceStart && maxJ >= sliceEnd);
      }
    }

    if (!isIndexSelected) {
      // Reset the selection to only the row/column that is being dragged
      if (draggingDimension === TableConstants.TableDimension.ROW) {
        ({start: selectionStart, end: selectionEnd} = this._rowRange(index, index));
      } else {
        ({start: selectionStart, end: selectionEnd} = this._colRange(index, index));
      }
    }

    // Ensure that the selection includes any merged cells
    const {minI, maxI, minJ, maxJ} = this._multiSelectionBox(selectionStart, selectionEnd);
    this.setMultiIndices({
      start: [minI, minJ],
      end: [maxI, maxJ],
    });

    this.props.setState({draggingDimension});
    return true;
  };

  fillDownSquareMouseDownHandler = (e: ElementMouseEvent): void => {
    e.preventDefault(); // prevent browser drag behavior from changing selection

    assert(this.props.selection.hasMultiSelection, 'Expected to have a selection');
    // Because the fill down is dependent on the multiSelectionBox, we store fillDown state alongside
    // multiSelectionBox state, so that if it is cleared, it also clears the fillDown state.
    this.setMultiExtra({fillDownActive: true, fillDownBox: this._multiSelectionBox()});
    $(window).one('mouseup', this.fillDownMouseUpHandler);
  };

  fillDownSquareDoubleClickHandler = (e: ElementMouseEvent): void => {
    e.preventDefault();
    this.performDoubleClickFillDown();
  };

  _cellBox(i: number, j: number): ClientRect | DOMRect | undefined {
    const scrollArea = this._getTableHorizontalScrollContainer();
    if (!scrollArea) {
      return;
    }
    return __guard__($(scrollArea).find(`[data-cid='${this.cellCid(i, j)}']`)[0], (x: Element) =>
      x.getBoundingClientRect()
    );
  }

  _scrollCellIntoView(...args: Array<TableConstants.TableIndex>): void {
    if (!this._isMounted) {
      return;
    }
    const [i, j] = Array.from(args[0]);
    const tableHorizontalScrollArea = this._getTableHorizontalScrollContainer();
    const tableHorizontalScrollAreaBox = this.getHorizontalScrollWrapperRect();
    const pageVerticalScrollAreaBox = this.getPageVerticalScrollWrapperRect();

    const stickyHeaderBox = this.getStickyHeaderRect();

    if (!tableHorizontalScrollAreaBox || !pageVerticalScrollAreaBox) {
      return;
    }

    if (this.virtualizationHandleRef.current && this.horizontalScrollContainerRef.current) {
      this.virtualizationHandleRef.current.scrollIntoView({
        index: i,
        calculateViewLocation: ({
          itemTop,
          itemBottom,
          viewportTop,
          viewportBottom,
          locationParams: {behavior, align, ...rest},
        }) => {
          const isStickyHeaderVisible = this._isStickyHeaderVisible();
          const isMaximizedOrSplitScreen = this.props.views.isMaximizedOrSplitScreen;
          const singleInputGroupHeight = this._getSingleInputGroupHeight();
          const stickyTopContentHeight = this.getStickyTopContentHeight();

          /** A buffer of 1 base row-height to to trigger scroll for */
          const bufferHeight = TableConstants.TABLE_ROW_HEIGHT;

          /** The offset from the top to trigger scrolling from */
          let topOffset = 0;
          // don't add buffer for the first row
          if (i !== 0) {
            topOffset += bufferHeight;
          }
          if (isStickyHeaderVisible) {
            topOffset += stickyTopContentHeight;
          } else if (isMaximizedOrSplitScreen) {
            topOffset -= singleInputGroupHeight;
          }

          /** The offset from the bottom to trigger scrolling from */
          let bottomOffset = 0;
          // don't add buffer for the last row
          if (i !== this.props.table.table.length - 1) {
            bottomOffset += bufferHeight;
          }
          // Maximized tables include Single Inputs in the scroll area
          if (isMaximizedOrSplitScreen) {
            bottomOffset += singleInputGroupHeight;
          } else {
            bottomOffset += TableConstants.TABLE_FOOTER_HEIGHT;
            if (this.shouldShowPersistentHorizontalScrollbar()) {
              bottomOffset += TableConstants.NATIVE_SCROLLBAR_HEIGHT_ESTIMATE;
            }
          }

          // Single Inputs should be stuck to the top of the window
          const isSingleInput = j < (this.props.singleInputs.boundaryIndex ?? 0);
          if (
            isSingleInput &&
            (isStickyHeaderVisible ||
              itemTop - singleInputGroupHeight < viewportTop ||
              itemTop - singleInputGroupHeight > viewportBottom)
          ) {
            return {
              ...rest,
              behavior,
              align: align ?? 'start',
              offset: -(singleInputGroupHeight + stickyTopContentHeight),
            };
          }
          if (itemTop < viewportTop + topOffset) {
            return {...rest, behavior, align: align ?? 'start', offset: -topOffset};
          }
          if (itemBottom > viewportBottom - bottomOffset) {
            return {...rest, behavior, align: align ?? 'end', offset: bottomOffset};
          }
          return null;
        },
      });
    }

    const cellBox = this._cellBox(i, j);
    // There is an edge case with inserting/deleting columns and rows where the selection updates out of band
    // with the table updating. Therefore, we make a best effort to perform the scroll
    if (cellBox == null) {
      return;
    }

    // Compute change in scrollLeft to horizontally scroll desired cell into view
    // If there are affixed columns, we want to scroll the cell into view relative to the affixed columns
    let tableBoxLeft = stickyHeaderBox ? stickyHeaderBox.left : tableHorizontalScrollAreaBox.left;

    // Don't scroll the current cell if it is affixed
    const cellIsAffixed = j < TableUtil.getAffixedColumnCount(this.props.table) && this.props.state.isAffixed;
    if (!cellIsAffixed) {
      if (this.stickyHeaderRef.current) {
        // Not full screen
        // If there is a sticky header, then we want to scroll the cell into view relative to the rightmost
        // cell in the sticky header
        const lastAffixedHeaderCell = $(this.stickyHeaderRef.current).find('.cell-rightShadow');
        if (lastAffixedHeaderCell.length) {
          tableBoxLeft = lastAffixedHeaderCell[0].getBoundingClientRect().right;
        }
      } else if (tableHorizontalScrollArea) {
        // Full screen
        // If there is no sticky header, then we want to scroll
        // to the rightmost cell that is sticky
        const lastAffixedColumnCell = $(tableHorizontalScrollArea).find(
          'tbody tr:first-child td.cell-rightShadow'
        );
        if (lastAffixedColumnCell.length) {
          tableBoxLeft = lastAffixedColumnCell[0].getBoundingClientRect().right;
        }
      }
    }

    let offsetLeft = 0;
    if (cellBox.left < tableBoxLeft) {
      offsetLeft = cellBox.left - tableBoxLeft - DRAG_SCROLL_PADDING;
    } else if (cellBox.right > tableHorizontalScrollAreaBox.right) {
      offsetLeft = cellBox.right - tableHorizontalScrollAreaBox.right + DRAG_SCROLL_PADDING;
    }

    // Compute change in scrollTop to vertically scroll desired cell into view
    // If there is a sticky header, we want to scroll the cell into view relative to the sticky header
    let offsetTop = 0;
    const bottomOfHeader =
      stickyHeaderBox?.bottom ?? pageVerticalScrollAreaBox?.top ?? tableHorizontalScrollAreaBox.top;
    const bottomOfScroll = pageVerticalScrollAreaBox?.bottom ?? tableHorizontalScrollAreaBox.bottom;
    const stickyFooterSize = this._getStickyFooterSize();
    const visibleViewHeight = bottomOfScroll - stickyFooterSize - bottomOfHeader;
    const cellTallerThanView = cellBox.height > visibleViewHeight;
    const cellTopAboveHeader = cellBox.top < bottomOfHeader;
    const cellBottomBelowFooter = cellBox.bottom > bottomOfScroll - stickyFooterSize;
    if (cellTopAboveHeader && !cellTallerThanView) {
      offsetTop = cellBox.top - bottomOfHeader - DRAG_SCROLL_PADDING;
    } else if (cellBottomBelowFooter && !cellTallerThanView) {
      offsetTop = cellBox.bottom - bottomOfScroll + stickyFooterSize + DRAG_SCROLL_PADDING;
    }

    this.scrollBy(offsetLeft, offsetTop);
  }

  _getStickyFooterSize(): number {
    if (this.isBottomContainerSticky()) {
      let height = TableConstants.TABLE_FOOTER_HEIGHT;
      if (this.shouldShowPersistentHorizontalScrollbar()) {
        height += TableConstants.NATIVE_SCROLLBAR_HEIGHT_ESTIMATE;
      }
      return height;
    }
    return 0;
  }

  _scrollTableIntoView(): void {
    const horizontalScrollArea = this._getTableHorizontalScrollContainer();
    const horizontalScrollAreaBox = horizontalScrollArea?.getBoundingClientRect();
    const verticalScrollArea = this._getPageVerticalScrollContainer();
    const verticalScrollAreaBox = verticalScrollArea?.getBoundingClientRect();
    if (!horizontalScrollArea || !horizontalScrollAreaBox || !verticalScrollAreaBox) {
      return;
    }

    // If the table has been scrolled out of view, scroll it back centered
    if (
      horizontalScrollAreaBox.bottom < verticalScrollAreaBox.top ||
      horizontalScrollAreaBox.top > verticalScrollAreaBox.bottom
    ) {
      const verticalCenter = (verticalScrollAreaBox.top + verticalScrollAreaBox.bottom) / 2;
      const tableCenter = (horizontalScrollAreaBox.top + horizontalScrollAreaBox.bottom) / 2;
      const scrollOffset = tableCenter - verticalCenter;

      verticalScrollArea?.scrollBy?.({top: scrollOffset, behavior: 'auto'});
    }
  }

  _scrollTableHeaderIntoView(): void {
    const horizontalScrollArea = this._getTableHorizontalScrollContainer();
    const horizontalScrollAreaBox = horizontalScrollArea?.getBoundingClientRect();
    const verticalScrollArea = this._getPageVerticalScrollContainer();
    const verticalScrollAreaBox = verticalScrollArea?.getBoundingClientRect();
    if (!horizontalScrollArea || !horizontalScrollAreaBox || !verticalScrollAreaBox) {
      return;
    }

    if (horizontalScrollArea.scrollIntoView) {
      horizontalScrollArea.scrollIntoView({block: 'start'});
    }

    // Account for title and formula bar heights
    const stickyTopContentHeight = this.getStickyTopContentHeight();
    if (stickyTopContentHeight > 0 && verticalScrollArea?.scrollBy) {
      verticalScrollArea.scrollBy(0, -stickyTopContentHeight);
    }
  }
}

function __guard__<Value, TransformedValue>(
  value: Value,
  transform: (value: Value) => TransformedValue
): TransformedValue | undefined {
  return value != null ? transform(value) : undefined;
}

function __range__(left: number, right: number, inclusive: boolean): Array<number> {
  const range = [];
  const ascending = left < right;
  const end = !inclusive ? right : ascending ? right + 1 : right - 1;
  for (let i = left; ascending ? i < end : i > end; ascending ? i++ : i--) {
    range.push(i);
  }
  return range;
}

/**
 * When running TableEditable in unit tests, we need to have the mock virtualization context
 * otherwise it will not render any rows.
 * see: https://virtuoso.dev/mocking-in-tests
 */
function TableEditableInTest(props: TableEditableProps): JSX.Element {
  const context = useMemo(() => ({viewportHeight: 1000, itemHeight: 25}), []);
  return (
    <VirtuosoMockContext.Provider value={context}>
      <TableEditable {...props} />
    </VirtuosoMockContext.Provider>
  );
}

/**
 * Allow reassigning TableEditable in tests to TableEditableInTest by using mockVirtualization().
 * Must cleanup with restore() afterwards.
 */
let DefaultTableEditable = TableEditable; // eslint-disable-line import-x/no-mutable-exports

// eslint-disable-next-line no-restricted-exports
export {DefaultTableEditable as default};

export function mockVirtualization(): void {
  DefaultTableEditable = TableEditableInTest;
}

export function restore(): void {
  DefaultTableEditable = TableEditable;
}
